import AuthHelper from 'features/Auth/authHelper';
import AccountHelper from 'features/Account/accountHelper';
import SystemHelper from 'features/System/systemHelper';
import feApi from 'api/feApi';
import AppRoute from 'general/constants/AppRoute';
import AppConstants from 'general/constants/AppConstants';
import AppResource from 'general/constants/AppResource';
import PreferenceKeys from 'general/constants/PreferenceKeys';
import RoleUtils from 'general/utils/RoleUtils';
import Utils from 'general/utils/Utils';
import React, { useEffect, useMemo, useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useSelector } from 'react-redux';
import { motion, AnimatePresence } from 'framer-motion';
import QRCode from 'react-qr-code';
import ToastHelper from 'general/helpers/ToastHelper';
import miscHelper from 'general/helpers/miscHelper';
import InputSearchDropdown from 'general/components/Others/InputSearchDropdown';
import StringUtils from 'general/utils/StringUtils';
import useAppConfig from 'hooks/useAppConfig';
import CustomerProductDetailScreen from '../CustomerProductDetailScreen';
import CustomerRideTab from '../../CustomerRide';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import './CustomerHomeScreen.scss';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
  ArcElement,
  BarElement,
} from 'chart.js';
import { Line, Doughnut, Bar } from 'react-chartjs-2';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
  ArcElement,
  BarElement
);
// Welcome Banner Assets
import banner1 from 'assets/icons/1783148333953_194284037513614117_4642898201278877177_0807db0806d4340ac4b91ae8f85890dd.jpg';
import banner2 from 'assets/icons/1783148333933_194284037513614117_4642898201278877177_f1f620bd0bbdcd527d40b8c60a1160af.jpg';
import banner3 from 'assets/icons/1783148333967_194284037513614117_4642898201278877177_e888358da2926ee81df9b264aae828e9.jpg';
import bannerFrame from 'assets/banner/Frame 2147227867.jpg';
import evgLogoLeft from 'assets/plugins/header/Group 5.png';
import evgLogoRight from 'assets/plugins/header/Group.png';
import greenWalletIcon from 'assets/mobile-app/images/img_green_wallet.png';
import bidvLogoIcon from 'assets/mobile-app/icons/ic_logobidv.png';
import paymentInstructionImg from 'assets/mobile-app/images/img_payment_instruction.png';

const tabs = [
  { key: 'overview', label: 'Tổng quan' },
  { key: 'commerce', label: 'Đơn hàng - Sản phẩm' },
  { key: 'ride', label: 'Xe ghép' },
  { key: 'network', label: 'Mạng lưới giới thiệu' },
  { key: 'wallet', label: 'Hoa hồng - Ví tiền' },
  { key: 'tools', label: 'Công cụ - Chính sách' },
  { key: 'account', label: 'Tài khoản' },
];

const customerTabByPath = {
  home: 'overview',
  overview: 'overview',
  orders: 'commerce',
  commerce: 'commerce',
  ride: 'ride',
  network: 'network',
  wallet: 'wallet',
  tools: 'tools',
  account: 'account',
  notifications: 'notifications',
  product: 'product',
  cart: 'cart',
};

const customerPathByTab = {
  overview: 'home',
  commerce: 'orders',
  ride: 'ride',
  product: 'product',
  network: 'network',
  wallet: 'wallet',
  tools: 'tools',
  account: 'account',
  notifications: 'notifications',
  cart: 'cart',
};

const CUSTOMER_SCROLL_STORAGE_PREFIX = 'evg_customer_scroll_position:';
const SUPPORT_HOTLINE_DISPLAY = '0886 322 622';
const SUPPORT_HOTLINE_TEL = '0886322622';

const getCustomerScrollStorageKey = (pathname = '', search = '') => (
  `${CUSTOMER_SCROLL_STORAGE_PREFIX}${pathname}${search || ''}`
);

const saveCustomerScrollPosition = (pathname, search) => {
  if (typeof window === 'undefined') return;
  try {
    sessionStorage.setItem(
      getCustomerScrollStorageKey(pathname, search),
      JSON.stringify({
        x: window.scrollX || window.pageXOffset || 0,
        y: window.scrollY || window.pageYOffset || 0,
        savedAt: Date.now(),
      })
    );
  } catch (error) {
    // Ignore storage failures, scroll restoration is only a UX enhancement.
  }
};

const readCustomerScrollPosition = (pathname, search) => {
  if (typeof window === 'undefined') return null;
  try {
    const raw = sessionStorage.getItem(getCustomerScrollStorageKey(pathname, search));
    return raw ? JSON.parse(raw) : null;
  } catch (error) {
    return null;
  }
};

const getCustomerCartStorageKey = (user) => {
  const userKey = user?.userId || user?.userCode || user?.email;
  return userKey ? `evg_customer_cart_${userKey}` : null;
};

const getSavedCartItems = (storageKey) => {
  if (!storageKey) return [];
  try {
    const savedCart = JSON.parse(localStorage.getItem(storageKey) || '[]');
    return Array.isArray(savedCart) ? savedCart.filter(item => item?.product?.id != null) : [];
  } catch (error) {
    return [];
  }
};

const specRows = [
  { label: 'Đặc điểm', render: (product) => [product.category, product.chargingLevel, product.mountType] },
  { label: 'Kích thước', render: (product) => product.dimensions },
  { label: 'Đầu vào', render: (product) => product.input },
  { label: 'Xuất xứ', render: (product) => product.origin },
  { label: 'Điện áp đầu vào', render: (product) => product.voltage },
  { label: 'Phương pháp sạc', render: (product) => product.chargeMethod },
  { label: 'Kết nối', render: (product) => product.connection },
  { label: 'Công suất đầu ra tối đa', render: (product) => product.maxOutput },
  { label: 'Công suất đầu ra tối đa của phích cắm đơn', render: (product) => product.socketOutput },
  { label: 'Nhiệt độ hoạt động', render: (product) => product.temperature },
  { label: 'Độ ẩm', render: (product) => product.humidity },
  { label: 'Giao thức kết nối', render: (product) => product.protocol },
  { label: 'Trọng lượng', render: (product) => product.weight },
  { label: 'Mức độ bảo vệ', render: (product) => product.protection },
  { label: 'Đặc điểm', render: (product) => product.features },
  { label: 'Lưu ý', render: (product) => product.note },
];

const normalizeList = (value) => {
  if (Array.isArray(value)) return value;
  if (typeof value === 'string' && value.trim()) return value.split('\n').map((item) => item.trim()).filter(Boolean);
  return [];
};

const normalizeProductAttributes = (value) => {
  if (!Array.isArray(value)) return [];
  return value
    .map((item) => ({
      label: String(item?.label || '').trim(),
      value: item?.value,
    }))
    .filter((item) => item.label || item.value);
};

const formatCurrency = (value) => `${Utils.formatNumber(Math.round(Number(value || 0)))}đ`;

// VAT bán hàng. Phải khớp evgapi/services/productTax.js#priceLine (cùng cách làm tròn theo từng dòng)
// để số tiền hiển thị ở đây luôn bằng số tiền server tạo đơn/QR.
const computeVatSummary = (lines, percent, exclusive) => {
  const rate = Math.max(0, Number(percent) || 0) / 100;
  let beforeTax = 0;
  let total = 0;
  lines.forEach(({ price, quantity }) => {
    const unit = Math.max(0, Number(price) || 0);
    const qty = Math.max(0, Number(quantity) || 0);
    const unitAfterTax = exclusive ? Math.round(unit * (1 + rate)) : Math.round(unit);
    const lineAfterTax = unitAfterTax * qty;
    const lineBeforeTax = exclusive ? Math.round(unit * qty) : Math.round(lineAfterTax / (1 + rate));
    beforeTax += lineBeforeTax;
    total += lineAfterTax;
  });
  return { percent: rate * 100, beforeTax, vat: total - beforeTax, total };
};
const getSingleLineAmountFontSize = (value) => {
  const length = String(value ?? '').length;
  if (length >= 15) return 16;
  if (length >= 12) return 19;
  if (length >= 10) return 22;
  return 28;
};
const formatDocumentDate = (value) => {
  if (!value) return 'Chưa có dữ liệu';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleDateString('vi-VN');
};
const formatDateTime = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString('vi-VN', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};
const RequiredMark = () => <span style={styles.requiredMark}>*</span>;
const MANUAL_WITHDRAW_BANK_SOURCE = 'MANUAL';

const maskBankAccountNumber = (value) => {
  const accountNumber = String(value || '').trim();
  if (!accountNumber) return '';
  return `**** ${accountNumber.slice(-4)}`;
};

const productPaymentMethods = [
  {
    value: 'VNPAY',
    label: 'VNPAY',
    description: 'Thanh toán qua cổng VNPAY, hệ thống tự cập nhật khi giao dịch thành công.',
    logo: AppResource.icons.evg.icLogoVNPay,
    available: true,
  },
  {
    value: 'MOMO',
    label: 'Ví MoMo',
    description: 'Thanh toán qua ví MoMo bằng app hoặc cổng thanh toán MoMo.',
    logo: AppResource.icons.evg.icLogoMomo,
    available: true,
  },
  {
    value: 'BIDV',
    label: 'Chuyển khoản',
    description: 'Tạo mã QR chuyển khoản ngân hàng, hệ thống ghi nhận sau khi thanh toán.',
    logo: AppResource.icons.evg.icLogoBank,
    available: true,
  },
  {
    value: 'CASH',
    label: 'Tiền mặt',
    description: 'Đặt đơn và thanh toán tiền mặt theo xác nhận của admin/kế toán.',
    icon: 'fa-money-bill-wave',
    available: true,
  },
];

const walletTopupPaymentMethods = [
  {
    value: 'BIDV',
    label: 'Chuyển khoản BIDV (Quét QR)',
    description: 'Quét mã QR BIDV để hệ thống tự động cộng tiền vào ví.',
    logo: bidvLogoIcon,
  },
  {
    value: 'MOMO',
    label: 'Ví MoMo',
    description: 'Thanh toán bằng ứng dụng hoặc cổng thanh toán MoMo.',
    logo: AppResource.icons.evg.icLogoMomo,
  },
  {
    value: 'VNPAY',
    label: 'VNPAY',
    description: 'Thanh toán trực tuyến qua cổng VNPAY.',
    logo: AppResource.icons.evg.icLogoVNPay,
  },
];

const getWalletTopupSubmitLabel = (paymentMethod, loading) => {
  if (loading) return paymentMethod === 'BIDV' ? 'Đang tạo mã QR...' : 'Đang tạo giao dịch...';
  if (paymentMethod === 'MOMO') return 'Thanh toán qua MoMo';
  if (paymentMethod === 'VNPAY') return 'Thanh toán qua VNPAY';
  return 'Tạo mã QR chuyển khoản BIDV';
};

const getProductPaymentSubmitLabel = (paymentMethod, submitting) => {
  if (submitting) return 'Đang tạo đơn...';
  const method = String(paymentMethod || '').toUpperCase();
  if (method === 'VNPAY') return 'Thanh toán qua VNPAY';
  if (method === 'MOMO') return 'Thanh toán qua MoMo';
  if (method === 'BIDV') return 'Tạo mã QR chuyển khoản';
  if (method === 'CASH') return 'Đặt hàng thanh toán tiền mặt';
  return 'Tạo mã QR thanh toán';
};

const getProductPaymentMethodLabel = (paymentMethod) => {
  const method = String(paymentMethod || '').toUpperCase();
  return productPaymentMethods.find((item) => item.value === method)?.label || (['QR', 'BANK_TRANSFER'].includes(method) ? 'Chuyển khoản' : method || 'Thanh toán');
};

const productPriceRanges = [
  { value: 'ALL', label: 'Tất cả mức giá' },
  { value: 'UNDER_10', label: 'Dưới 10 triệu', min: 0, max: 10000000 },
  { value: '10_20', label: '10 - 20 triệu', min: 10000000, max: 20000000 },
  { value: '20_50', label: '20 - 50 triệu', min: 20000000, max: 50000000 },
  { value: 'OVER_50', label: 'Trên 50 triệu', min: 50000000 },
];

const productStockStatuses = [
  { value: 'ALL', label: 'Tất cả tồn kho' },
  { value: 'IN_STOCK', label: 'Còn hàng' },
  { value: 'OUT_OF_STOCK', label: 'Hết hàng' },
];

const productSortOptions = [
  { value: 'DEFAULT', label: 'Mặc định' },
  { value: 'NEWEST', label: 'Mới nhất' },
  { value: 'PRICE_ASC', label: 'Giá tăng dần' },
  { value: 'PRICE_DESC', label: 'Giá giảm dần' },
  { value: 'NAME_ASC', label: 'Tên A-Z' },
];

const productBadgeOptions = {
  BEST_SELLER: { label: 'Bán chạy', color: '#f97316' },
  NEW: { label: 'Mới', color: '#3b82f6' },
  HOT: { label: 'Hot', color: '#ef4444' },
  PROMOTION: { label: 'Khuyến mãi', color: '#ec4899' },
  FEATURED: { label: 'Nổi bật', color: '#8b5cf6' },
  LIMITED: { label: 'Giới hạn', color: '#0f766e' },
};

const normalizeProductBadges = (value) => {
  if (!Array.isArray(value)) return [];
  return value
    .map((item) => (item && typeof item === 'object' ? item.value : item))
    .map((item) => String(item || '').trim().toUpperCase())
    .filter((item, index, array) => productBadgeOptions[item] && array.indexOf(item) === index);
};

const formatNotificationTime = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleString('vi-VN', {
    hour: '2-digit',
    minute: '2-digit',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
};

const normalizeImages = (value) => {
  if (!value || value === 'null' || value === 'undefined') return [];

  let images = value;
  if (typeof images === 'string') {
    try {
      images = JSON.parse(images);
    } catch (error) {
      images = [images.replace(/^"+|"+$/g, '')];
    }
  }
  if (!Array.isArray(images)) images = [images];
  return images.map((image) => {
    const imagePath = image && typeof image === 'object'
      ? image.url || image.path || image.relativePath || image.src || ''
      : image;
    if (!imagePath || imagePath === 'null' || imagePath === 'undefined') return '';
    const cleanPath = String(imagePath).replace(/^"+|"+$/g, '');
    if (cleanPath.startsWith('/static/') || cleanPath.startsWith('/assets/') || cleanPath.startsWith('data:image')) return cleanPath;
    return Utils.getFullUrl(cleanPath);
  }).filter(Boolean);
};

const normalizeImage = (value) => normalizeImages(value)[0] || '';

const mapProduct = (item) => {
  let specs = item?.technicalSpecs || {};
  if (typeof specs === 'string') {
    try {
      specs = JSON.parse(specs);
    } catch (error) {
      specs = {};
    }
  }
  const stockQuantity = Number(item?.stockQuantity ?? item?.stock ?? item?.quantity ?? item?.inventoryQuantity ?? 1);
  return {
    id: item?.id || item?.categoryId,
    slug: item?.slug || '',
    code: specs.productCode || item?.categoryCode || `SP${String(item?.categoryId || item?.id || '').padStart(8, '0')}`,
    name: item?.name || '',
    shortName: specs.shortName || item?.name || '',
    shortDescription: specs.shortDescription || item?.shortDescription || '',
    price: item?.price ? `${Utils.formatNumber(item.price)}đ` : 'Liên hệ',
    rawPrice: item?.price || 0,
    originalPrice: item?.originalPrice ? `${Utils.formatNumber(item.originalPrice)}đ` : '',
    rawOriginalPrice: item?.originalPrice || 0,
    commission: `${item?.commissionRate || 0}%`,
    image: normalizeImage(item?.images),
    images: normalizeImages(item?.images),
    description: item?.description || '',
    productCategoryId: item?.productCategoryId || '',
    productCategory: item?.productCategory?.name || '',
    productCategorySortOrder: Number(item?.productCategory?.displayOrder ?? 0),
    productAttributes: normalizeProductAttributes(item?.productAttributes),
    productBadges: normalizeProductBadges(item?.productBadges?.length ? item.productBadges : specs.productBadges),
    stockQuantity,
    sold: Number(item?.soldQuantity ?? item?.sold ?? item?.totalSold ?? 0),
    isInStock: stockQuantity > 0 && String(item?.status || 'ACTIVE').toUpperCase() !== 'INACTIVE',
    createdAt: item?.createdAt || '',
    displayOrder: Number(item?.displayOrder ?? 0),
    category: specs.category || '',
    chargingLevel: specs.chargingLevel || '',
    mountType: specs.mountType || '',
    dimensions: specs.dimensions || '',
    input: specs.input || '',
    origin: specs.origin || '',
    voltage: specs.voltage || '',
    chargeMethod: specs.chargeMethod || '',
    connection: specs.connection || '',
    maxOutput: specs.maxOutput || '',
    socketOutput: specs.socketOutput || '',
    temperature: specs.temperature || '',
    humidity: specs.humidity || '',
    protocol: specs.protocol || '',
    weight: specs.weight || '',
    protection: normalizeList(specs.protection),
    features: normalizeList(specs.features),
    note: specs.note || '',
  };
};

const parseOrderNote = (note) => {
  if (!note) return {};
  try {
    return JSON.parse(note);
  } catch (error) {
    return { note };
  }
};

const getOrderStatusName = (status) => {
  switch (status) {
    case 'ORDERED':
      return 'Chờ thanh toán';
    case 'PAID':
      return 'Đã thanh toán';
    case 'SHIPPING':
    case 'INSTALLTING':
    case 'INSTALLING':
    case 'INSTALLED':
      return 'Đã thanh toán';
    case 'COMPLETED':
      return 'Hoàn thành';
    case 'PENDING':
    case 'REQUESTED':
      return 'Chờ duyệt';
    case 'PROCESSING':
      return 'Đã thanh toán';
    case 'CANCELLED':
      return 'Đã hủy';
    case 'REJECTED':
      return 'Bị từ chối';
    case 'FAILED':
      return 'Thất bại';
    case 'AVAILABLE':
      return 'Chưa thuê';
    case 'ACTIVE':
    case 'NORMAL':
      return 'Đang hoạt động';
    case 'REGISTERED':
      return 'Đã đăng ký';
    case 'INACTIVE':
      return 'Ngừng hoạt động';
    default:
      return status || '-';
  }
};

const getNetworkStatusName = (status) => {
  const rawStatus = String(status || '').trim();
  if (!rawStatus) return '-';
  const normalizedStatus = rawStatus.toUpperCase();
  const translatedStatus = getOrderStatusName(normalizedStatus);
  return translatedStatus === normalizedStatus ? rawStatus : translatedStatus;
};

const getNetworkLevelName = (level) => {
  const normalizedLevel = Number(level || 0);
  return normalizedLevel > 0 ? `Tuyến ${normalizedLevel}` : 'Chưa có tuyến dưới';
};

const getWalletFundingSourceName = (fundingSource) => {
  const source = String(fundingSource || '').toUpperCase();
  if (source.includes('REFUND')) {
    return 'Hoàn tiền';
  }
  switch (source) {
    case 'INTERNAL':
      return 'Nạp nội bộ';
    case 'CASH':
      return 'Tiền mặt';
    case 'QR':
      return 'Mã QR';
    case 'VNPAY':
      return 'VNPAY';
    case 'MOMO':
      return 'MoMo';
    case 'BIDV':
      return 'BIDV';
    case 'WALLET':
      return 'Ví điện tử';
    default:
      return fundingSource || 'Giao dịch ví';
  }
};

const getMarketingDocumentTypeName = (type) => {
  switch (type) {
    case 'BANNER':
    case 'IMAGE':
    case 'VIDEO':
      return 'Tài liệu';
    case 'GUIDE':
      return 'Hướng dẫn sử dụng';
    case 'POLICY':
      return 'Chính sách hoa hồng';
    default:
      return type || 'Tài liệu';
  }
};

const parseMarketingDocumentDescription = (description = '') => {
  const text = String(description || '').trim();
  if (!text.startsWith('{') || !text.endsWith('}')) return null;
  try {
    return JSON.parse(text);
  } catch (error) {
    return null;
  }
};

const getMarketingDocumentSummary = (document = {}) => {
  const parsed = parseMarketingDocumentDescription(document.description);
  if (parsed) return parsed.plainDescription || parsed.summary || parsed.content || document.fileName || 'Tài liệu marketing';
  return document.description || document.fileName || 'Tài liệu marketing';
};

const isCommercePinnedMarketingDocument = (document = {}) => {
  const type = String(document.type || '').toUpperCase();
  const parsed = parseMarketingDocumentDescription(document.description);
  return Boolean(parsed?.pinnedToCommerce) && !['POLICY', 'GUIDE'].includes(type);
};

const getMarketingDocumentCategory = (document = {}) => {
  const type = String(document.type || '').toUpperCase();
  if (type === 'POLICY') return 'POLICY';
  if (type === 'GUIDE') return 'GUIDE';
  if (['BANNER', 'IMAGE', 'VIDEO'].includes(type)) return 'MARKETING';
  return document.category || 'MARKETING';
};

const getMarketingDocumentFormat = (document = {}) => {
  const source = `${document.fileName || ''} ${document.fileUrl || ''}`.toLowerCase();
  if (/\.(png|jpg|jpeg|gif|webp|svg)(\?|$|\s)/.test(source)) return 'PNG';
  if (/\.pdf(\?|$|\s)/.test(source)) return 'PDF';
  if (/\.(mp4|mov|avi|mkv|webm)(\?|$|\s)/.test(source)) return 'MP4';
  if (/\.(xls|xlsx|csv)(\?|$|\s)/.test(source)) return 'EXCEL';
  return document.format || 'OTHER';
};

const hasMarketingDocumentFile = (document = {}) => {
  const fileUrl = String(document.fileUrl || '').trim();
  return fileUrl && !fileUrl.startsWith('#');
};

const getMarketingDocumentIcon = (type, fileUrl = '') => {
  const lower = String(fileUrl).toLowerCase();
  if (type === 'VIDEO' || /\.(mp4|mov|avi|mkv|webm)(\?|$)/.test(lower)) return 'fa-solid fa-circle-play';
  if (type === 'POLICY' || /\.pdf(\?|$)/.test(lower)) return 'fa-solid fa-file-pdf';
  if (type === 'GUIDE') return 'fa-solid fa-book-open';
  return 'fa-solid fa-image';
};

const EMPTY_AFFILIATE_DATA = {
  summary: {},
  performance: [],
  commissions: { direct: [], passive: [] },
  network: { landlords: [], investors: [], retailCustomers: [] },
  withdrawals: [],
  walletTransactions: [],
};

const EvgCustomSelect = ({ value, onChange, children, style, className }) => {
  const [isOpen, setIsOpen] = useState(false);
  const wrapperRef = useRef(null);

  useEffect(() => {
    function handleClickOutside(event) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [wrapperRef]);

  const options = React.Children.toArray(children).map(child => {
    if (child && child.type === 'option') {
      return {
        value: child.props.value,
        label: child.props.children
      };
    }
    return null;
  }).filter(Boolean);

  const selectedOption = options.find(opt => String(opt.value) === String(value)) || options[0];

  return (
    <div ref={wrapperRef} className={`evg-custom-select-wrapper ${isOpen ? 'is-open' : ''} ${className || ''}`} style={{ position: 'relative', width: '100%', height: '100%', zIndex: isOpen ? 9999 : 1, ...style }}>
      <div 
        className={`evg-custom-select-trigger ${isOpen ? 'is-open' : ''}`}
        onClick={() => setIsOpen(!isOpen)}
        style={{
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '0 16px',
          cursor: 'pointer',
          color: '#1e293b',
          fontWeight: 600,
          background: 'transparent',
          border: 'none',
          width: '100%'
        }}
      >
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1, textAlign: 'left' }}>
          {selectedOption?.label || ''}
        </span>
        <i className="fa-solid fa-chevron-down" style={{ 
          fontSize: 12, 
          color: '#64748b', 
          transition: 'transform 0.3s ease',
          transform: isOpen ? 'rotate(-180deg)' : 'none',
          flexShrink: 0,
          marginLeft: 8
        }} />
      </div>
      
      {isOpen && (
        <div className="evg-custom-select-dropdown" style={{
          position: 'absolute',
          top: 'calc(100% + 8px)',
          left: 0,
          minWidth: '220px',
          background: '#ffffff',
          borderRadius: 16,
          boxShadow: '0 12px 35px rgba(0,0,0,0.08)',
          border: '1px solid #f1f5f9',
          padding: 8,
          zIndex: 9999,
          maxHeight: 280,
          overflowY: 'auto',
          display: 'flex',
          flexDirection: 'column',
          gap: 4
        }}>
          {options.map((opt) => {
            const isSelected = String(value) === String(opt.value);
            return (
              <div 
                key={opt.value}
                className="evg-custom-select-option"
                style={{
                  padding: '12px 14px',
                  borderRadius: 10,
                  cursor: 'pointer',
                  fontSize: 14,
                  fontWeight: isSelected ? 700 : 500,
                  color: isSelected ? '#16a34a' : '#334155',
                  background: isSelected ? '#f0fdf4' : 'transparent',
                  transition: 'all 0.2s ease',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between'
                }}
                onMouseEnter={(e) => {
                  if (!isSelected) {
                    e.currentTarget.style.background = '#f8fafc';
                    e.currentTarget.style.color = '#0f172a';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isSelected) {
                    e.currentTarget.style.background = 'transparent';
                    e.currentTarget.style.color = '#334155';
                  }
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  if (onChange) onChange({ target: { value: opt.value } });
                  setIsOpen(false);
                }}
              >
                <span>{opt.label}</span>
                {isSelected && <i className="fa-solid fa-check" style={{ fontSize: 12 }}></i>}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

function CustomerHomeScreen() {
  const { user, agencyRoleList } = useSelector((state) => state.auth);
  const navigate = useNavigate();
  const location = useLocation();
  const { customerTab, '*': subPath } = useParams();
  const cartStorageKey = useMemo(() => getCustomerCartStorageKey(user), [user?.userId, user?.userCode, user?.email]);
  const [activeTab, setActiveTab] = useState(() => customerTabByPath[customerTab] || 'overview');
  const [previousProductTab, setPreviousProductTab] = useState('commerce');
  const [notificationCategory, setNotificationCategory] = useState('ALL');
  const [products, setProducts] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);

  const [productKeyword, setProductKeyword] = useState('');
  const [selectedProductCategoryId, setSelectedProductCategoryId] = useState('ALL');
  const [selectedPriceRange, setSelectedPriceRange] = useState('ALL');
  const [selectedStockStatus, setSelectedStockStatus] = useState('ALL');
  const [selectedProductSort, setSelectedProductSort] = useState('DEFAULT');
  const [productPage, setProductPage] = useState(1);
  const [selectedCommerceStatus, setSelectedCommerceStatus] = useState('ALL');
  const [showAllCommerceOrders, setShowAllCommerceOrders] = useState(false);
  const [productQuantities, setProductQuantities] = useState({});
  const [orders, setOrders] = useState([]);
  const [loadingOrders, setLoadingOrders] = useState(false);
  const [cartItems, setCartItems] = useState(() => getSavedCartItems(cartStorageKey));
  const shouldRestoreScrollRef = useRef(true);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const originalScrollRestoration = window.history?.scrollRestoration;
    if (window.history && 'scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }

    let ticking = false;
    const saveCurrentPosition = () => saveCustomerScrollPosition(
      window.location.pathname,
      window.location.search
    );
    const handleScroll = () => {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(() => {
        ticking = false;
        saveCurrentPosition();
      });
    };
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') saveCurrentPosition();
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('beforeunload', saveCurrentPosition);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      saveCurrentPosition();
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('beforeunload', saveCurrentPosition);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      if (window.history && 'scrollRestoration' in window.history && originalScrollRestoration) {
        window.history.scrollRestoration = originalScrollRestoration;
      }
    };
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    if (!shouldRestoreScrollRef.current) {
      shouldRestoreScrollRef.current = true;
      return undefined;
    }
    const savedPosition = readCustomerScrollPosition(location.pathname, location.search);
    const targetY = Number(savedPosition?.y || 0);
    const targetX = Number(savedPosition?.x || 0);
    if (targetY <= 0 && targetX <= 0) return undefined;

    const restoreScroll = () => {
      window.scrollTo({
        top: targetY,
        left: targetX,
        behavior: 'auto',
      });
    };
    const timers = [0, 50, 150, 300, 600, 1000].map((delay) => window.setTimeout(restoreScroll, delay));
    return () => {
      timers.forEach((timer) => window.clearTimeout(timer));
    };
  }, [location.pathname, location.search]);
  const [loadedCartStorageKey, setLoadedCartStorageKey] = useState(cartStorageKey);

  const [showCheckout, setShowCheckout] = useState(false);
  const [productPaymentQR, setProductPaymentQR] = useState(null);
  const [loadingPaymentQR, setLoadingPaymentQR] = useState(false);
  const [orderForm, setOrderForm] = useState({
    buyerName: '',
    buyerPhone: '',
    shippingAddress: '',
    note: '',
    paymentMethod: 'VNPAY',
  });
  const [submittingOrder, setSubmittingOrder] = useState(false);
  const [affiliateData, setAffiliateData] = useState({
    summary: {},
    performance: [],
    commissions: { direct: [], passive: [] },
    network: { landlords: [], investors: [], retailCustomers: [] },
    withdrawals: [],
    walletTransactions: [],
  });
  const [loadingAffiliateSummary, setLoadingAffiliateSummary] = useState(false);
  const [withdrawAmount, setWithdrawAmount] = useState('');
  const [withdrawNote, setWithdrawNote] = useState('');
  const [withdrawBankSource, setWithdrawBankSource] = useState('');
  const [withdrawBankForm, setWithdrawBankForm] = useState({
    bankName: '',
    bankAccountNumber: '',
    accountHolder: '',
  });
  const [withdrawBankErrors, setWithdrawBankErrors] = useState({});
  const [bankList, setBankList] = useState([]);
  const [accountBankQuery, setAccountBankQuery] = useState('');
  const [submittingWithdraw, setSubmittingWithdraw] = useState(false);
  const [walletNotice, setWalletNotice] = useState(null);
  const [commissionTab, setCommissionTab] = useState('direct');
  const [commissionKeyword, setCommissionKeyword] = useState('');
  const [commissionStatus, setCommissionStatus] = useState('ALL');
  const [commissionPeriod, setCommissionPeriod] = useState('ALL');
  const [selectedInsightKey, setSelectedInsightKey] = useState(null);
  const [selectedMarketingDocument, setSelectedMarketingDocument] = useState(null);
  const [showQR, setShowQR] = useState(false);
  const [topupWalletScope, setTopupWalletScope] = useState(null);
  const [topupWalletAmount, setTopupWalletAmount] = useState('100000');
  const [topupPaymentMethod, setTopupPaymentMethod] = useState('BIDV');
  const [topupWalletState, setTopupWalletState] = useState('idle');
  const [topupWalletResult, setTopupWalletResult] = useState(null);
  const [topupWalletError, setTopupWalletError] = useState('');
  const [networkKeyword, setNetworkKeyword] = useState('');
  const [networkGroup, setNetworkGroup] = useState('ALL');
  const [networkStatus, setNetworkStatus] = useState('ALL');
  const [networkPeriod, setNetworkPeriod] = useState('ALL');
  const [networkSort, setNetworkSort] = useState('UPDATED_DESC');

  const [selectedMemberTreeModal, setSelectedMemberTreeModal] = useState(null);
  const [memberTreeData, setMemberTreeData] = useState(null);
  const [loadingMemberTree, setLoadingMemberTree] = useState(false);
  const [profile, setProfile] = useState(null);
  const [loadingProfile, setLoadingProfile] = useState(false);
  const [savingProfile, setSavingProfile] = useState(false);
  const [uploadingKyc, setUploadingKyc] = useState(false);
  const [accountErrors, setAccountErrors] = useState({});
  const [referralInput, setReferralInput] = useState('');
  const [verifyingReferral, setVerifyingReferral] = useState(false);
  const [verifiedReferrer, setVerifiedReferrer] = useState(null);
  const [applyingReferral, setApplyingReferral] = useState(false);
  const [accountForm, setAccountForm] = useState({
    fullname: '',
    email: '',
    phone: '',
    idCardType: 'CC',
    idCardNumber: '',
    idCardDocuments: [],
    bankCode: '',
    bankName: '',
    bankAccount: '',
    bankBeneficiary: '',
  });
  const savedWithdrawBankAccounts = useMemo(() => {
    const agencyBankAccounts = Array.isArray(profile?.agency?.bankAccounts)
      ? profile.agency.bankAccounts
      : [];
    const candidates = [
      ...agencyBankAccounts,
      {
        bankAccountId: 'profile',
        bankCode: accountForm.bankCode,
        bankName: accountForm.bankName,
        bankAccount: accountForm.bankAccount,
        bankBeneficiary: accountForm.bankBeneficiary,
        defaultAccount: 1,
        status: 'VALIDATED',
      },
    ];
    const uniqueAccounts = new Map();

    candidates.forEach((item) => {
      const status = String(item?.status || '').toUpperCase();
      if (['DELETED', 'DISABLED', 'INACTIVE', 'INVALID'].includes(status)) return;

      const bankAccount = String(item?.bankAccount || item?.bankAccountNumber || '').trim();
      const bankName = String(item?.bankName || item?.bankCode || '').trim();
      const bankBeneficiary = String(item?.bankBeneficiary || item?.accountHolder || '').trim();
      if (!bankAccount || !bankName || !bankBeneficiary) return;

      const duplicateKey = `${bankName.toLowerCase()}|${bankAccount}`;
      if (uniqueAccounts.has(duplicateKey)) return;
      uniqueAccounts.set(duplicateKey, {
        bankAccountId: item?.bankAccountId,
        bankCode: String(item?.bankCode || '').trim(),
        bankName,
        bankAccount,
        bankBeneficiary,
        defaultAccount: Number(item?.defaultAccount || 0),
      });
    });

    return Array.from(uniqueAccounts.values())
      .sort((left, right) => right.defaultAccount - left.defaultAccount)
      .map((item, index) => ({
        ...item,
        value: `SAVED_${item.bankAccountId || index}_${item.bankAccount}`,
      }));
  }, [
    profile?.agency?.bankAccounts,
    accountForm.bankCode,
    accountForm.bankName,
    accountForm.bankAccount,
    accountForm.bankBeneficiary,
  ]);
  const effectiveWithdrawBankSource = withdrawBankSource === MANUAL_WITHDRAW_BANK_SOURCE
    || savedWithdrawBankAccounts.some((item) => item.value === withdrawBankSource)
    ? withdrawBankSource
    : (savedWithdrawBankAccounts[0]?.value || MANUAL_WITHDRAW_BANK_SOURCE);
  const selectedWithdrawBankAccount = savedWithdrawBankAccounts.find(
    (item) => item.value === effectiveWithdrawBankSource,
  );
  const findValidBank = (bankCode) => {
    const normalizedBankCode = String(bankCode || '').trim().toUpperCase();
    return bankList.find((bank) => String(bank?.bankCode || '').trim().toUpperCase() === normalizedBankCode);
  };
  const accountBankOptions = useMemo(() => {
    const normalizedQuery = StringUtils.removeVietnameseTones(String(accountBankQuery || ''))
      .toLowerCase()
      .trim();

    return bankList
      .filter((bank) => {
        if (!normalizedQuery) return true;
        const searchableText = StringUtils.removeVietnameseTones([
          bank?.bankCode,
          bank?.shortName,
          bank?.bankName,
        ].filter(Boolean).join(' ')).toLowerCase();
        return searchableText.includes(normalizedQuery);
      })
      .sort((left, right) => String(left?.shortName || left?.bankName || '').localeCompare(
        String(right?.shortName || right?.bankName || ''),
        'vi',
      ))
      .map((bank) => ({
        title: bank?.shortName
          ? `${bank.shortName}${bank?.bankCode ? `, ${bank.bankCode}` : ''}`
          : (bank?.bankCode || bank?.bankName || ''),
        subTitle: bank?.bankName || '',
        value: bank?.bankCode || '',
      }));
  }, [bankList, accountBankQuery]);
  const selectedAccountBank = findValidBank(accountForm.bankCode);
  const [notifications, setNotifications] = useState([]);
  const [loadingNotifications, setLoadingNotifications] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showAccountMenu, setShowAccountMenu] = useState(false);
  const [showWalletMenu, setShowWalletMenu] = useState(false);




  useEffect(() => {
    if (showQR || selectedInsightKey || selectedMarketingDocument || walletNotice || topupWalletScope) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [showQR, selectedInsightKey, selectedMarketingDocument, walletNotice, topupWalletScope]);

  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [allNotifications, setAllNotifications] = useState([]);
  
  const filteredAllNotifications = useMemo(() => {
    if (notificationCategory === 'ALL') return allNotifications;
    return allNotifications.filter((n) => {
      const notificationType = String(n.type || '').toUpperCase();
      if (notificationType === notificationCategory) return true;

      const title = String(n.title || '').toLowerCase();
      const msg = String(n.message || '').toLowerCase();
      const isPayment = title.includes('thanh toán') || msg.includes('thanh toán');
      const isOrder = title.includes('đơn') || msg.includes('đơn');
      const derivedType = isPayment ? 'PAYMENT' : isOrder ? 'ORDER' : 'SYSTEM';
      return derivedType === notificationCategory;
    });
  }, [allNotifications, notificationCategory]);
  const [loadingAllNotifications, setLoadingAllNotifications] = useState(false);

  const [currentBannerIndex, setCurrentBannerIndex] = useState(0);
  const bannerImages = useMemo(() => [
    { src: bannerFrame, width: 2170, height: 512 },
  ], []);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentBannerIndex((prevIndex) => (prevIndex + 1) % bannerImages.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [bannerImages]);
  const [selectedNotificationIds, setSelectedNotificationIds] = useState([]);
  const [marketingDocuments, setMarketingDocuments] = useState([]);
  const [toolKeyword, setToolKeyword] = useState('');
  const [toolCategory, setToolCategory] = useState('ALL');
  const [toolFileType, setToolFileType] = useState('ALL');
  const [toolFormat, setToolFormat] = useState('ALL');
  const [toolSortBy, setToolSortBy] = useState('NEWEST');
  const [loadingMarketingDocuments, setLoadingMarketingDocuments] = useState(false);
  const notificationsRef = useRef(null);
  const accountMenuRef = useRef(null);
  const walletMenuRef = useRef(null);
  const commerceOrderSectionRef = useRef(null);
  const productSectionRef = useRef(null);
  const [windowWidth, setWindowWidth] = useState(() => window.innerWidth);

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const kpiGridCols = windowWidth <= 768 ? 'repeat(2, minmax(0, 1fr))' : windowWidth <= 1366 ? 'repeat(3, minmax(0, 1fr))' : 'repeat(6, minmax(0, 1fr))';

  useEffect(() => {
    setCartItems(getSavedCartItems(cartStorageKey));
    setLoadedCartStorageKey(cartStorageKey);
  }, [cartStorageKey]);

  useEffect(() => {
    if (!cartStorageKey || loadedCartStorageKey !== cartStorageKey) return;
    localStorage.setItem(cartStorageKey, JSON.stringify(cartItems));
  }, [cartItems, cartStorageKey, loadedCartStorageKey]);

  const cartQuantity = useMemo(
    () => cartItems.reduce((sum, item) => sum + (item.checked !== false ? Number(item.quantity || 0) : 0), 0),
    [cartItems]
  );
  const cartTotal = useMemo(
    () => cartItems.reduce(
      (sum, item) => sum + (item.checked !== false ? Number(item.product?.rawPrice || 0) * Number(item.quantity || 0) : 0),
      0
    ),
    [cartItems]
  );
  const appConfig = useAppConfig();
  // VAT lấy từ server (đúng mức server sẽ tính khi tạo đơn); cấu hình trong redux chỉ là dự phòng vì chỉ nạp 1 lần lúc đăng nhập.
  const [serverVat, setServerVat] = useState(null);
  useEffect(() => {
    const loadVat = () => {
      feApi.getProducts({ page: 0, limit: 1 })
        .then((response) => {
          if (response?.vat && Number.isFinite(Number(response.vat.percent))) {
            setServerVat({ percent: Number(response.vat.percent), exclusive: !response.vat.pricesIncludeVat });
          }
        })
        .catch(() => {});
    };
    loadVat();
    window.addEventListener('focus', loadVat);
    return () => window.removeEventListener('focus', loadVat);
  }, []);
  const vatPercent = serverVat ? serverVat.percent : appConfig.productVatPercent;
  const vatExclusive = serverVat ? serverVat.exclusive : Boolean(appConfig.productVatExclusive);
  // Số tiền khách thực trả cho các sản phẩm đã tích chọn (đã gồm VAT).
  const cartVat = useMemo(
    () => computeVatSummary(
      cartItems.filter((item) => item.checked !== false).map((item) => ({ price: item.product?.rawPrice, quantity: item.quantity })),
      vatPercent,
      vatExclusive
    ),
    [cartItems, vatPercent, vatExclusive]
  );
  const allCartChecked = useMemo(
    () => cartItems.length > 0 && cartItems.every((item) => item.checked !== false),
    [cartItems]
  );

  const productCategories = useMemo(() => {
    const categoryMap = new Map();
    products.forEach((product) => {
      if (!product.productCategoryId || !product.productCategory) return;
      const key = String(product.productCategoryId);
      const current = categoryMap.get(key);
      categoryMap.set(key, {
        id: key,
        name: product.productCategory,
        count: (current?.count || 0) + 1,
        sortOrder: product.categorySortOrder || product.productCategorySortOrder || current?.sortOrder || 0,
      });
    });
    
    return Array.from(categoryMap.values()).sort((a, b) => {
      if (a.sortOrder !== b.sortOrder) {
        return (a.sortOrder || 0) - (b.sortOrder || 0);
      }
      return a.name.localeCompare(b.name, 'vi');
    });
  }, [products]);

  const filteredProducts = useMemo(() => {
    const keyword = productKeyword.trim().toLowerCase();
    const selectedRange = productPriceRanges.find((range) => range.value === selectedPriceRange);
    const filtered = products.filter((product) => {
      const matchCategory = selectedProductCategoryId === 'ALL'
        || String(product.productCategoryId || '') === String(selectedProductCategoryId);
      const productPrice = Number(product.rawPrice || 0);
      const matchPrice = !selectedRange || selectedRange.value === 'ALL'
        || (productPrice >= Number(selectedRange.min || 0)
          && (selectedRange.max == null || productPrice < Number(selectedRange.max)));
      const matchStock = selectedStockStatus === 'ALL'
        || (selectedStockStatus === 'IN_STOCK' && product.isInStock)
        || (selectedStockStatus === 'OUT_OF_STOCK' && !product.isInStock);
      const searchableText = [
        product.name,
        product.code,
        product.description,
        product.productCategory,
        ...(product.productAttributes || []).flatMap((attribute) => [attribute.label, attribute.value]),
      ].filter(Boolean).join(' ').toLowerCase();
      return matchCategory && matchPrice && matchStock && (!keyword || searchableText.includes(keyword));
    });
    return [...filtered].sort((a, b) => {
      if (selectedProductSort === 'PRICE_ASC') return Number(a.rawPrice || 0) - Number(b.rawPrice || 0);
      if (selectedProductSort === 'PRICE_DESC') return Number(b.rawPrice || 0) - Number(a.rawPrice || 0);
      if (selectedProductSort === 'NAME_ASC') return String(a.name || '').localeCompare(String(b.name || ''), 'vi');
      if (selectedProductSort === 'NEWEST') return new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime();
      const orderDiff = Number(a.displayOrder || 0) - Number(b.displayOrder || 0);
      if (orderDiff !== 0) return orderDiff;
      return new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime();
    });
  }, [products, productKeyword, selectedProductCategoryId, selectedPriceRange, selectedStockStatus, selectedProductSort]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (notificationsRef.current && !notificationsRef.current.contains(event.target)) {
        setShowNotifications(false);
      }
      if (accountMenuRef.current && !accountMenuRef.current.contains(event.target)) {
        setShowAccountMenu(false);
      }
      if (walletMenuRef.current && !walletMenuRef.current.contains(event.target)) {
        setShowWalletMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const changeTab = (tabKey) => {
    setActiveTab(tabKey);
    if (tabKey !== 'product') setSelectedProduct(null);
    shouldRestoreScrollRef.current = false;
    navigate(`/customer/${customerPathByTab[tabKey] || 'home'}`);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const openProductDetail = (product) => {
    const fromTab = activeTab === 'product' ? previousProductTab : activeTab;
    setPreviousProductTab(fromTab || 'commerce');
    setSelectedProduct(product);
    setActiveTab('product');
    shouldRestoreScrollRef.current = false;
    navigate(`/customer/product/${product.slug || product.id || product.categoryId || ''}`, {
      state: {
        product,
        fromTab: fromTab || 'commerce',
      },
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const returnToPreviousProductTab = () => {
    const targetTab = previousProductTab || location.state?.fromTab || 'commerce';
    setActiveTab(targetTab);
    setSelectedProduct(null);
    shouldRestoreScrollRef.current = false;
    navigate(`/customer/${customerPathByTab[targetTab] || 'orders'}`, { replace: true });
    window.scrollTo({ top: 0, behavior: 'smooth' });
    if (targetTab === 'commerce') {
      setTimeout(() => {
        productSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    }
  };

  const sidebarItems = [
    ['overview', 'fa-regular fa-grid-2', 'Tổng quan'],
    ['commerce', 'fa-regular fa-cart-shopping', 'Đơn hàng - Sản phẩm'],
    ['ride', 'fa-regular fa-car-side', 'Xe ghép'],
    ['network', 'fa-regular fa-share-nodes', 'Mạng lưới giới thiệu'],
    ['wallet', 'fa-regular fa-wallet', 'Hoa hồng - Ví tiền'],
    ['tools', 'fa-regular fa-gear', 'Công cụ - Chính sách'],
    ['account', 'fa-regular fa-user', 'Tài khoản'],
  ];

  const renderCustomerSidebar = () => (
    <aside
      data-customer-sidebar
      className={`responsive-sidebar-wrapper ${mobileMenuOpen ? 'mobile-open' : ''}`}
      style={{
        ...(sidebarCollapsed ? styles.dashboardSidebarCollapsed : {}),
        ...(mobileMenuOpen ? {
          display: 'block',
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          width: '100%',
          height: '100vh',
          background: 'rgba(0, 0, 0, 0.5)',
          zIndex: 9999,
          margin: 0,
          padding: 0,
          transform: 'none'
        } : {})
      }}
      onClick={() => {
        if (mobileMenuOpen) setMobileMenuOpen(false);
      }}
    >
      <div 
        className="mobile-sidebar-content" 
        onClick={(e) => e.stopPropagation()}
        style={mobileMenuOpen ? {
          position: 'absolute',
          top: 0,
          left: 0,
          bottom: 0,
          background: '#ffffff',
          width: '280px',
          maxWidth: '85%',
          height: '100%',
          margin: 0,
          padding: '0 0 20px 0',
          display: 'flex',
          flexDirection: 'column',
          overflowY: 'auto',
          boxShadow: '2px 0 10px rgba(0,0,0,0.1)'
        } : {}}
      >
        {mobileMenuOpen && (
          <div style={{ padding: '20px', display: 'flex', alignItems: 'center', justifyContent: 'center', borderBottom: '1px solid #f1f5f9' }}>
            <img src={evgLogoLeft} alt="EV Group" style={{ height: 32, objectFit: 'contain' }} />
          </div>
        )}
        <div data-side-menu style={{ ...styles.sideMenu, padding: mobileMenuOpen ? 0 : 12, borderRadius: mobileMenuOpen ? 0 : 20 }}>
        {sidebarItems.map(([key, icon, label], index) => {
          const isActive = activeTab === key || (key === 'commerce' && activeTab === 'product');
          const isLast = index === sidebarItems.length - 1;
          const nextIsActive = activeTab === sidebarItems[index + 1]?.[0];
          const showDivider = !isLast && !sidebarCollapsed && !isActive && !nextIsActive;

          return (
            <div key={key} style={{ display: 'flex', flexDirection: 'column' }}>
              <button
                type="button"
                onClick={() => {
                  changeTab(key);
                  setMobileMenuOpen(false);
                }}
                style={{
                  ...styles.sideMenuItem,
                  ...(sidebarCollapsed ? styles.sideMenuItemCollapsed : {}),
                  ...(isActive ? styles.sideMenuItemActive : {}),
                }}
                title={label}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
                  <div style={{ width: 24, display: 'flex', justifyContent: 'center', alignItems: 'center', flexShrink: 0 }}>
                    <i className={icon} style={{ fontSize: 18, color: isActive ? '#047857' : '#64748b' }} />
                  </div>
                  {!sidebarCollapsed && <span style={{ color: isActive ? '#047857' : '#475569', whiteSpace: 'nowrap', fontFamily: "'SVN-Gilroy', 'Inter', sans-serif", fontWeight: 600, fontSize: 15, lineHeight: 1 }}>{label}</span>}
                </div>
                {!sidebarCollapsed && isActive && <i className="fa-solid fa-chevron-right" style={{ position: 'absolute', right: 16, top: '50%', transform: 'translateY(-50%)', fontSize: 13, color: '#047857' }} />}
              </button>
              {!isLast && !sidebarCollapsed && <div style={{ height: 1, background: (!isActive && !nextIsActive) ? '#f1f5f9' : 'transparent', marginLeft: 46, marginRight: 12, marginTop: 4, marginBottom: 4 }} />}
            </div>
          );
        })}
      </div>

      {!sidebarCollapsed && (
        <>
          <div style={styles.rankCard} className="sidebar-promo-card">
            <div className="promo-img-1" style={{
              width: '100%',
              height: 180,
              background: 'url("/assets/images/evg_robot.jpg") center/cover',
              borderBottom: '1px solid #333',
            }} />
            
            <div className="promo-content-wrapper" style={{ padding: '16px' }}>
              <div className="promo-badge-row" style={{
                background: '#2b2b2b',
                borderRadius: 12,
                padding: '12px 14px',
                display: 'flex',
                alignItems: 'center',
                gap: 14,
                marginBottom: 16
              }}>
                <div style={{
                  width: 38, height: 38, background: '#3f3f46', borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20
                }}>🏆</div>
                <div>
                  <div style={{ color: '#f8fafc', fontSize: 13, fontWeight: 800 }}>Nâng Cấp Đối Tác</div>
                  <div style={{ color: '#fbbf24', fontWeight: 900, fontSize: 16, marginTop: 2 }}>Gold</div>
                </div>
              </div>
              
              <p style={{ color: '#d1d5db', fontSize: 13, lineHeight: 1.6, marginBottom: 16, fontWeight: 600 }}>
                Nhận được hoa hồng cao hơn và các đặc quyền hấp dẫn khác.
              </p>
              
              <button type="button" style={{
                width: '100%',
                background: '#22c55e',
                color: '#fff',
                border: 'none',
                borderRadius: 12,
                padding: '14px',
                fontWeight: 800,
                cursor: 'pointer',
                marginBottom: 16,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                gap: 8,
                fontSize: 14,
              }}>
                Xem chi tiết <i className="fa-solid fa-chevron-right" style={{ fontSize: 11 }} />
              </button>
            </div>

            <div className="promo-img-2" style={{
              background: 'url("/assets/images/evg_charger.jpg") center/cover',
              height: 150,
              margin: '0 16px 16px 16px',
              borderRadius: 12
            }} />
          </div>

          <div style={styles.supportCard} className="sidebar-support-card">
            <div style={styles.supportRow}><i className="fa-regular fa-user" /> Hỗ trợ đối tác</div>
            <div style={styles.supportRow}>
              <i className="fa-solid fa-phone" />
              <a href={`tel:${SUPPORT_HOTLINE_TEL}`} style={{ color: 'inherit', textDecoration: 'none' }}>
                {SUPPORT_HOTLINE_DISPLAY}
              </a>
            </div>
            <div style={styles.supportRow}><i className="fa-regular fa-envelope" /> cskh@evgroup.com.vn</div>
            <div style={styles.supportRow}><i className="fa-regular fa-message" /> Trung tâm hỗ trợ</div>
          </div>
        </>
      )}
      </div>
    </aside>
  );

  useEffect(() => {
    const nextTab = customerTabByPath[customerTab] || 'overview';
    setActiveTab(nextTab);
    if (nextTab === 'product') {
      if (location.state?.fromTab) {
        setPreviousProductTab(location.state.fromTab);
      }
      if (location.state?.product) {
        setSelectedProduct(location.state.product);
      }
    } else {
      setSelectedProduct(null);
    }

    if (!customerTab || !customerTabByPath[customerTab]) {
      navigate(AppRoute.customerHome, { replace: true });
    }
  }, [customerTab, location.state, navigate]);

  const affiliateCode = useMemo(() => {
    if (profile) {
      return profile.userCode || profile.phone || `EVG${profile.userId || profile.id || 'CTV'}`;
    }
    if (user?.userCode || user?.phone) {
      return user.userCode || user.phone;
    }
    return '...';
  }, [profile, user]);

  useEffect(() => {
    if (products.length > 0) return;
    setLoadingProducts(true);
    feApi.getProducts({ page: 0, limit: 100 })
      .then((response) => {
        const p = Array.isArray(response?.products) ? response.products : [];
        setProducts(p.map(mapProduct));
      })
      .catch(() => {
        setProducts([]);
      })
      .finally(() => {
        setLoadingProducts(false);
      });
  }, [products.length]);

  const loadOrders = () => {
    setLoadingOrders(true);
    feApi.getOrders({ page: 0, limit: 200 })
      .then((response) => {
        const o = Array.isArray(response?.orders) ? response.orders : [];
        setOrders(o);
      })
      .catch(() => {
        setOrders([]);
      })
      .finally(() => {
        setLoadingOrders(false);
      });
  };

  useEffect(() => {
    if (!['overview', 'commerce', 'wallet'].includes(activeTab)) return;
    loadOrders();
  }, [activeTab]);

  useEffect(() => {
    setShowAllCommerceOrders(false);
  }, [selectedCommerceStatus]);

  const loadAffiliateSummary = () => {
    setLoadingAffiliateSummary(true);
    feApi.getAffiliateSummary()
      .then((response) => {
        if (response?.result === 'success') {
          setAffiliateData({
            summary: response.summary || {},
            performance: Array.isArray(response.performance) ? response.performance : [],
            commissions: {
              direct: Array.isArray(response.commissions?.direct) ? response.commissions.direct : [],
              passive: Array.isArray(response.commissions?.passive) ? response.commissions.passive : [],
            },
            network: {
              landlords: Array.isArray(response.network?.landlords) ? response.network.landlords : [],
              investors: Array.isArray(response.network?.investors) ? response.network.investors : [],
              retailCustomers: Array.isArray(response.network?.retailCustomers) ? response.network.retailCustomers : [],
            },
            withdrawals: Array.isArray(response.withdrawals) ? response.withdrawals : [],
            walletTransactions: Array.isArray(response.walletTransactions) ? response.walletTransactions : [],
          });
        } else {
          setAffiliateData(EMPTY_AFFILIATE_DATA);
        }
      })
      .catch(() => {
        setAffiliateData(EMPTY_AFFILIATE_DATA);
      })
      .finally(() => {
        setLoadingAffiliateSummary(false);
      });
  };

  const loadBankList = () => {
    miscHelper.callApiGetBankList()
      .then((banks) => {
        setBankList(Array.isArray(banks) ? banks : []);
      })
      .catch(() => {
        setBankList([]);
      });
  };

  useEffect(() => {
    loadAffiliateSummary();
    loadBankList();
  }, []);

  useEffect(() => {
    if (['overview', 'network', 'wallet'].includes(activeTab)) {
      loadAffiliateSummary();
    }
  }, [activeTab]);

  const loadMarketingDocuments = () => {
    setLoadingMarketingDocuments(true);
    feApi.getMarketingDocuments({ page: 0, limit: 100 })
      .then((response) => {
        const documents = Array.isArray(response?.documents)
          ? response.documents
          : Array.isArray(response?.items)
            ? response.items
            : Array.isArray(response?.data)
              ? response.data
              : [];
        setMarketingDocuments(documents);
      })
      .catch(() => {
        setMarketingDocuments([]);
      })
      .finally(() => {
        setLoadingMarketingDocuments(false);
      });
  };

  useEffect(() => {
    if (['commerce', 'tools'].includes(activeTab)) {
      loadMarketingDocuments();
    }
  }, [activeTab]);

  const fillAccountForm = (nextProfile) => {
    setAccountForm({
      fullname: nextProfile?.fullname || '',
      email: nextProfile?.email || '',
      phone: nextProfile?.phone || '',
      idCardType: nextProfile?.idCardType || 'CC',
      idCardNumber: nextProfile?.idCardNumber || '',
      idCardDocuments: Array.isArray(nextProfile?.idCardDocuments) ? nextProfile.idCardDocuments.filter(Boolean) : [],
      bankCode: nextProfile?.bankCode || '',
      bankName: nextProfile?.bankName || '',
      bankAccount: nextProfile?.bankAccount || '',
      bankBeneficiary: nextProfile?.bankBeneficiary || '',
    });
    if (nextProfile?.referredBy) {
      setReferralInput(nextProfile.referredBy.userCode || nextProfile.referredBy.phone || nextProfile.referredBy.email || '');
      setVerifiedReferrer(nextProfile.referredBy);
    } else {
      setReferralInput('');
      setVerifiedReferrer(null);
    }
  };

  const loadProfile = () => {
    setLoadingProfile(true);
    feApi.getProfile()
      .then((response) => {
        if (response?.result === 'success') {
          setProfile(response.profile);
          fillAccountForm(response.profile);
        }
      })
      .catch(() => {
        ToastHelper.showError('Không tải được thông tin tài khoản');
      })
      .finally(() => {
        setLoadingProfile(false);
      });
  };

  useEffect(() => {
    loadProfile();
  }, []);

  const openWalletTopup = (walletScope) => {
    setShowWalletMenu(false);
    setTopupWalletScope(walletScope);
    setTopupWalletAmount('100000');
    setTopupPaymentMethod('BIDV');
    setTopupWalletState('idle');
    setTopupWalletResult(null);
    setTopupWalletError('');
  };

  const closeWalletTopup = () => {
    if (topupWalletState === 'loading') return;
    setTopupWalletScope(null);
    setTopupWalletResult(null);
    setTopupWalletError('');
  };

  const createWalletTopup = async () => {
    const amount = Number(String(topupWalletAmount).replace(/[^0-9]/g, ''));
    if (!Number.isFinite(amount) || amount < 100000) {
      setTopupWalletError('Số tiền nạp tối thiểu là 100.000đ.');
      return;
    }
    setTopupWalletState('loading');
    setTopupWalletError('');
    try {
      const response = await feApi.topUpWallet({
        amount,
        fundingSource: topupWalletScope === 'RIDE' ? 'BIDV' : topupPaymentMethod,
        currency: 'VND',
        redirectUrl: typeof window !== 'undefined'
          ? `${window.location.origin}${window.location.pathname}`
          : undefined,
        walletScope: topupWalletScope,
      });
      if (response?.result !== 'success') {
        throw new Error(response?.reason || 'Không tạo được giao dịch nạp tiền.');
      }
      if (topupPaymentMethod !== 'BIDV') {
        if (!response?.link) {
          throw new Error('Cổng thanh toán chưa trả về đường dẫn thanh toán.');
        }
        ToastHelper.showSuccess(`Đang chuyển sang cổng thanh toán ${topupPaymentMethod === 'MOMO' ? 'MoMo' : 'VNPAY'}...`);
        window.location.href = response.link;
        return;
      }
      if (!response?.qrData?.qrCode) {
        throw new Error(response?.reason || 'Không tạo được mã QR nạp tiền.');
      }
      setTopupWalletResult(response);
      setTopupWalletState('success');
    } catch (error) {
      setTopupWalletState('error');
      setTopupWalletError(error?.response?.data?.reason || error?.message || 'Không tạo được mã QR nạp tiền.');
    }
  };

  const handleCopyToClipboard = (text, label) => {
    if (!text) return;
    try {
      if (navigator?.clipboard?.writeText) {
        navigator.clipboard.writeText(String(text));
      } else {
        const textarea = document.createElement('textarea');
        textarea.value = String(text);
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
      }
      ToastHelper.showSuccess(`Đã sao chép ${label || 'thông tin'}!`);
    } catch (e) {
      ToastHelper.showSuccess(`Đã sao chép ${label || 'thông tin'}!`);
    }
  };

  const handleDownloadQrCode = () => {
    try {
      const svg = document.querySelector('.customer-topup-qr-wrapper svg') || document.querySelector('.customer-topup-qr-card svg');
      if (!svg) {
        ToastHelper.showError('Không tìm thấy mã QR để tải về');
        return;
      }
      const svgData = new XMLSerializer().serializeToString(svg);
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();
      img.onload = () => {
        canvas.width = img.width + 40;
        canvas.height = img.height + 40;
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, 20, 20);
        const pngUrl = canvas.toDataURL('image/png');
        const link = document.createElement('a');
        link.download = `EVG-QR-NAP-TIEN-${topupWalletResult?.qrData?.amount || 'TOPUP'}.png`;
        link.href = pngUrl;
        link.click();
        ToastHelper.showSuccess('Đã tải mã QR về máy');
      };
      img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)));
    } catch (err) {
      ToastHelper.showError('Không thể tải mã QR');
    }
  };

  // Hồ sơ trả về hai ví độc lập: ví EVG dùng cho hệ sinh thái/hoa hồng và
  // ví Xe ghép chỉ dùng để thanh toán các chuyến xe ghép.
  const evgWallet = Array.isArray(profile?.wallets) ? profile.wallets[0] : null;
  const rideWallet = Array.isArray(profile?.rideWallets) ? profile.rideWallets[0] : null;
  const evgWalletBalance = formatCurrency(evgWallet?.balance || 0);
  const rideWalletBalance = formatCurrency(rideWallet?.balance || 0);

  const getLocalNotifications = () => {
    try {
      return JSON.parse(localStorage.getItem(`evg_local_notifications_${user?.userId || 'guest'}`) || '[]');
    } catch (e) {
      return [];
    }
  };

  const addLocalNotification = (notif) => {
    const local = getLocalNotifications();
    const newNotif = {
      notificationId: `LOC_${Date.now()}`,
      isRead: 0,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      ...notif,
    };
    localStorage.setItem(`evg_local_notifications_${user?.userId || 'guest'}`, JSON.stringify([newNotif, ...local]));
    return newNotif;
  };

  const updateLocalNotification = (notificationId, action) => {
    if (!String(notificationId).startsWith('LOC_')) return;
    const local = getLocalNotifications();
    if (action === 'read') {
      const updated = local.map(n => n.notificationId === notificationId ? { ...n, isRead: 1 } : n);
      localStorage.setItem(`evg_local_notifications_${user?.userId || 'guest'}`, JSON.stringify(updated));
    } else if (action === 'delete') {
      const updated = local.filter(n => n.notificationId !== notificationId);
      localStorage.setItem(`evg_local_notifications_${user?.userId || 'guest'}`, JSON.stringify(updated));
    }
  };

  const bulkUpdateLocalNotifications = (notificationIds, action) => {
    const local = getLocalNotifications();
    if (action === 'read') {
      const updated = local.map(n => notificationIds.includes(n.notificationId) ? { ...n, isRead: 1 } : n);
      localStorage.setItem(`evg_local_notifications_${user?.userId || 'guest'}`, JSON.stringify(updated));
    } else if (action === 'delete') {
      const updated = local.filter(n => !notificationIds.includes(n.notificationId));
      localStorage.setItem(`evg_local_notifications_${user?.userId || 'guest'}`, JSON.stringify(updated));
    } else if (action === 'readAll') {
      const updated = local.map(n => ({ ...n, isRead: 1 }));
      localStorage.setItem(`evg_local_notifications_${user?.userId || 'guest'}`, JSON.stringify(updated));
    }
  };

  const loadNotifications = () => {
    setLoadingNotifications(true);
    feApi.getNotifications({ page: 0, limit: 10, isSent: 1 })
      .then((response) => {
        const notifs = response?.result === 'success' && Array.isArray(response.notifications) ? response.notifications : [];
        const combined = [...getLocalNotifications(), ...notifs];
        setNotifications(combined.slice(0, 4));
        setAllNotifications(combined);
      })
      .catch(() => {
        const local = getLocalNotifications();
        setNotifications(local.slice(0, 4));
        setAllNotifications(local);
      })
      .finally(() => {
        setLoadingNotifications(false);
      });
  };

  useEffect(() => {
    loadNotifications();
    const timer = setInterval(loadNotifications, 60000);
    return () => clearInterval(timer);
  }, []);

  const loadAllNotifications = () => {
    setLoadingAllNotifications(true);
    feApi.getNotifications({ page: 0, limit: 200, isSent: 1 })
      .then((response) => {
        const notifs = response?.result === 'success' && Array.isArray(response.notifications) ? response.notifications : [];
        const combined = [...getLocalNotifications(), ...notifs];
        setAllNotifications(combined);
      })
      .catch(() => {
        setAllNotifications(getLocalNotifications());
      })
      .finally(() => {
        setLoadingAllNotifications(false);
      });
  };

  useEffect(() => {
    if (activeTab === 'notifications') {
      loadAllNotifications();
    }
  }, [activeTab]);

  const dashboardStats = useMemo(() => [
    {
      label: 'Tổng doanh thu',
      value: formatCurrency(affiliateData.summary?.totalRevenue),
      note: 'Doanh thu từ các đơn bạn tạo/giới thiệu',
    },
    {
      label: 'Hoa hồng chờ duyệt',
      value: formatCurrency(affiliateData.summary?.pendingCommission),
      note: 'Đơn chưa hoàn thành, chưa mở khóa ví',
    },
    {
      label: 'Có thể rút',
      value: formatCurrency(affiliateData.summary?.availableCommission),
      note: 'Đơn đã hoàn thành trừ lệnh rút đang xử lý',
    },
  ], [affiliateData.summary]);

  const totalRevenueValue = useMemo(() => (
    Number(affiliateData.summary?.totalRevenue ?? orders.reduce((sum, order) => sum + Number(order.amountAfterTax || order.amount || 0), 0) ?? 0)
  ), [affiliateData.summary?.totalRevenue, orders]);
  const realPending = Number(affiliateData.summary?.pendingCommission ?? 0);
  const realAvailable = Number(affiliateData.summary?.availableCommission ?? 0);
  const realRejected = Number(affiliateData.summary?.rejectedCommission ?? 0);
  const walletBalanceValue = Number(affiliateData.summary?.walletBalance ?? 0);
  const withdrawalPendingValue = Number(affiliateData.summary?.withdrawalPending ?? 0);
  const walletWithdrawableValue = Math.max(realAvailable, 0);
  const pendingCommissionValue = realPending;
  const availableCommissionValue = Math.max(realAvailable, 0);
  const rejectedCommissionValue = realRejected;
  
  const totalCommissionValue = pendingCommissionValue + availableCommissionValue + rejectedCommissionValue;
  
  const pctRejected = totalCommissionValue > 0 ? Math.round((rejectedCommissionValue / totalCommissionValue) * 100) : 0;
  const pctPending = totalCommissionValue > 0 ? Math.round((pendingCommissionValue / totalCommissionValue) * 100) : 0;
  const chartGradient = totalCommissionValue > 0 
    ? `conic-gradient(#ff4d4f 0% ${pctRejected}%, #4ade80 ${pctRejected}% ${pctRejected + pctPending}%, #16a34a ${pctRejected + pctPending}% 100%)`
    : `conic-gradient(#e2e8f0 0% 100%)`;

  const currentRankLabel = affiliateData.summary?.rankName || affiliateData.summary?.tierName || affiliateData.summary?.rank || affiliateData.summary?.tier || '—';
  const amountToNextRank = Number(affiliateData.summary?.amountToNextRank ?? affiliateData.summary?.amountToNextTier ?? 0);
  const nextRankText = amountToNextRank > 0
    ? `Còn ${formatCurrency(amountToNextRank)} để lên hạng tiếp theo`
    : 'Theo dữ liệu tài khoản';
  const tierCurrentAmount = Number(affiliateData.summary?.tierRevenue ?? affiliateData.summary?.currentRankRevenue ?? totalRevenueValue ?? 0);
  const tierTargetAmount = Number(affiliateData.summary?.nextRankTarget ?? affiliateData.summary?.nextTierTarget ?? 0);
  const tierProgressPercent = tierTargetAmount > 0 ? Math.min(100, Math.round((tierCurrentAmount / tierTargetAmount) * 100)) : 0;

  const overviewKpis = useMemo(() => {
    return [
      {
        key: 'revenue',
        label: 'Tổng doanh thu (VNĐ)',
        value: totalRevenueValue > 0 ? formatCurrency(totalRevenueValue).replace('₫', '').trim() : '0',
        change: 'Dữ liệu thực tế',
        icon: 'fa-solid fa-chart-simple',
        color: '#16a34a',
      },
      {
        key: 'pendingCommission',
        label: 'Hoa hồng chờ duyệt (VNĐ)',
        value: pendingCommissionValue > 0 ? formatCurrency(pendingCommissionValue).replace('₫', '').trim() : '0',
        change: 'Dữ liệu thực tế',
        icon: 'fa-regular fa-money-bill-1',
        color: '#16a34a',
      },
      {
        key: 'availableCommission',
        label: 'Hoa hồng đã duyệt (VNĐ)',
        value: availableCommissionValue > 0 ? formatCurrency(availableCommissionValue).replace('₫', '').trim() : '0',
        change: 'Dữ liệu thực tế',
        icon: 'fa-regular fa-credit-card',
        color: '#16a34a',
      },
      {
        key: 'orders',
        label: 'Số đơn hàng',
        value: orders.length,
        change: 'Dữ liệu thực tế',
        icon: 'fa-regular fa-file-lines',
        color: '#16a34a',
      },
    ];
  }, [availableCommissionValue, orders.length, pendingCommissionValue, totalRevenueValue]);

  const overviewChartData = useMemo(() => {
    const now = new Date();
    const emptyData = Array.from({ length: 6 }, (_, index) => {
      const date = new Date(now.getFullYear(), now.getMonth() - (5 - index), 1);
      return {
        month: `${String(date.getMonth() + 1).padStart(2, '0')}/${date.getFullYear()}`,
        revenue: 0,
        commission: 0,
      };
    });
    const data = affiliateData.performance?.length
      ? affiliateData.performance.slice(-6)
      : emptyData;
    const maxValue = Math.max(...data.flatMap((item) => [Number(item.revenue || 0), Number(item.commission || 0)]), 0);
    return data.map((item) => ({
      ...item,
      revenueHeight: maxValue > 0 ? Math.max(8, Math.round((Number(item.revenue || 0) / maxValue) * 170)) : 0,
      commissionHeight: maxValue > 0 ? Math.max(8, Math.round((Number(item.commission || 0) / maxValue) * 170)) : 0,
      label: String(item.month || '').includes('-') ? String(item.month).slice(5) : item.month,
    }));
  }, [affiliateData.performance]);

  const incomeTrendSummary = useMemo(() => {
    const totalRevenue = overviewChartData.reduce((sum, item) => sum + Number(item.revenue || 0), 0);
    const totalCommission = overviewChartData.reduce((sum, item) => sum + Number(item.commission || 0), 0);
    const maxValue = Math.max(...overviewChartData.flatMap((item) => [Number(item.revenue || 0), Number(item.commission || 0)]), 0);
    return { totalRevenue, totalCommission, maxValue };
  }, [overviewChartData]);

  const recentOverviewOrders = useMemo(() => {
    return orders.slice(0, 5).map((order) => {
      const note = parseOrderNote(order?.note);
      const productNames = (order?.inventoryItems || []).map((item) => item?.name).filter(Boolean).join(', ');
      return {
        id: order.saleId,
        code: `${String(order.saleId || '').padStart(8, '0')}`,
        buyer: note.buyerName || user?.fullname || '-',
        productNames: productNames || '-',
        amount: Number(order.amountAfterTax || 0),
        commission: Number(order.commissionAmount ?? order.commission ?? 0),
        status: order.status,
      };
    });
  }, [orders, user]);

  const bestSellingProducts = useMemo(() => {
    return products.slice(0, 4).map((product) => {
      const sold = Number(product.soldQuantity ?? product.sold ?? product.totalSold ?? 0);
      return {
        id: product.id,
        name: product.name,
        sold,
        price: product.price || formatCurrency(product.rawPrice || 0),
        rawPrice: Number(product.rawPrice || 0),
        revenue: Number(product.rawPrice || 0) * sold,
        image: product.image,
      };
    });
  }, [products]);

  const overviewNotifications = useMemo(() => {
    return notifications.slice(0, 4);
  }, [notifications]);

  const commerceOrderRows = useMemo(() => {
    return orders.map((order) => {
      const note = parseOrderNote(order?.note);
      const payment = order?.payment || null;
      const paymentStatus = payment?.status || note.paymentStatus || '';
      return {
        id: order.saleId,
        code: `${String(order.saleId || '').padStart(8, '0')}`,
        productNames: (order?.inventoryItems || []).map((item) => item?.name).filter(Boolean).join(', ') || '-',
        quantity: (order?.inventoryItems || []).reduce((sum, item) => sum + Number(item?.quantity || 0), 0) || 1,
        buyer: note.buyerName || user?.fullname || '-',
        phone: note.buyerPhone || user?.phone || '-',
        amount: Number(order.amountAfterTax ?? order.amount ?? 0),
        status: order.status || '',
        payment,
        paymentStatus,
        updatedAt: order.updatedAt || order.createdAt || '',
        sourceOrder: order,
      };
    });
  }, [orders, user]);

  const filteredCommerceOrders = useMemo(() => {
    if (selectedCommerceStatus === 'ALL') return commerceOrderRows;
    if (selectedCommerceStatus === 'PAID') {
      return commerceOrderRows.filter((order) => ['PAID', 'PROCESSING', 'SHIPPING', 'INSTALLING', 'INSTALLTING', 'INSTALLED'].includes(order.status));
    }
    return commerceOrderRows.filter((order) => order.status === selectedCommerceStatus);
  }, [commerceOrderRows, selectedCommerceStatus]);

  const displayedCommerceOrders = useMemo(() => (
    showAllCommerceOrders ? filteredCommerceOrders : filteredCommerceOrders.slice(0, 5)
  ), [filteredCommerceOrders, showAllCommerceOrders]);

  const commerceOrderTabs = useMemo(() => [
    { key: 'ALL', label: 'Tất cả', count: commerceOrderRows.length },
    { key: 'PENDING', label: 'Chờ duyệt', count: commerceOrderRows.filter((order) => order.status === 'PENDING').length },
    { key: 'ORDERED', label: 'Chờ thanh toán', count: commerceOrderRows.filter((order) => order.status === 'ORDERED').length },
    { key: 'PAID', label: 'Đã thanh toán', count: commerceOrderRows.filter((order) => ['PAID', 'PROCESSING', 'SHIPPING', 'INSTALLING', 'INSTALLTING', 'INSTALLED'].includes(order.status)).length },
    { key: 'COMPLETED', label: 'Hoàn thành', count: commerceOrderRows.filter((order) => order.status === 'COMPLETED').length },
    { key: 'REJECTED', label: 'Từ chối', count: commerceOrderRows.filter((order) => order.status === 'REJECTED').length },
    { key: 'CANCELLED', label: 'Đã hủy', count: commerceOrderRows.filter((order) => order.status === 'CANCELLED').length },
  ], [commerceOrderRows]);

  const handleCommerceKpiClick = (item) => {
    if (item.targetTab === 'wallet') {
      setCommissionTab('direct');
      setCommissionKeyword('');
      setCommissionStatus('ALL');
      setCommissionPeriod('ALL');
      changeTab('wallet');
      setTimeout(() => {
        document.querySelector('[data-wallet-content]')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 120);
      return;
    }

    if (item.targetStatus) {
      setSelectedCommerceStatus(item.targetStatus);
      setShowAllCommerceOrders(false);
    }

    setTimeout(() => {
      commerceOrderSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 50);
  };

  const commerceKpis = useMemo(() => {
    const payableOrders = commerceOrderRows.filter((order) => !['CANCELLED', 'REJECTED', 'FAILED'].includes(String(order.status || '').toUpperCase()));
    const totalAmount = payableOrders.reduce((sum, order) => sum + Number(order.amount || 0), 0);
    const completed = commerceOrderRows.filter((order) => order.status === 'COMPLETED').length;
    const paid = commerceOrderRows.filter((order) => ['PAID', 'PROCESSING', 'SHIPPING', 'INSTALLING', 'INSTALLTING', 'INSTALLED'].includes(order.status)).length;
    return [
      { label: 'Tổng đơn hàng', value: commerceOrderRows.length, note: 'Dữ liệu thực tế', icon: 'fa-regular fa-rectangle-list', color: '#3b82f6', targetStatus: 'ALL' },
      { label: 'Chờ thanh toán', value: commerceOrderRows.filter((order) => order.status === 'ORDERED').length, note: 'Cần thanh toán', icon: 'fa-regular fa-clock', color: '#f59e0b', targetStatus: 'ORDERED' },
      { label: 'Đã thanh toán', value: paid, note: 'Đã ghi nhận thanh toán', icon: 'fa-solid fa-wallet', color: '#22c55e', targetStatus: 'PAID' },
      { label: 'Hoàn thành', value: completed, note: 'Dữ liệu thực tế', icon: 'fa-regular fa-circle-check', color: '#22c55e', targetStatus: 'COMPLETED' },
      { label: 'Tổng chi tiêu', value: formatCurrency(totalAmount), note: 'Dữ liệu thực tế', icon: 'fa-solid fa-receipt', color: '#ef4444', keepValueOnOneLine: true, targetStatus: 'ALL' },
      { label: 'Hoa hồng ước tính', value: formatCurrency(pendingCommissionValue), note: 'Dữ liệu thực tế', icon: 'fa-solid fa-hand-holding-dollar', color: '#22c55e', keepValueOnOneLine: true, targetTab: 'wallet' },
    ];
  }, [commerceOrderRows, pendingCommissionValue]);

  const commerceCartDisplayItems = useMemo(() => {
    return cartItems;
  }, [cartItems]);

  const commerceCartSubtotal = useMemo(() => (
    commerceCartDisplayItems.reduce((sum, item) => sum + Number(item.product?.rawPrice || 0) * Number(item.quantity || 1), 0)
  ), [commerceCartDisplayItems]);
  const commerceCartVat = useMemo(() => computeVatSummary(
    commerceCartDisplayItems.map((item) => ({ price: item.product?.rawPrice, quantity: item.quantity || 1 })),
    vatPercent,
    vatExclusive
  ), [commerceCartDisplayItems, vatPercent, vatExclusive]);

  const networkRows = useMemo(() => ({
    landlords: affiliateData.network?.landlords || [],
    investors: affiliateData.network?.investors || [],
    retailCustomers: affiliateData.network?.retailCustomers || [],
  }), [affiliateData.network]);

  const allNetworkRows = useMemo(() => [
    ...networkRows.landlords.map((item) => ({ ...item, networkGroup: 'LANDLORD' })),
    ...networkRows.investors.map((item) => ({ ...item, networkGroup: 'INVESTOR' })),
    ...networkRows.retailCustomers.map((item) => ({ ...item, networkGroup: 'CUSTOMER' })),
  ], [networkRows]);

  const networkStatusOptions = useMemo(() => Array.from(new Set(
    allNetworkRows.map((item) => String(item.status || 'Mới').trim()).filter(Boolean)
  )).sort((a, b) => a.localeCompare(b, 'vi')), [allNetworkRows]);

  const filteredNetworkRows = useMemo(() => {
    const keyword = networkKeyword.trim().toLowerCase();
    const now = Date.now();
    const periodDays = networkPeriod === 'ALL' ? null : Number(networkPeriod);
    return allNetworkRows
      .filter((item) => {
        const searchable = [item.name, item.phone, item.email, item.productName, item.referralCode, item.code]
          .filter(Boolean).join(' ').toLowerCase();
        const itemStatus = String(item.status || 'Mới');
        const itemDate = new Date(item.updatedAt || item.createdAt || 0).getTime();
        const matchesPeriod = periodDays == null || !itemDate || now - itemDate <= periodDays * 86400000;
        return (!keyword || searchable.includes(keyword))
          && (networkGroup === 'ALL' || item.networkGroup === networkGroup)
          && (networkStatus === 'ALL' || itemStatus === networkStatus)
          && matchesPeriod;
      })
      .sort((a, b) => {
        if (networkSort === 'NAME_ASC') return String(a.name || '').localeCompare(String(b.name || ''), 'vi');
        if (networkSort === 'REVENUE_DESC') return Number(b.revenue || 0) - Number(a.revenue || 0);
        if (networkSort === 'ORDERS_DESC') return Number(b.orders || 0) - Number(a.orders || 0);
        return new Date(b.updatedAt || b.createdAt || 0).getTime() - new Date(a.updatedAt || a.createdAt || 0).getTime();
      });
  }, [allNetworkRows, networkKeyword, networkGroup, networkStatus, networkPeriod, networkSort]);

  const filteredNetworkGroups = useMemo(() => ({
    landlords: filteredNetworkRows.filter((item) => item.networkGroup === 'LANDLORD'),
    investors: filteredNetworkRows.filter((item) => item.networkGroup === 'INVESTOR'),
    retailCustomers: filteredNetworkRows.filter((item) => item.networkGroup === 'CUSTOMER'),
  }), [filteredNetworkRows]);

  const networkMetrics = useMemo(() => {
    const contacts = filteredNetworkRows.length;
    const orders = filteredNetworkRows.reduce((sum, item) => sum + Number(item.orders || 0), 0);
    const revenue = filteredNetworkRows.reduce((sum, item) => sum + Number(item.revenue || 0), 0);
    const activeContacts = filteredNetworkRows.filter(item => Number(item.orders || 0) > 0 || Number(item.revenue || 0) > 0).length;
    const conversionRate = contacts > 0 ? ((activeContacts / contacts) * 100).toFixed(1) + '%' : '0%';
    return { contacts, orders, revenue, conversionRate };
  }, [filteredNetworkRows]);

  const resetNetworkFilters = () => {
    setNetworkKeyword('');
    setNetworkGroup('ALL');
    setNetworkStatus('ALL');
    setNetworkPeriod('ALL');
    setNetworkSort('UPDATED_DESC');
  };

  const handleNetworkKpiClick = (group = 'ALL') => {
    setNetworkGroup(group);
    setNetworkPeriod('30');
    setTimeout(() => {
      const target = document.querySelector('[data-network-filter]');
      target?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 50);
  };

  const getNetworkMemberIdentity = (member = {}) => {
    const userId = member.userId
      || member.memberUserId
      || member.customerUserId
      || member.agencyUserId
      || member.referrerUserId
      || member.referredUserId;

    return {
      ...member,
      userId,
      fullName: member.fullName || member.name || member.email || member.phone || 'thành viên',
      memberCode: member.memberCode || member.code || member.customerCode || member.agencyCode || member.referralCode || '-',
    };
  };

  const resolveNetworkMemberIdentity = async (member) => {
    const normalizedMember = getNetworkMemberIdentity(member);
    if (normalizedMember.userId) return normalizedMember;

    const searchValue = normalizedMember.email
      || normalizedMember.phone
      || normalizedMember.name
      || normalizedMember.fullName
      || normalizedMember.memberCode;
    if (!searchValue || searchValue === '-') return normalizedMember;

    const response = await feApi.getAffiliateNetworkDirectMembers({
      page: 1,
      limit: 100,
      search: searchValue,
    });
    const items = Array.isArray(response?.data?.items) ? response.data.items : [];
    const matchedMember = items.find((item) => {
      const sameCode = item.memberCode && normalizedMember.memberCode && item.memberCode === normalizedMember.memberCode;
      const sameName = item.fullName && normalizedMember.fullName && item.fullName === normalizedMember.fullName;
      return sameCode || sameName;
    }) || items[0];

    return matchedMember ? getNetworkMemberIdentity({ ...normalizedMember, ...matchedMember }) : normalizedMember;
  };

  const handleOpenMemberTree = async (member) => {
    const initialMember = getNetworkMemberIdentity(member);
    setSelectedMemberTreeModal(initialMember);
    setMemberTreeData(null);
    setLoadingMemberTree(true);
    try {
      const normalizedMember = await resolveNetworkMemberIdentity(member);
      if (!normalizedMember.userId) {
        setSelectedMemberTreeModal(null);
        ToastHelper.showError('Không tìm thấy mã thành viên để xem mạng lưới.');
        return;
      }
      setSelectedMemberTreeModal(normalizedMember);
      const response = await feApi.getAffiliateNetworkMemberTree(normalizedMember.userId, { maxDepth: 10 });
      if (response && response.success && response.data) {
        setMemberTreeData(response.data);
      } else {
        ToastHelper.showError(response?.reason || 'Không thể đọc chi tiết cây mạng lưới.');
      }
    } catch (err) {
      console.error('Lỗi khi tải cây mạng lưới thành viên:', err);
      ToastHelper.showError(err?.response?.data?.reason || err?.reason || err?.message || 'Không có quyền xem hoặc lỗi tải cây mạng lưới.');
    } finally {
      setLoadingMemberTree(false);
    }
  };

  const walletSummaryCards = useMemo(() => [
    { label: 'Số dư ví', value: formatCurrency(walletBalanceValue), change: 'Gồm tiền admin nạp', icon: 'fa-solid fa-wallet', color: '#0ea5e9' },
    { label: 'Số dư có thể rút', value: formatCurrency(walletWithdrawableValue), change: 'Theo hoa hồng khả dụng', icon: 'fa-regular fa-money-bill-1', color: '#22c55e' },
    { label: 'Chờ duyệt', value: formatCurrency(withdrawalPendingValue), change: 'Yêu cầu rút tiền', icon: 'fa-regular fa-clock', color: '#f59e0b' },
    { label: 'Đã rút', value: formatCurrency(affiliateData.summary?.withdrawnCommission ?? 0), change: 'Dữ liệu thực tế', icon: 'fa-solid fa-building-columns', color: '#3b82f6' },
  ], [affiliateData.summary, walletBalanceValue, walletWithdrawableValue, withdrawalPendingValue]);

  const walletTransactionRows = useMemo(() => {
    const directRows = (affiliateData.commissions?.direct || []).map((item) => ({
      ...item,
      rowId: `direct-${item.saleId || item.saleCode}`,
      rowType: 'direct',
      typeLabel: 'Hoa hồng bán hàng',
      customerName: item.customerName || item.buyer || 'Khách hàng',
    }));
    const passiveRows = (affiliateData.commissions?.passive || []).map((item) => ({
      ...item,
      rowId: `passive-${item.saleId || item.walletTransactionId || item.saleCode}`,
      rowType: 'passive',
      typeLabel: 'Hoa hồng từ mạng lưới',
      customerName: item.customerName || item.buyer || item.description || 'Đối tác trong mạng lưới',
    }));
    const withdrawalRows = (affiliateData.withdrawals || []).map((item) => ({
      ...item,
      rowId: `withdrawal-${item.walletTransactionId}`,
      rowType: 'withdrawal',
      saleCode: item.walletTransactionId,
      productName: item.description || 'Yêu cầu rút tiền',
      typeLabel: 'Rút tiền',
      customerName: accountForm.bankBeneficiary || profile?.fullname || 'Tài khoản ngân hàng',
      amount: Math.abs(Number(item.amount || 0)),
    }));
    const walletRows = (affiliateData.walletTransactions || []).map((item) => ({
      ...item,
      rowId: `wallet-${item.walletTransactionId}`,
      rowType: 'wallet',
      saleCode: item.walletTransactionCode || item.walletTransactionId,
      productName: item.description || getWalletFundingSourceName(item.fundingSource),
      typeLabel: getWalletFundingSourceName(item.fundingSource),
      customerName: profile?.fullname || 'Ví khách hàng',
      amount: Number(item.amount || 0),
    }));
    const orderRows = (orders || []).map((order) => {
      const note = parseOrderNote(order?.note);
      const productNames = (order?.inventoryItems || []).map((item) => item?.name).filter(Boolean).join(', ');
      const status = order?.payment?.status || note.paymentStatus || order?.status || '';
      const isFailed = ['FAILED', 'CANCELLED', 'REJECTED'].includes(String(status).toUpperCase());
      return {
        ...order,
        rowId: `product-order-${order.saleId}`,
        rowType: 'order',
        saleCode: order.saleCode || `DH${String(order.saleId || '').padStart(8, '0')}`,
        productName: productNames || 'Đơn hàng sản phẩm',
        typeLabel: 'Đơn hàng',
        customerName: note.buyerName || profile?.fullname || 'Khách hàng',
        customerEmail: profile?.email || '',
        amount: isFailed ? 0 : -Math.abs(Number(order.amountAfterTax ?? order.amount ?? 0)),
        amountDisplay: isFailed ? '—' : null,
        status,
        createdAt: order.createdAt || order.time,
      };
    });
    return { directRows, passiveRows, transactionRows: [...walletRows, ...orderRows, ...directRows, ...passiveRows, ...withdrawalRows] };
  }, [affiliateData.commissions, affiliateData.withdrawals, affiliateData.walletTransactions, accountForm.bankBeneficiary, profile?.fullname, profile?.email, orders]);

  const walletCommissionRows = useMemo(() => {
    const source = commissionTab === 'direct'
      ? walletTransactionRows.directRows
      : commissionTab === 'passive'
        ? walletTransactionRows.passiveRows
        : walletTransactionRows.transactionRows;
    const keyword = commissionKeyword.trim().toLowerCase();
    const periodDays = commissionPeriod === 'ALL' ? null : Number(commissionPeriod);
    const now = Date.now();
    return source.filter((item) => {
      const searchable = [item.saleCode, item.productName, item.customerName, item.customerEmail, item.typeLabel, item.description]
        .filter(Boolean).join(' ').toLowerCase();
      const itemDate = new Date(item.createdAt || item.time || 0).getTime();
      return (!keyword || searchable.includes(keyword))
        && (commissionStatus === 'ALL' || String(item.status || '') === commissionStatus)
        && (periodDays == null || !itemDate || now - itemDate <= periodDays * 86400000);
    });
  }, [commissionTab, walletTransactionRows, commissionKeyword, commissionStatus, commissionPeriod]);

  const insightDetails = useMemo(() => {
    const orderRows = commerceOrderRows.map((order) => ({
      id: `order-${order.id}`,
      title: order.productNames || `Đơn ${order.code}`,
      subtitle: `${order.buyer || '-'}${order.phone ? ` - ${order.phone}` : ''}`,
      amount: order.amount,
      status: order.status,
      time: order.updatedAt,
    }));
    const pendingRows = walletTransactionRows.directRows
      .filter((item) => !['COMPLETED', 'CANCELLED'].includes(String(item.status || '').toUpperCase()))
      .map((item) => ({
        id: `pending-${item.rowId}`,
        title: item.productName || item.saleCode || 'Hoa hồng chờ duyệt',
        subtitle: item.customerName || item.buyer || item.description || '-',
        amount: item.amount,
        status: item.status,
        time: item.createdAt || item.time,
      }));
    const approvedRows = [...walletTransactionRows.directRows, ...walletTransactionRows.passiveRows]
      .filter((item) => String(item.status || '').toUpperCase() === 'COMPLETED')
      .map((item) => ({
        id: `approved-${item.rowId}`,
        title: item.productName || item.saleCode || 'Hoa hồng đã duyệt',
        subtitle: item.customerName || item.buyer || item.description || item.typeLabel || '-',
        amount: item.amount,
        status: item.status,
        time: item.createdAt || item.time,
      }));

    return {
      revenue: {
        title: 'Chi tiết doanh thu',
        description: 'Các đơn hàng thực tế của tài khoản này.',
        totalLabel: 'Tổng doanh thu',
        totalValue: totalRevenueValue,
        rows: orderRows,
        emptyText: 'Chưa có đơn hàng phát sinh doanh thu.',
      },
      pendingCommission: {
        title: 'Hoa hồng chờ duyệt',
        description: 'Các khoản hoa hồng từ đơn chưa hoàn thành hoặc chưa mở khóa.',
        totalLabel: 'Tổng chờ duyệt',
        totalValue: pendingCommissionValue,
        rows: pendingRows,
        emptyText: 'Không có hoa hồng đang chờ duyệt.',
      },
      availableCommission: {
        title: 'Hoa hồng đã duyệt',
        description: 'Các khoản hoa hồng đã ghi nhận vào ví.',
        totalLabel: 'Tổng đã duyệt',
        totalValue: availableCommissionValue,
        rows: approvedRows,
        emptyText: 'Không có hoa hồng đã duyệt.',
      },
      orders: {
        title: 'Chi tiết đơn hàng',
        description: 'Danh sách đơn hàng thực tế của tài khoản.',
        totalLabel: 'Tổng số đơn',
        totalValue: orderRows.length,
        rows: orderRows,
        emptyText: 'Tài khoản này chưa có đơn hàng.',
        isCount: true,
      },
    };
  }, [commerceOrderRows, pendingCommissionValue, availableCommissionValue, totalRevenueValue, walletTransactionRows]);

  const selectedInsight = selectedInsightKey ? insightDetails[selectedInsightKey] : null;

  const commissionStatusOptions = useMemo(() => Array.from(new Set(
    walletTransactionRows.transactionRows.map((item) => String(item.status || '')).filter(Boolean)
  )), [walletTransactionRows]);

  const resetCommissionFilters = () => {
    setCommissionKeyword('');
    setCommissionStatus('ALL');
    setCommissionPeriod('ALL');
  };

  const exportWalletRows = () => {
    if (!walletCommissionRows.length) {
      ToastHelper.showError('Không có dữ liệu để xuất');
      return;
    }
    const csvEscape = (value) => `"${String(value ?? '').replace(/"/g, '""')}"`;
    const csv = [
      ['Mã giao dịch', 'Loại giao dịch', 'Nội dung', 'Khách hàng/Người nhận', 'Số tiền', 'Ngày tạo', 'Trạng thái'],
      ...walletCommissionRows.map((item) => [
        item.saleCode,
        item.typeLabel,
        item.productName,
        item.customerName,
        item.amountDisplay ?? item.amount,
        formatDateTime(item.createdAt || item.time),
        getOrderStatusName(item.status),
      ]),
    ].map((row) => row.map(csvEscape).join(',')).join('\n');
    const url = URL.createObjectURL(new Blob([`\ufeff${csv}`], { type: 'text/csv;charset=utf-8' }));
    const link = document.createElement('a');
    link.href = url;
    link.download = `hoa-hong-${commissionTab}-${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const toolDocuments = useMemo(() => {
    let result = [...marketingDocuments];
    if (toolKeyword) {
      const lowerKeyword = toolKeyword.toLowerCase();
      result = result.filter(item => (
        item.title?.toLowerCase().includes(lowerKeyword)
        || getMarketingDocumentTypeName(item.type).toLowerCase().includes(lowerKeyword)
        || getMarketingDocumentSummary(item).toLowerCase().includes(lowerKeyword)
      ));
    }
    if (toolCategory !== 'ALL') {
      result = result.filter(item => getMarketingDocumentCategory(item) === toolCategory);
    }
    if (toolFileType !== 'ALL') {
      result = result.filter(item => {
        const type = String(item.type || '').toUpperCase();
        if (toolFileType === 'DOCUMENT') return !['POLICY', 'GUIDE'].includes(type);
        return type === toolFileType;
      });
    }
    if (toolFormat !== 'ALL') {
      result = result.filter(item => getMarketingDocumentFormat(item) === toolFormat);
    }
    if (toolSortBy === 'NEWEST') {
      result.sort((a, b) => new Date(b.updatedAt || 0) - new Date(a.updatedAt || 0));
    } else if (toolSortBy === 'OLDEST') {
      result.sort((a, b) => new Date(a.updatedAt || 0) - new Date(b.updatedAt || 0));
    }
    return result;
  }, [marketingDocuments, toolKeyword, toolCategory, toolFileType, toolFormat, toolSortBy]);

  const documentToolDocuments = useMemo(
    () => toolDocuments.filter((item) => !['POLICY', 'GUIDE'].includes(String(item.type || '').toUpperCase())),
    [toolDocuments]
  );

  const interestedToolDocuments = useMemo(
    () => documentToolDocuments.slice(0, 3),
    [documentToolDocuments]
  );

  const guideToolDocuments = useMemo(() => {
    return marketingDocuments
      .filter((item) => String(item.type || '').toUpperCase() === 'GUIDE')
      .sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0) || new Date(b.updatedAt || 0) - new Date(a.updatedAt || 0))
      .slice(0, 4);
  }, [marketingDocuments]);

  const activePolicyDocument = useMemo(
    () => [...marketingDocuments]
      .filter((item) => String(item.type || '').toUpperCase() === 'POLICY')
      .sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0) || new Date(b.updatedAt || 0) - new Date(a.updatedAt || 0))[0] || null,
    [marketingDocuments]
  );

  const activePolicyDetail = useMemo(
    () => parseMarketingDocumentDescription(activePolicyDocument?.description) || {},
    [activePolicyDocument]
  );

  const activeGuideDocument = guideToolDocuments[0] || null;

  const commercePinnedMarketingDocument = useMemo(
    () => [...marketingDocuments]
      .filter(isCommercePinnedMarketingDocument)
      .sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0) || new Date(b.updatedAt || 0) - new Date(a.updatedAt || 0))[0] || null,
    [marketingDocuments]
  );

  const selectedMarketingDocumentDetail = useMemo(
    () => parseMarketingDocumentDescription(selectedMarketingDocument?.description) || {},
    [selectedMarketingDocument]
  );

  const affiliateLink = `${window.location.origin}${AppRoute.auth}/sign-up?referralCode=${affiliateCode}`;

  const handleCopyLink = () => {
    Utils.copyTextToClipboard(affiliateLink, () => {
      ToastHelper.showSuccess('Đã sao chép liên kết giới thiệu!');
    });
  };

  const openReferralQR = (event) => {
    event?.preventDefault?.();
    event?.stopPropagation?.();
    setShowAccountMenu(false);
    setShowQR(true);
  };

  const closeReferralQR = (event) => {
    event?.preventDefault?.();
    event?.stopPropagation?.();
    setShowQR(false);
  };

  const handleCopyMarketingContent = (product) => {
    Utils.copyTextToClipboard([
      product.name,
      product.description || product.note || 'Sản phẩm trụ sạc EV Group',
      `Giá bán: ${product.price}`,
      `Hoa hồng giới thiệu: ${product.commission}`,
      affiliateLink,
    ].filter(Boolean).join('\n'), () => {
      ToastHelper.showSuccess('Đã sao chép nội dung quảng bá!');
    });
  };

  const scrollToToolsContent = (delay = 0) => {
    setTimeout(() => {
      window.document.querySelector('[data-tools-content]')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, delay);
  };

  const showToolList = (category = 'ALL', fileType = 'ALL') => {
    setToolKeyword('');
    setToolCategory(category);
    setToolFileType(fileType);
    setToolFormat('ALL');
    scrollToToolsContent();
  };

  const openCommercePromotion = () => {
    setToolKeyword('');
    setToolCategory('MARKETING');
    setToolFileType('DOCUMENT');
    setToolFormat('ALL');
    setToolSortBy('NEWEST');
    changeTab('tools');
    scrollToToolsContent(120);
  };

  const openCommercePinnedMarketingDocument = () => {
    if (!commercePinnedMarketingDocument) return;
    setToolKeyword(commercePinnedMarketingDocument.title || '');
    setToolCategory('MARKETING');
    setToolFileType('DOCUMENT');
    setToolFormat('ALL');
    setToolSortBy('NEWEST');
    changeTab('tools');
    scrollToToolsContent(120);
  };

  const openMarketingDocument = (item) => {
    if (!item) return;
    setSelectedMarketingDocument(item);
  };

  const openMarketingDocumentFile = (item) => {
    if (!hasMarketingDocumentFile(item)) {
      ToastHelper.showError('Tài liệu này chưa có file đính kèm');
      return;
    }
    window.open(item.fileUrl, '_blank');
  };

  const copyMarketingDocument = (item, successMessage = 'Đã sao chép nội dung tài liệu') => {
    if (!item) return;
    Utils.copyTextToClipboard([
      item.title,
      getMarketingDocumentSummary(item),
      hasMarketingDocumentFile(item) ? item.fileUrl : affiliateLink,
    ].filter(Boolean).join('\n'), () => {
      ToastHelper.showSuccess(successMessage);
    });
  };

  const unreadNotificationCount = notifications.filter((item) => !item.isRead).length;

  const toggleNotifications = () => {
    setShowNotifications((prev) => !prev);
    if (!showNotifications) {
      loadNotifications();
    }
  };

  const markAllNotificationsRead = () => {
    setNotifications((prev) => prev.map((item) => ({ ...item, isRead: 1 })));
    setAllNotifications((prev) => prev.map((item) => ({ ...item, isRead: 1 })));
    bulkUpdateLocalNotifications(null, 'readAll');
    feApi.readAllNotifications().catch(() => {});
  };

  const markNotificationRead = (notification) => {
    if (!notification?.notificationId || notification?.isRead) return;
    setNotifications((prev) => prev.map((item) => (
      item.notificationId === notification.notificationId ? { ...item, isRead: 1 } : item
    )));
    setAllNotifications((prev) => prev.map((item) => (
      item.notificationId === notification.notificationId ? { ...item, isRead: 1 } : item
    )));
    updateLocalNotification(notification.notificationId, 'read');
    feApi.updateNotification(notification.notificationId, { isRead: 1 }).catch(() => {});
  };

  const toggleSelectNotification = (id) => {
    setSelectedNotificationIds((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  const handleToggleSelectAll = (e) => {
    if (e?.target?.checked || selectedNotificationIds.length < filteredAllNotifications.length) {
      setSelectedNotificationIds(filteredAllNotifications.map((item) => item.notificationId));
    } else {
      setSelectedNotificationIds([]);
    }
  };

  const handleBulkRead = () => {
    if (selectedNotificationIds.length === 0) return;
    setNotifications((prev) =>
      prev.map((item) =>
        selectedNotificationIds.includes(item.notificationId) ? { ...item, isRead: 1 } : item
      )
    );
    setAllNotifications((prev) =>
      prev.map((item) =>
        selectedNotificationIds.includes(item.notificationId) ? { ...item, isRead: 1 } : item
      )
    );
    bulkUpdateLocalNotifications(selectedNotificationIds, 'read');
    setSelectedNotificationIds([]);
    ToastHelper.showSuccess('Đã đánh dấu đọc các mục chọn thành công!');
    feApi.bulkNotifications('read', selectedNotificationIds).catch(() => {});
  };

  const handleBulkDelete = () => {
    if (selectedNotificationIds.length === 0) return;
    if (window.confirm(`Bạn có chắc chắn muốn xóa ${selectedNotificationIds.length} thông báo đã chọn? Chức năng này chỉ xóa hiển thị với bạn, admin vẫn có thể tra cứu lại.`)) {
      setNotifications((prev) =>
        prev.filter((item) => !selectedNotificationIds.includes(item.notificationId))
      );
      setAllNotifications((prev) =>
        prev.filter((item) => !selectedNotificationIds.includes(item.notificationId))
      );
      bulkUpdateLocalNotifications(selectedNotificationIds, 'delete');
      setSelectedNotificationIds([]);
      ToastHelper.showSuccess('Đã xóa thành công các thông báo đã chọn!');
      feApi.bulkNotifications('delete', selectedNotificationIds).catch(() => {});
    }
  };

  const handleDeleteNotification = (notificationId) => {
    if (!notificationId) return;
    if (window.confirm('Bạn có chắc chắn muốn xóa thông báo này? Chức năng này chỉ xóa hiển thị với bạn, admin vẫn có thể tra cứu lại.')) {
      setNotifications((prev) => prev.filter((item) => item.notificationId !== notificationId));
      setAllNotifications((prev) => prev.filter((item) => item.notificationId !== notificationId));
      updateLocalNotification(notificationId, 'delete');
      setSelectedNotificationIds((prev) => prev.filter((id) => id !== notificationId));
      ToastHelper.showSuccess('Đã xóa thông báo thành công!');
      feApi.bulkNotifications('delete', [notificationId]).catch(() => {});
    }
  };

  const openAllNotifications = (e) => {
    if (e && e.stopPropagation) {
      e.stopPropagation();
    }
    setShowNotifications(false);
    changeTab('notifications');
  };

  const handleSignOut = () => {
    AuthHelper.signOut();
    Utils.openInCurrentTab(`${AppRoute.auth}/sign-in`);
  };

  const handleChangePassword = () => {
    setShowAccountMenu(false);
    SystemHelper.UI.Account.showDialogChangePassword(() => {});
  };

  const handleSwitchToCms = async () => {
    if (!RoleUtils.hasCmsUi(user, agencyRoleList)) return;
    localStorage.setItem(PreferenceKeys.savedUiMode, RoleUtils.uiModes.cms);
    const cmsProfile = await AccountHelper.callApiGetProfile();
    const cmsUser = cmsProfile || user;
    const cmsAgencyRoleList = RoleUtils.getAgencyRoles(cmsUser, agencyRoleList);
    navigate(RoleUtils.getCmsHomeRoute(cmsUser, cmsAgencyRoleList), { replace: true });
  };

  const cmsRoleLabel = useMemo(() => {
    const role = RoleUtils.getPrimaryAgencyRole(user, agencyRoleList);
    switch (role) {
      case AppConstants.userAgencyType.investor:
        return 'Nhà đầu tư';
      case AppConstants.userAgencyType.landlord:
        return 'Chủ mặt bằng';
      case AppConstants.userAgencyType.group:
        return 'NĐT HTKD';
      case AppConstants.userAgencyType.sale:
        return 'Đại lý';
      default:
        return 'Quản trị';
    }
  }, [agencyRoleList, user]);

  const addToCart = (product, quantity = 1, openCart = false) => {
    if (!product) return;
    setCartItems((currentItems) => {
      const existingItem = currentItems.find((item) => Number(item.product?.id) === Number(product.id));
      if (existingItem) {
        return currentItems.map((item) => Number(item.product?.id) === Number(product.id)
          ? { ...item, quantity: Number(item.quantity || 0) + Number(quantity || 1) }
          : item);
      }
      return [...currentItems, { product, quantity: Number(quantity || 1) }];
    });
    ToastHelper.showSuccess(`Đã thêm ${product.name} vào giỏ hàng`);
    if (openCart) changeTab('cart');
  };

  const getProductQuantity = (productId) => Math.max(1, Number(productQuantities[productId] || 1));

  const updateProductQuantity = (productId, nextQuantity) => {
    setProductQuantities((current) => ({
      ...current,
      [productId]: Math.max(1, Number(nextQuantity || 1)),
    }));
  };

  const addProductToCartFromCard = (product) => {
    const quantity = getProductQuantity(product.id);
    addToCart(product, quantity);
  };

  const handleReorder = (order) => {
    const items = order?.sourceOrder?.inventoryItems || [];
    if (!items.length) {
      ToastHelper.showError('Đơn này chưa có dữ liệu sản phẩm để đặt lại');
      return;
    }

    const reorderItems = items.map((item) => {
      const itemName = String(item?.name || item?.inventoryName || '').trim().toLowerCase();
      const itemId = item?.categoryId || item?.inventoryId || item?.productId;
      const matchedProduct = products.find((product) => (
        (itemId && Number(product.id) === Number(itemId))
        || (itemName && String(product.name || '').trim().toLowerCase() === itemName)
      ));
      return matchedProduct ? {
        product: matchedProduct,
        quantity: Math.max(1, Number(item.quantity || 1)),
        checked: true,
      } : null;
    }).filter(Boolean);

    if (!reorderItems.length) {
      ToastHelper.showError('Không tìm thấy sản phẩm tương ứng để đặt lại');
      return;
    }

    const note = parseOrderNote(order?.sourceOrder?.note);
    setCartItems(reorderItems);
    const reorderPaymentMethod = String(note.paymentMethod || order?.payment?.paymentMethod || 'VNPAY').toUpperCase();
    setOrderForm({
      ...resetOrderForm(['QR', 'BANK_TRANSFER'].includes(reorderPaymentMethod) ? 'BIDV' : reorderPaymentMethod),
      buyerName: note.buyerName || order.buyer || user?.fullname || '',
      buyerPhone: note.buyerPhone || order.phone || user?.phone || '',
      shippingAddress: order?.sourceOrder?.shippingAddress || '',
      note: note.note || '',
    });
    setShowCheckout(true);
  };

  const updateCartQuantity = (productId, quantity) => {
    const nextQuantity = Math.max(1, Number(quantity || 1));
    setCartItems((currentItems) => currentItems.map((item) =>
      Number(item.product?.id) === Number(productId)
        ? { ...item, quantity: nextQuantity }
        : item
    ));
  };

  const removeCartItem = (productId) => {
    setCartItems((currentItems) => currentItems.filter(
      (item) => Number(item.product?.id) !== Number(productId)
    ));
  };

  const toggleCartItemChecked = (productId) => {
    setCartItems((currentItems) => currentItems.map(
      (item) => Number(item.product?.id) === Number(productId)
        ? { ...item, checked: item.checked === false ? true : false }
        : item
    ));
  };

  const toggleAllCartItems = (checked) => {
    setCartItems((currentItems) => currentItems.map(
      (item) => ({ ...item, checked })
    ));
  };

  const resetOrderForm = (paymentMethod = 'VNPAY') => ({
    buyerName: user?.fullname || '',
    buyerPhone: user?.phone || '',
    shippingAddress: '',
    note: '',
    paymentMethod,
    // Hóa đơn điện tử: mặc định xuất theo thông tin người mua; tích "công ty" để xuất theo MST.
    invoiceCompany: false,
    invoiceCompanyName: '',
    invoiceTaxCode: '',
    invoiceAddress: '',
    invoiceEmail: user?.email || '',
  });

  const openOrderForm = () => {
    setOrderForm({
      ...resetOrderForm('VNPAY'),
    });
    setShowCheckout(true);
  };

  const startBuyNow = (product, quantity = 1) => {
    if (!product) return;
    setCartItems((currentItems) => {
      let found = false;
      const newItems = currentItems.map((item) => {
        if (Number(item.product?.id) === Number(product.id)) {
          found = true;
          return { ...item, quantity: Math.max(1, Number(quantity || 1)), checked: true };
        }
        return { ...item, checked: false };
      });
      if (!found) {
        newItems.unshift({ product, quantity: Math.max(1, Number(quantity || 1)), checked: true });
      }
      return newItems;
    });
    setOrderForm({
      ...resetOrderForm('VNPAY'),
    });

    setSelectedProduct(null);
    setShowCheckout(true);
  };

  const submitOrder = () => {
    const checkedItems = cartItems.filter((item) => item.checked !== false);
    if (checkedItems.length === 0) {
      ToastHelper.showError('Vui lòng chọn ít nhất 1 sản phẩm để đặt hàng');
      return;
    }
    if (!String(orderForm.buyerName || '').trim() || !String(orderForm.buyerPhone || '').trim()) {
      ToastHelper.showError('Vui lòng nhập tên và số điện thoại người nhận');
      return;
    }
    if (orderForm.invoiceCompany) {
      if (String(orderForm.invoiceCompanyName || '').trim().length < 1) {
        ToastHelper.showError('Vui lòng nhập tên đơn vị xuất hóa đơn');
        return;
      }
      if (!/^\d{10}(\d{3})?$/.test(String(orderForm.invoiceTaxCode || '').replace(/[\s.-]/g, ''))) {
        ToastHelper.showError('Mã số thuế không hợp lệ (10 hoặc 13 chữ số)');
        return;
      }
    }
    setSubmittingOrder(true);
    feApi.createOrder({
      items: checkedItems.map((item) => ({
        productId: item.product.id,
        quantity: Number(item.quantity),
      })),
      buyerName: orderForm.buyerName,
      buyerPhone: orderForm.buyerPhone,
      shippingAddress: orderForm.shippingAddress,
      note: orderForm.note,
      paymentMethod: orderForm.paymentMethod,
      invoice: {
        company: Boolean(orderForm.invoiceCompany),
        companyName: orderForm.invoiceCompanyName,
        taxCode: orderForm.invoiceTaxCode,
        address: orderForm.invoiceAddress,
        email: orderForm.invoiceEmail,
      },
    })
      .then((response) => {
        if (response?.result === 'success') {
          const orderCode = `DH${String(response.order?.saleId || '').padStart(8, '0')}`;
          const selectedPaymentMethod = String(orderForm.paymentMethod || '').toUpperCase();
          const needsOnlineResult = ['VNPAY', 'MOMO', 'BIDV'].includes(selectedPaymentMethod);
          if (response.qrData && !response.link) {
            setShowCheckout(false);
            setCartItems((current) => current.filter((item) => item.checked === false));
            setProductPaymentQR({
              order: response.order,
              payment: response.payment,
              qrData: response.qrData,
              expiryInSeconds: response.expiryInSeconds,
            });
            ToastHelper.showSuccess(`Đã tạo đơn ${orderCode}. Quét QR để thanh toán.`);
            addLocalNotification({
              title: 'Đơn hàng mới',
              message: `Đã tạo đơn ${orderCode}. Vui lòng thanh toán qua mã QR để hoàn tất.`,
            });
          } else if (response.link) {
            setShowCheckout(false);
            setCartItems((current) => current.filter((item) => item.checked === false));
            ToastHelper.showSuccess(`Đã tạo đơn ${orderCode}. Đang chuyển sang cổng thanh toán...`);
            addLocalNotification({
              title: 'Đơn hàng mới',
              message: `Đã tạo đơn ${orderCode}. Đang chờ thanh toán qua cổng trực tuyến.`,
            });
            setTimeout(() => {
              window.location.href = response.link;
            }, 350);
          } else if (needsOnlineResult) {
            ToastHelper.showError(response?.reason || 'Đã tạo đơn nhưng cổng thanh toán chưa trả link/mã QR. Kiểm tra cấu hình payment backend.');
          } else {
            setShowCheckout(false);
            setCartItems((current) => current.filter((item) => item.checked === false));
            const successMessage = selectedPaymentMethod === 'CASH'
              ? `Đã tạo đơn ${orderCode}. Kế toán sẽ xác nhận thanh toán tiền mặt.`
              : `Đặt hàng thành công - mã đơn ${orderCode}`;
            ToastHelper.showSuccess(successMessage);
            addLocalNotification({
              title: 'Đặt hàng thành công',
              message: successMessage,
            });
          }
          loadOrders();
          loadAffiliateSummary();
          loadNotifications();
          loadAllNotifications();
        } else {
          ToastHelper.showError(response?.reason || 'Không tạo được đơn hàng');
        }
      })
      .catch((error) => {
        ToastHelper.showError(error?.response?.data?.reason || error?.message || 'Không tạo được đơn hàng');
      })
      .finally(() => {
        setSubmittingOrder(false);
      });
  };

  const openOrderPaymentQR = (order) => {
    const saleId = Number(order?.id || order?.sourceOrder?.saleId || order?.saleId);
    if (!saleId) {
      ToastHelper.showError('Không xác định được mã đơn hàng');
      return;
    }
    if (['PAID', 'COMPLETED'].includes(String(order?.status || order?.sourceOrder?.status || '').toUpperCase())) {
      ToastHelper.showSuccess('Đơn hàng này đã thanh toán');
      loadOrders();
      return;
    }

    setLoadingPaymentQR(true);
    feApi.getOrderPaymentQR(saleId)
      .then((response) => {
        if (response?.paid) {
          setProductPaymentQR(null);
          ToastHelper.showSuccess('Đơn hàng đã được thanh toán');
          addLocalNotification({
            title: 'Thanh toán thành công',
            message: `Thanh toán thành công cho đơn hàng. Cảm ơn bạn đã mua sắm!`,
          });
          loadOrders();
          loadAffiliateSummary();
          loadNotifications();
          loadAllNotifications();
          return;
        }
        if (response?.result === 'success' && response.link) {
          ToastHelper.showSuccess('Đang chuyển sang cổng thanh toán...');
          setTimeout(() => {
            window.location.href = response.link;
          }, 300);
        } else if (response?.result === 'success' && response.qrData?.checkoutUrl && !response.qrData?.qrCode) {
          ToastHelper.showSuccess('Đang chuyển sang cổng thanh toán...');
          setTimeout(() => {
            window.location.href = response.qrData.checkoutUrl;
          }, 300);
        } else if (response?.result === 'success' && (response.qrData?.qrCode || response.qrData?.checkoutUrl)) {
          setProductPaymentQR({
            order: response.order || order?.sourceOrder || order,
            payment: response.payment,
            qrData: response.qrData,
            expiryInSeconds: response.expiryInSeconds,
          });
        } else {
          ToastHelper.showError(response?.reason || 'Không lấy được mã QR thanh toán cho đơn hàng này');
        }
      })
      .catch((error) => {
        ToastHelper.showError(error?.response?.data?.reason || 'Không lấy được mã QR thanh toán cho đơn hàng này');
      })
      .finally(() => {
        setLoadingPaymentQR(false);
      });
  };

  useEffect(() => {
    const saleId = Number(productPaymentQR?.order?.saleId || productPaymentQR?.order?.id);
    if (!saleId) return undefined;

    const timer = setInterval(() => {
      feApi.getOrderPaymentQR(saleId)
        .then((response) => {
          if (response?.paid) {
            clearInterval(timer);
            setProductPaymentQR(null);
            ToastHelper.showSuccess('Thanh toán thành công. Đơn hàng đã chuyển sang mục Đã thanh toán.');
            addLocalNotification({
              title: 'Thanh toán thành công',
              message: `Đơn hàng của bạn đã được xác nhận thanh toán thành công qua mã QR.`,
            });
            loadOrders();
            loadAffiliateSummary();
            loadNotifications();
            loadAllNotifications();
          }
        })
        .catch(() => {});
    }, 6000);

    return () => clearInterval(timer);
  }, [productPaymentQR?.order?.saleId, productPaymentQR?.order?.id]);

  const renderProductValue = (value) => {
    if (Array.isArray(value)) {
      return value.length > 0 ? (
        <ul style={styles.detailList}>
          {value.filter(Boolean).map((item) => <li key={item}>{item}</li>)}
        </ul>
      ) : '-';
    }
    return value || '-';
  };

  const openWalletNotice = (message, title = 'Thông báo', type = 'warning') => {
    setWalletNotice({ title, message, type });
  };

  const updateWithdrawBankField = (field, value) => {
    setWithdrawBankForm((prev) => ({ ...prev, [field]: value }));
    setWithdrawBankErrors((prev) => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };

  const submitWithdrawal = () => {
    const requestedAmount = Number(withdrawAmount);
    if (!withdrawAmount || requestedAmount <= 0) {
      openWalletNotice('Vui lòng nhập số tiền muốn rút', 'Thiếu thông tin rút tiền');
      return;
    }
    if (requestedAmount < 50000) {
      openWalletNotice('Số tiền rút tối thiểu là 50.000đ', 'Số tiền chưa hợp lệ');
      return;
    }
    if (requestedAmount > walletWithdrawableValue) {
      openWalletNotice(`Số dư khả dụng hiện tại là ${formatCurrency(walletWithdrawableValue)}`, 'Số dư không đủ');
      return;
    }

    const isManualBankAccount = effectiveWithdrawBankSource === MANUAL_WITHDRAW_BANK_SOURCE;
    const withdrawalBank = isManualBankAccount
      ? {
        bankCode: String(withdrawBankForm.bankCode || withdrawBankForm.bankName || '').trim().toUpperCase(),
        bankName: String(withdrawBankForm.bankName || '').trim(),
        bankAccountNumber: String(withdrawBankForm.bankAccountNumber || '').replace(/\s+/g, ''),
        accountHolder: String(withdrawBankForm.accountHolder || '').trim(),
      }
      : {
        bankCode: String(selectedWithdrawBankAccount?.bankCode || selectedWithdrawBankAccount?.bankName || '').trim().toUpperCase(),
        bankName: String(selectedWithdrawBankAccount?.bankName || '').trim(),
        bankAccountNumber: String(selectedWithdrawBankAccount?.bankAccount || '').trim(),
        accountHolder: String(selectedWithdrawBankAccount?.bankBeneficiary || '').trim(),
      };
    const nextBankErrors = {};
    if (!withdrawalBank.bankName) nextBankErrors.bankName = 'Vui lòng nhập mã ngân hàng';
    if (!withdrawalBank.bankAccountNumber) nextBankErrors.bankAccountNumber = 'Vui lòng nhập số tài khoản';
    if (!withdrawalBank.accountHolder) nextBankErrors.accountHolder = 'Vui lòng nhập tên chủ tài khoản';
    setWithdrawBankErrors(nextBankErrors);
    if (Object.keys(nextBankErrors).length > 0) {
      openWalletNotice('Vui lòng nhập đầy đủ thông tin tài khoản nhận tiền', 'Thiếu thông tin ngân hàng');
      return;
    }

    const selectedBank = findValidBank(withdrawalBank.bankCode);
    if (!selectedBank) {
      setWithdrawBankErrors((prev) => ({
        ...prev,
        bankName: `Mã ngân hàng ${withdrawalBank.bankCode || withdrawalBank.bankName} không hợp lệ`,
      }));
      openWalletNotice(
        `Mã ngân hàng ${withdrawalBank.bankCode || withdrawalBank.bankName} không hợp lệ. Vui lòng nhập đúng mã ngân hàng trong danh sách hệ thống.`,
        'Ngân hàng không hợp lệ',
      );
      return;
    }
    withdrawalBank.bankCode = selectedBank.bankCode;
    withdrawalBank.bankName = selectedBank.bankName || withdrawalBank.bankName;

    setSubmittingWithdraw(true);
    feApi.withdraw({
      amount: requestedAmount,
      note: withdrawNote,
      bankCode: withdrawalBank.bankCode,
      bankName: withdrawalBank.bankName,
      bankAccountNumber: withdrawalBank.bankAccountNumber,
      accountHolder: withdrawalBank.accountHolder,
    })
      .then((response) => {
        if (response?.result === 'success') {
          setWithdrawAmount('');
          setWithdrawNote('');
          setWithdrawBankErrors({});
          openWalletNotice('Yêu cầu rút tiền đã được gửi. Hệ thống sẽ xử lý trong 1-3 ngày làm việc.', 'Gửi yêu cầu thành công', 'success');
          loadAffiliateSummary();
        } else {
          openWalletNotice(response?.reason || 'Tạo yêu cầu rút tiền thất bại', 'Không thể rút tiền');
        }
      })
      .catch((error) => {
        openWalletNotice(error?.response?.data?.reason || 'Tạo yêu cầu rút tiền thất bại', 'Không thể rút tiền');
      })
      .finally(() => {
        setSubmittingWithdraw(false);
      });
  };

  const updateAccountField = (field, value) => {
    setAccountForm((prev) => ({ ...prev, [field]: value }));
    setAccountErrors((prev) => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };

  const updateAccountBank = (bankCode) => {
    const selectedBank = findValidBank(bankCode);
    setAccountForm((prev) => ({
      ...prev,
      bankCode: selectedBank?.bankCode || '',
      bankName: selectedBank?.bankName || '',
    }));
    setAccountErrors((prev) => {
      if (!prev.bankCode && !prev.bankName) return prev;
      const next = { ...prev };
      delete next.bankCode;
      delete next.bankName;
      return next;
    });
  };

  const uploadKycDocument = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    setUploadingKyc(true);
    try {
      const mediaList = await miscHelper.callApiUploadFile(file);
      const media = mediaList?.[0];
      const filePath = media?.relativePath || media?.filePath;
      if (!filePath) {
        ToastHelper.showError('Upload giấy tờ thất bại');
        return;
      }
      setAccountForm((prev) => ({
        ...prev,
        idCardDocuments: [...(prev.idCardDocuments || []), filePath],
      }));
      ToastHelper.showSuccess('Đã tải giấy tờ eKYC');
    } catch (error) {
      ToastHelper.showError('Upload giấy tờ thất bại');
    } finally {
      setUploadingKyc(false);
    }
  };

  const removeKycDocument = (index) => {
    setAccountForm((prev) => ({
      ...prev,
      idCardDocuments: (prev.idCardDocuments || []).filter((_, itemIndex) => itemIndex !== index),
    }));
  };

  const saveAccountProfile = () => {
    const nextErrors = {};
    if (!String(accountForm.fullname || '').trim()) nextErrors.fullname = 'Vui lòng nhập họ và tên';
    if (!String(accountForm.phone || '').trim()) nextErrors.phone = 'Vui lòng nhập số điện thoại';
    if (!String(accountForm.email || '').trim()) nextErrors.email = 'Vui lòng nhập email';
    if (!String(accountForm.idCardType || '').trim()) nextErrors.idCardType = 'Vui lòng chọn loại giấy tờ';
    if (!String(accountForm.bankCode || '').trim()) nextErrors.bankCode = 'Vui lòng chọn ngân hàng';
    if (!String(accountForm.bankName || '').trim()) nextErrors.bankName = 'Vui lòng chọn ngân hàng';
    if (!String(accountForm.bankAccount || '').trim()) nextErrors.bankAccount = 'Vui lòng nhập số tài khoản';
    if (!String(accountForm.bankBeneficiary || '').trim()) nextErrors.bankBeneficiary = 'Vui lòng nhập tên chủ tài khoản';

    setAccountErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) {
      ToastHelper.showError('Vui lòng kiểm tra các trường bắt buộc còn thiếu');
      return;
    }
    const selectedBank = findValidBank(accountForm.bankCode);
    if (!selectedBank) {
      setAccountErrors((prev) => ({
        ...prev,
        bankCode: `Mã ngân hàng ${accountForm.bankCode} không hợp lệ`,
      }));
      ToastHelper.showError('Ngân hàng đã chọn không hợp lệ, vui lòng chọn lại');
      return;
    }
    const payload = {
      ...accountForm,
      bankCode: selectedBank.bankCode,
      bankName: selectedBank.bankName || accountForm.bankName,
    };
    setSavingProfile(true);
    feApi.updateProfile(payload)
      .then((response) => {
        if (response?.result === 'success') {
          ToastHelper.showSuccess('Đã cập nhật thông tin tài khoản');
          loadProfile();
        } else {
          ToastHelper.showError(response?.reason || 'Cập nhật thông tin thất bại');
        }
      })
      .catch((error) => {
        ToastHelper.showError(error?.response?.data?.reason || 'Cập nhật thông tin thất bại');
      })
      .finally(() => {
        setSavingProfile(false);
      });
  };

  const handleVerifyReferral = () => {
    const code = referralInput.trim();
    if (!code) {
      ToastHelper.showError('Vui lòng nhập mã người giới thiệu');
      return;
    }
    setVerifyingReferral(true);
    feApi.verifyReferralCode(code)
      .then((response) => {
        if (response?.result === 'success') {
          setVerifiedReferrer(response.referrer);
          ToastHelper.showSuccess('Mã người giới thiệu hợp lệ');
        } else {
          setVerifiedReferrer(null);
          ToastHelper.showError(response?.reason || 'Mã người giới thiệu không hợp lệ');
        }
      })
      .catch((error) => {
        setVerifiedReferrer(null);
        ToastHelper.showError(error?.response?.data?.reason || 'Mã người giới thiệu không tồn tại hoặc không hợp lệ');
      })
      .finally(() => {
        setVerifyingReferral(false);
      });
  };

  const handleApplyReferral = () => {
    const code = referralInput.trim();
    if (!code) {
      ToastHelper.showError('Vui lòng nhập mã người giới thiệu');
      return;
    }
    setApplyingReferral(true);
    feApi.updateReferralCode(code)
      .then((response) => {
        if (response?.result === 'success') {
          ToastHelper.showSuccess('Đã áp dụng mã giới thiệu thành công');
          loadProfile();
        } else {
          ToastHelper.showError(response?.reason || 'Áp dụng mã giới thiệu thất bại');
        }
      })
      .catch((error) => {
        ToastHelper.showError(error?.response?.data?.reason || 'Áp dụng mã giới thiệu thất bại');
      })
      .finally(() => {
        setApplyingReferral(false);
      });
  };

  return (
    <div
      className="evg-customer-page"
      style={styles.page}
    >
      <style>
        {`
          .evg-customer-page * { box-sizing: border-box; }
          @keyframes evgProductImageFade {
            from { opacity: .5; }
            to { opacity: 1; }
          }
          .evg-customer-page button {
            font-family: inherit;
            cursor: pointer;
            border: 0;
            outline: none;
            transition: transform .16s ease, box-shadow .16s ease, background .16s ease, border-color .16s ease, color .16s ease;
          }
          .evg-customer-page button:focus, .evg-customer-page button:active {
            outline: none !important;
          }
          .evg-customer-page button:disabled {
            cursor: not-allowed;
            opacity: .55;
          }
          .evg-customer-page input,
          .evg-customer-page select,
          .evg-customer-page textarea {
            font-family: inherit;
            font-weight: 500;
            border: 1px solid #e5e7eb;
            border-radius: 10px;
            outline: none;
          }
          .evg-customer-page small { display: block; color: #64748b; font-weight: 600; line-height: 1.35; }
          .evg-customer-page strong { font-weight: 700; }
          .evg-customer-page img { max-width: 100%; }
          .evg-customer-page ul { margin: 0; padding-left: 18px; }
          .evg-customer-page li { line-height: 1.45; }
          .evg-customer-page [data-qty-control] button {
            height: 100%;
            display: grid;
            place-items: center;
            background: #f8fafc;
            color: #334155;
            font-size: 15px;
            font-weight: 950;
          }
          .evg-customer-page [data-qty-control] button:first-child {
            border-right: 1px solid #e5e7eb;
          }
          .evg-customer-page [data-qty-control] button:last-child {
            border-left: 1px solid #e5e7eb;
          }
          .evg-customer-page [data-qty-control] span {
            display: grid;
            place-items: center;
            color: #0f172a;
            font-weight: 900;
          }
          .evg-customer-page [data-qty-control] button:hover {
            background: #ecfdf5;
            color: #16a34a;
          }
          .evg-customer-page [data-voucher-row] input {
            height: 42px;
            padding: 0 14px;
            background: #fff;
          }
          .evg-customer-page [data-network-filter] input,
          .evg-customer-page [data-network-filter] select {
            width: 100%;
            height: 44px;
            border: 1px solid #e2e8f0;
            border-radius: 10px;
            background: #fff;
            color: #0f172a;
            font-size: 13px;
            font-weight: 500;
          }
          .evg-customer-page [data-network-filter] select {
            padding: 0 34px 0 12px;
            cursor: pointer;
          }
          .evg-customer-page [data-network-filter] input:focus,
          .evg-customer-page [data-network-filter] select:focus {
            border-color: #22c55e;
            box-shadow: 0 0 0 3px rgba(34, 197, 94, .12);
          }
          .evg-customer-page [data-network-search] input { padding: 0 38px; }
          .evg-customer-page [data-network-search] > i {
            position: absolute;
            left: 14px;
            z-index: 1;
          }
          .evg-customer-page [data-network-search] > button {
            position: absolute;
            right: 8px;
            width: 28px;
            height: 28px;
            display: grid;
            place-items: center;
            border-radius: 8px;
            background: #f1f5f9;
            color: #64748b;
            font-size: 18px;
          }
          .evg-customer-page .evg-custom-select-dropdown {
            overscroll-behavior: contain;
          }
          @media (max-width: 1600px) {
            .evg-customer-page [data-shop-page] {
              grid-template-columns: minmax(0, 1fr) !important;
            }
            .evg-customer-page [data-cart-sidebar] {
              position: static !important;
              max-width: none !important;
            }
            .evg-customer-page [data-filter-panel] {
              grid-template-columns: repeat(3, minmax(0, 1fr)) !important;
            }
            .evg-customer-page [data-filter-panel] > :first-child {
              grid-column: 1 / -1;
            }
            .evg-customer-page [data-wallet-filter] {
              grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
            }
            .evg-customer-page [data-wallet-filter] > :nth-child(3) {
              grid-column: 1 / -1;
            }
          }
          @media (max-width: 900px) {
            .evg-customer-page [data-network-hero] {
              align-items: flex-start !important;
              flex-direction: column !important;
              gap: 16px !important;
            }
            .evg-customer-page [data-network-hero] > div:last-child {
              flex-wrap: wrap !important;
              width: 100% !important;
            }
            .evg-customer-page [data-network-hero] > div:last-child > button {
              flex: 1 1 auto !important;
            }
            .evg-customer-page [data-network-insights] {
              grid-template-columns: minmax(0, 1fr) !important;
            }
            .evg-customer-page [data-stats-grid],
            .evg-customer-page [data-dashboard-grid] {
              grid-template-columns: minmax(0, 1fr) !important;
            }
            .evg-customer-page {
              overflow-x: clip !important;
            }
            .evg-customer-page main,
            .evg-customer-page [data-customer-shell],
            .evg-customer-page [data-shop-page],
            .evg-customer-page [data-shop-page] > div,
            .evg-customer-page [data-shop-page] section,
            .evg-customer-page [data-shop-page] article,
            .evg-customer-page [data-network-page],
            .evg-customer-page [data-network-page] > div,
            .evg-customer-page [data-network-page] section,
            .evg-customer-page [data-network-page] article,
            .evg-customer-page [data-wallet-page],
            .evg-customer-page [data-wallet-page] > div,
            .evg-customer-page [data-wallet-page] section,
            .evg-customer-page [data-wallet-page] article,
            .evg-customer-page [data-tools-page],
            .evg-customer-page [data-tools-page] > div,
            .evg-customer-page [data-tools-page] section,
            .evg-customer-page [data-tools-page] article,
            .evg-customer-page [data-account-page],
            .evg-customer-page [data-account-page] > div,
            .evg-customer-page [data-account-page] section,
            .evg-customer-page [data-account-page] article {
              min-width: 0 !important;
            }
            .evg-customer-page [data-dashboard-grid] {
              grid-template-columns: minmax(0, 1fr) !important;
            }
            .evg-customer-page [data-dashboard-grid] > article {
              grid-column: 1 / -1 !important;
            }
            .evg-customer-page header {
              min-height: 72px !important;
              padding: 8px 16px !important;
              justify-content: space-between !important;
              gap: 8px !important;
            }
            .evg-customer-page header > div:first-child {
              min-width: 0 !important;
              flex: 1 1 auto !important;
            }
            .evg-customer-page .mobile-menu-toggle {
              width: 36px !important;
              height: 36px !important;
              min-width: 36px !important;
              font-size: 18px !important;
            }
            .evg-customer-page .evg-header-logo {
              width: min(34vw, 170px) !important;
              height: auto !important;
              max-height: 46px !important;
              object-fit: contain !important;
            }
            .evg-customer-page [data-customer-shell] {
              grid-template-columns: minmax(0, 1fr) !important;
            }

            .evg-customer-page [data-sidebar-toggle] {
              display: none !important;
            }
            .evg-customer-page [data-account-menu-btn] {
              width: 38px !important;
              min-width: 38px !important;
              height: 38px !important;
              padding: 0 !important;
              background: transparent !important;
              border: 0 !important;
              border-radius: 50% !important;
            }
            .evg-customer-page [data-quick-tool-grid] {
              grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
            }
            .evg-customer-page [data-account-info],
            .evg-customer-page [data-header-brand-text],
            .evg-customer-page [data-header-cart-text],
            .evg-customer-page [data-header-wallet-text] {
              display: none !important;
            }
            .evg-customer-page [data-header-actions] {
              gap: 6px !important;
              flex: 0 0 auto !important;
              display: flex !important;
              align-items: center !important;
            }
            .evg-customer-page [data-header-actions] > * {
              flex-shrink: 0 !important;
            }
            .evg-customer-page [data-header-actions] > button,
            .evg-customer-page [data-header-actions] > div > button {
              min-width: 38px !important;
              width: 38px !important;
              height: 38px !important;
              border-radius: 50% !important;
              padding: 0 !important;
              display: inline-flex !important;
              align-items: center !important;
              justify-content: center !important;
            }
            .evg-customer-page [data-account-avatar] {
              min-width: 38px !important;
              width: 38px !important;
              height: 38px !important;
              border-radius: 50% !important;
              font-size: 14px !important;
              display: flex !important;
              align-items: center !important;
              justify-content: center !important;
              border: 1.5px solid #bbf7d0 !important;
            }
            .evg-customer-page [data-header-actions] span[style*="background: rgb(239, 68, 68)"],
            .evg-customer-page [data-header-actions] span[style*="background: #ef4444"] {
              top: -3px !important;
              right: -3px !important;
              min-width: 18px !important;
              height: 18px !important;
              border-radius: 9px !important;
              font-size: 10px !important;
              font-weight: 700 !important;
              padding: 0 4px !important;
              border: 2px solid #ffffff !important;
              display: flex !important;
              align-items: center !important;
              justify-content: center !important;
              box-shadow: 0 1px 3px rgba(0, 0, 0, 0.15) !important;
              line-height: 1 !important;
            }
            .evg-customer-page [data-header-actions] .header-wallet-dropdown .header-wallet-option {
              width: 100% !important;
              min-width: 0 !important;
              height: auto !important;
              padding: 11px 9px !important;
            }
            .evg-customer-page [data-header-actions] .header-wallet-dropdown .header-wallet-topup {
              width: auto !important;
              min-width: 0 !important;
              height: auto !important;
              padding: 6px 8px !important;
            }
            .evg-customer-page .cart-hover-popup,
            .evg-customer-page .header-wallet-dropdown,
            .evg-customer-page [data-notification-dropdown],
            .evg-customer-page [data-account-dropdown] {
              position: fixed !important;
              top: 66px !important;
              left: 16px !important;
              right: 16px !important;
              width: auto !important;
              max-width: none !important;
              margin: 0 !important;
            }
            .evg-customer-page .customer-notification-dropdown {
              max-height: min(560px, calc(100dvh - 82px)) !important;
              display: flex !important;
              flex-direction: column !important;
              overflow: hidden !important;
            }
            .evg-customer-page .customer-notification-head {
              flex: 0 0 auto !important;
              padding: 16px !important;
              gap: 10px !important;
            }
            .evg-customer-page .customer-notification-read-all {
              width: auto !important;
              min-width: auto !important;
              height: auto !important;
              padding: 8px 11px !important;
              border-radius: 10px !important;
              flex-shrink: 0 !important;
            }
            .evg-customer-page .customer-notification-list {
              flex: 1 1 auto !important;
              min-height: 0 !important;
              max-height: none !important;
              overflow-y: auto !important;
              overscroll-behavior: contain;
              padding: 8px !important;
            }
            .evg-customer-page .customer-notification-item {
              width: 100% !important;
              min-width: 0 !important;
              height: auto !important;
              min-height: 0 !important;
              padding: 12px !important;
              border-radius: 12px !important;
              display: grid !important;
              justify-content: stretch !important;
              text-align: left !important;
              white-space: normal !important;
            }
            .evg-customer-page .customer-notification-item > div {
              min-width: 0 !important;
              max-width: 100% !important;
              overflow-wrap: anywhere !important;
              word-break: break-word !important;
            }
            .evg-customer-page .customer-notification-footer {
              flex: 0 0 auto !important;
              padding: 10px 12px max(10px, env(safe-area-inset-bottom)) !important;
            }
            .evg-customer-page .customer-notification-view-all {
              width: 100% !important;
              min-width: 0 !important;
              height: 44px !important;
              padding: 0 14px !important;
              border-radius: 11px !important;
              display: flex !important;
              align-items: center !important;
              justify-content: center !important;
              white-space: normal !important;
            }
          }
          .evg-customer-page [data-account-dropdown] button {
            width: 100%;
            min-height: 42px;
            display: flex;
            align-items: center;
            gap: 10px;
            padding: 10px 12px;
            border-radius: 12px;
            background: #fff;
            color: #334155;
            font-weight: 500;
            text-align: left;
          }
          .evg-customer-page [data-account-dropdown] button:hover { background: #f8fafc; }
          .evg-customer-page [data-account-switch-card]:hover { background: #dcfce7 !important; }
          .evg-customer-page [data-tool-actions] button {
            min-height: 38px;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            gap: 7px;
            padding: 8px 10px;
            border: 1px solid #e5e7eb;
            border-radius: 9px;
            background: #fff;
            color: #334155;
            font-size: 12px;
            font-weight: 500;
          }
          .evg-customer-page [data-tool-actions] button:hover {
            border-color: #86efac;
            background: #f0fdf4;
            color: #15803d;
          }
          .evg-customer-page [data-wallet-rules] i { width: 16px; text-align: center; }
          .evg-customer-page [data-wallet-filter] input,
          .evg-customer-page [data-wallet-filter] select,
          .evg-customer-page [data-filter-panel] input,
          .evg-customer-page [data-filter-panel] select {
            width: 100%;
            height: 42px;
            padding: 0 12px;
            border-color: #e2e8f0;
            background: #fff;
            color: #334155;
            font-size: 12px;
            font-weight: 500;
          }
          .evg-customer-page [data-wallet-filter] input:focus,
          .evg-customer-page [data-wallet-filter] select:focus,
          .evg-customer-page [data-filter-panel] input:focus,
          .evg-customer-page [data-filter-panel] select:focus {
            border-color: #22c55e;
            box-shadow: 0 0 0 3px rgba(34, 197, 94, .1);
          }
          .evg-customer-page [data-wallet-search] input { padding: 0 36px; }
          .evg-customer-page [data-wallet-search] > i { position: absolute; left: 13px; z-index: 1; color: #94a3b8; }
          .evg-customer-page [data-wallet-search] > button { position: absolute; right: 7px; width: 28px; height: 28px; border-radius: 8px; background: #eef2f7; color: #64748b; font-size: 17px; }
          /* ====== RESPONSIVE: data-table-wrapper always scrolls ====== */
          .evg-customer-page [data-table-wrapper] {
            width: 100%;
            overflow-x: auto;
            -webkit-overflow-scrolling: touch;
          }
          .evg-customer-page [data-table-wrapper] > div {
            min-width: 1050px !important;
          }

          @media (max-width: 1100px) {
            .evg-customer-page [data-wallet-top],
            .evg-customer-page [data-wallet-content],
            .evg-customer-page [data-tools-content] {
              grid-template-columns: minmax(0, 1fr) !important;
            }
            .evg-customer-page [data-wallet-summary-grid] {
              grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
            }
            .evg-customer-page [data-wallet-filter] {
              grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
            }
            .evg-customer-page [data-stats-grid] {
              grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
            }
            /* Commerce 1100 */
            .evg-customer-page [data-commerce-kpi-grid] {
              grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
            }
            .evg-customer-page [data-commerce-grid] {
              grid-template-columns: repeat(3, minmax(0, 1fr)) !important;
            }
            .evg-customer-page [data-filter-panel] {
              grid-template-columns: repeat(3, minmax(0, 1fr)) !important;
            }
            .evg-customer-page [data-commerce-promo] {
              grid-template-columns: auto 1fr auto !important;
              gap: 16px !important;
            }
            /* Tools 1100 */
            .evg-customer-page [data-tools-policy-grid] {
              grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
            }
            .evg-customer-page [data-tools-doc-grid] {
              grid-template-columns: minmax(0, 1fr) !important;
            }
            /* Account 1100 */
            .evg-customer-page [data-account-profile-grid] {
              grid-template-columns: minmax(0, 1fr) !important;
            }
          }

          @media (max-width: 900px) {
            /* Network */
            .evg-customer-page [data-table-wrapper] {
              padding-bottom: 8px;
              scrollbar-color: #cbd5e1 transparent;
            }
            .evg-customer-page [data-table-wrapper] > div {
              min-width: 900px !important;
            }
            .evg-customer-page [data-network-page] [data-table-wrapper] > div {
              min-width: 980px !important;
            }
            .evg-customer-page [data-network-grid] {
              grid-template-columns: minmax(0, 1fr) !important;
            }
            .evg-customer-page [data-stats-grid] {
              grid-template-columns: minmax(0, 1fr) !important;
            }
            /* Wallet */
            .evg-customer-page [data-wallet-tabs],
            .evg-customer-page [data-wallet-filter],
            .evg-customer-page [data-wallet-summary-grid] {
              grid-template-columns: minmax(0, 1fr) !important;
            }
            .evg-customer-page [data-policy-grid] {
              grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
            }
            /* Tools */
            .evg-customer-page [data-tools-policy-grid],
            .evg-customer-page [data-tools-doc-grid] {
              grid-template-columns: minmax(0, 1fr) !important;
            }
            .evg-customer-page [data-tool-actions] {
              flex-wrap: wrap !important;
            }
            .evg-customer-page [data-tool-actions] > button {
              flex: 1 1 45% !important;
              justify-content: center !important;
            }
            /* Account */
            .evg-customer-page [data-account-form-grid] {
              grid-template-columns: minmax(0, 1fr) !important;
            }
            .evg-customer-page [data-commerce-head] {
              flex-direction: column !important;
              align-items: flex-start !important;
              gap: 16px !important;
            }
            .evg-customer-page [data-account-actions] {
              flex-wrap: wrap !important;
            }
            .evg-customer-page [data-account-actions] > button {
              flex: 1 1 100% !important;
              justify-content: center !important;
              width: 100% !important;
            }

            /* ── Commerce: Hero ── */
            .evg-customer-page [data-commerce-hero] {
              flex-direction: column !important;
              align-items: flex-start !important;
              gap: 14px !important;
            }
            .evg-customer-page [data-commerce-hero] > div:last-child {
              flex-direction: row !important;
              flex-wrap: wrap !important;
              width: 100% !important;
              gap: 8px !important;
            }
            .evg-customer-page [data-commerce-hero] > div:last-child > button {
              flex: 1 1 auto !important;
              width: auto !important;
              justify-content: center !important;
            }

            /* ── Commerce: Promo banner ── */
            .evg-customer-page [data-commerce-promo] {
              grid-template-columns: minmax(0, 1fr) !important;
              gap: 12px !important;
              padding: 16px !important;
              text-align: center;
            }
            .evg-customer-page [data-commerce-promo] > div:first-child {
              display: none !important;
            }
            .evg-customer-page [data-commerce-promo] > button {
              width: 100% !important;
            }

            /* ── Commerce: KPI ── */
            .evg-customer-page [data-commerce-kpi-grid] {
              grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
              gap: 10px !important;
            }

            /* ── Commerce: Order tabs ── */
            .evg-customer-page [data-commerce-order-tabs] {
              flex-wrap: wrap !important;
              gap: 6px !important;
              border-bottom: none !important;
            }
            .evg-customer-page [data-commerce-order-tabs] > button {
              flex: 1 1 auto !important;
              min-width: 0 !important;
              text-align: center !important;
              font-size: 12px !important;
              padding: 7px 8px !important;
              border: 1px solid #e5e7eb !important;
              border-radius: 8px !important;
            }

            /* ── Commerce: Filter panel → 2 col then 1 col ── */
            .evg-customer-page [data-filter-panel] {
              grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
              gap: 8px !important;
            }
            .evg-customer-page [data-filter-panel] > :first-child {
              grid-column: 1 / -1 !important;
            }

            /* ── Commerce: Category chips ── */
            .evg-customer-page [data-commerce-category-tabs] {
              flex-wrap: wrap !important;
              gap: 8px !important;
            }
            .evg-customer-page [data-commerce-category-tabs] > button {
              flex: 1 1 calc(50% - 8px) !important;
              min-width: 0 !important;
              justify-content: center !important;
              padding: 10px 12px !important;
            }

            /* ── Commerce: Product grid 2 col ── */
            .evg-customer-page [data-commerce-grid] {
              grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
              gap: 10px !important;
            }

            /* ── Commerce: Product actions ── */
            .evg-customer-page [data-product-actions] {
              grid-template-columns: minmax(0, 1fr) !important;
              gap: 6px !important;
            }
            .evg-customer-page [data-product-actions] > button {
              width: 100% !important;
              min-height: 38px !important;
              font-size: 12px !important;
              padding: 0 8px !important;
            }
          }

          @media (max-width: 520px) {
            .evg-customer-page header {
              padding-inline: 16px !important;
            }
            .evg-customer-page .mobile-menu-toggle {
              width: 34px !important;
              height: 34px !important;
              min-width: 34px !important;
            }
            .evg-customer-page .evg-header-logo {
              width: min(31vw, 136px) !important;
              max-height: 40px !important;
            }
            .evg-customer-page [data-header-actions] {
              gap: 5px !important;
            }
            .evg-customer-page [data-header-actions] > button,
            .evg-customer-page [data-header-actions] > div > button {
              width: 38px !important;
              height: 38px !important;
              min-width: 38px !important;
            }
            /* Very small phones */
            .evg-customer-page [data-commerce-grid] {
              grid-template-columns: minmax(0, 1fr) !important;
            }
            .evg-customer-page [data-commerce-kpi-grid] {
              grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
            }
            .evg-customer-page [data-filter-panel] {
              grid-template-columns: minmax(0, 1fr) !important;
            }
            .evg-customer-page [data-commerce-category-tabs] > button {
              flex: 1 1 100% !important;
            }
            .evg-customer-page [data-policy-grid] {
              grid-template-columns: minmax(0, 1fr) !important;
            }
          }

          .header-wallet-wrap {
            position: relative;
          }
          .header-wallet-button {
            height: 46px;
            border: 1px solid #bbf7d0;
            border-radius: 999px;
            background: #f0fdf4;
            color: #15803d;
            padding: 0 16px;
            display: flex;
            align-items: center;
            gap: 9px;
            font-weight: 600;
            white-space: nowrap;
          }
          .header-wallet-button i {
            font-size: 17px;
          }
          .header-wallet-dropdown {
            position: absolute;
            top: calc(100% + 10px);
            right: 0;
            width: 350px;
            max-width: calc(100vw - 32px);
            padding: 12px;
            border: 1px solid #e2e8f0;
            border-radius: 16px;
            background: #fff;
            box-shadow: 0 22px 55px rgba(15, 23, 42, 0.18);
            z-index: 120;
          }
          .header-wallet-dropdown::before {
            content: '';
            position: absolute;
            right: 42px;
            bottom: 100%;
            border: 8px solid transparent;
            border-bottom-color: #fff;
          }
          .header-wallet-dropdown-title {
            padding: 4px 8px 10px;
            color: #0f172a;
            font-size: 15px;
            font-weight: 700;
          }
          .header-wallet-option {
            width: 100%;
            display: flex;
            align-items: center;
            gap: 11px;
            padding: 11px 9px;
            border: 0;
            border-radius: 12px;
            background: #fff;
            text-align: left;
            color: #0f172a;
          }
          .header-wallet-option[role="menuitem"] {
            cursor: pointer;
          }
          .header-wallet-option:hover {
            background: #f0fdf4;
          }
          .header-wallet-option > i {
            width: 36px;
            height: 36px;
            display: grid;
            place-items: center;
            flex: 0 0 auto;
            border-radius: 10px;
            background: #ecfdf5;
            color: #16a34a;
            font-size: 16px;
          }
          .header-wallet-option-copy {
            min-width: 0;
            display: grid;
            gap: 2px;
            flex: 1;
          }
          .header-wallet-option-copy strong {
            font-size: 14px;
          }
          .header-wallet-option-copy small {
            color: #64748b;
            font-size: 11px;
            line-height: 1.35;
          }
          .header-wallet-option-balance {
            color: #15803d;
            font-size: 13px;
            font-weight: 700;
            white-space: nowrap;
          }
          .header-wallet-topup {
            flex: 0 0 auto;
            border: 1px solid #bbf7d0;
            border-radius: 8px;
            background: #fff;
            color: #15803d;
            padding: 6px 8px;
            font-size: 11px;
            font-weight: 700;
            white-space: nowrap;
          }
          .header-wallet-topup:hover {
            background: #dcfce7;
          }

          .header-cart-wrap {
            position: relative;
          }
          .cart-hover-popup {
            display: none;
            position: absolute;
            top: calc(100% + 8px);
            right: 0;
            width: 380px;
            background: #fff;
            border-radius: 10px;
            box-shadow: 0 8px 30px rgba(15, 23, 42, 0.15);
            border: 1px solid #f1f5f9;
            z-index: 99;
            padding: 16px;
            cursor: default;
          }
          .cart-hover-popup::before {
            content: '';
            position: absolute;
            bottom: 100%;
            right: 24px;
            border: 8px solid transparent;
            border-bottom-color: #fff;
          }
          .cart-hover-popup::after {
            content: '';
            position: absolute;
            bottom: 100%;
            left: 0;
            right: 0;
            height: 12px;
            background: transparent;
          }
          .header-cart-wrap:hover .cart-hover-popup {
            display: block;
          }
          .cart-content::-webkit-scrollbar,
          .cart-hover-list::-webkit-scrollbar {
            width: 6px;
          }
          .cart-content::-webkit-scrollbar-track,
          .cart-hover-list::-webkit-scrollbar-track {
            background: #f8fafc;
          }
          .cart-content::-webkit-scrollbar-thumb,
          .cart-hover-list::-webkit-scrollbar-thumb {
            background: #cbd5e1;
            border-radius: 3px;
          }
          .cart-hover-list::-webkit-scrollbar-track {
            background: transparent;
          }
          .cart-hover-list::-webkit-scrollbar-thumb {
            background: #cbd5e1;
          }
          .cart-hover-list:hover::-webkit-scrollbar-thumb {
            background: #16a34a;
          }
        `}
      </style>
      <header className="customer-page-header" style={styles.header}>
        <div style={{ ...styles.brand, display: "flex", alignItems: "center" }}>
          <button
            type="button"
            className="mobile-menu-toggle"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            title="Mở menu"
          >
            <i className="fa-solid fa-bars" />
          </button>
          <img 
            className="evg-header-logo"
            src={evgLogoLeft} 
            alt="EV Group" 
            style={{ height: 42, objectFit: 'contain', cursor: 'pointer' }} 
            onClick={() => changeTab('overview')}
          />
        </div>
        
        <div className="header-spacer" style={styles.headerSpacer} />
        
        <div data-header-brand-text style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <img src={evgLogoRight} alt="EV Group Icon" style={{ height: 26, objectFit: 'contain' }} />
          <div style={{ ...styles.brandTitle, fontSize: 16, letterSpacing: 0.5, fontWeight: 700 }}>
            EV GROUP AFFILIATE PORTAL
          </div>
        </div>
        
        <div className="header-spacer" style={styles.headerSpacer} />
        <div data-header-actions style={styles.headerActions}>
          <div className="header-wallet-wrap" ref={walletMenuRef}>
            <button
              type="button"
              className="header-wallet-button"
              title="Ví tiền"
              aria-haspopup="menu"
              aria-expanded={showWalletMenu}
              onClick={() => {
                setShowWalletMenu((prev) => !prev);
                setShowNotifications(false);
                setShowAccountMenu(false);
              }}
            >
              <i className="fa-solid fa-wallet" />
              <span data-header-wallet-text>Ví tiền</span>
            </button>
            {showWalletMenu && (
              <div className="header-wallet-dropdown" role="menu" aria-label="Chọn ví">
                <div className="header-wallet-dropdown-title">Ví của bạn</div>
                <div
                  className="header-wallet-option"
                  role="menuitem"
                  tabIndex={0}
                  onClick={() => { setShowWalletMenu(false); changeTab('wallet'); }}
                  onKeyDown={(event) => { if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); setShowWalletMenu(false); changeTab('wallet'); } }}
                >
                  <i className="fa-solid fa-wallet" />
                  <span className="header-wallet-option-copy">
                    <strong>Ví EVG</strong>
                    <small>Dùng cho hoa hồng và các giao dịch EVG</small>
                  </span>
                  <span className="header-wallet-option-balance">{evgWalletBalance}</span>
                  <button type="button" className="header-wallet-topup" onClick={(event) => { event.stopPropagation(); openWalletTopup('GENERAL'); }} onKeyDown={(event) => event.stopPropagation()}>Nạp tiền</button>
                </div>
                <div
                  className="header-wallet-option"
                  role="menuitem"
                  tabIndex={0}
                  onClick={() => { setShowWalletMenu(false); changeTab('ride'); }}
                  onKeyDown={(event) => { if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); setShowWalletMenu(false); changeTab('ride'); } }}
                >
                  <i className="fa-solid fa-car-side" />
                  <span className="header-wallet-option-copy">
                    <strong>Ví Xe ghép</strong>
                    <small>Chỉ dùng để thanh toán chuyến xe ghép</small>
                  </span>
                  <span className="header-wallet-option-balance">{rideWalletBalance}</span>
                  <button type="button" className="header-wallet-topup" onClick={(event) => { event.stopPropagation(); openWalletTopup('RIDE'); }} onKeyDown={(event) => event.stopPropagation()}>Nạp tiền</button>
                </div>
              </div>
            )}
          </div>
          <div className="header-cart-wrap">
            <button
              type="button"
              style={styles.cartHeaderButton}
              title="Giỏ hàng"
              onClick={() => changeTab('cart')}
            >
              <i className="fa-solid fa-cart-shopping" />
              <span data-header-cart-text>Giỏ hàng</span>
              {cartItems.length > 0 && <span style={styles.cartBadge}>{cartItems.length}</span>}
            </button>
            {cartItems.length > 0 && (
              <div className="cart-hover-popup">
                <div style={styles.cartHoverTitle}>Sản phẩm mới thêm</div>
                <div className="cart-hover-list" style={styles.cartHoverList}>
                  {cartItems.slice(0, 5).map((item) => (
                    <div key={item.product.id} style={styles.cartHoverItem}>
                      {item.product.image ? (
                        <img
                          src={item.product.image}
                          alt={item.product.name}
                          style={styles.cartHoverImage}
                        />
                      ) : (
                        <div
                          aria-hidden="true"
                          style={{
                            ...styles.cartHoverImage,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            background: '#f1f5f9',
                            color: '#94a3b8',
                          }}
                        >
                          <i className="fa-regular fa-image" />
                        </div>
                      )}
                      <div style={styles.cartHoverItemInfo}>
                        <div style={styles.cartHoverItemName}>{item.product.name}</div>
                      </div>
                      <div style={styles.cartHoverItemPrice}>{item.product.price}</div>
                    </div>
                  ))}
                </div>
                <div style={styles.cartHoverFooter}>
                  <button type="button" style={styles.cartHoverViewBtn} onClick={() => changeTab('cart')}>
                    Xem tất cả
                  </button>
                </div>
              </div>
            )}
          </div>
          <div style={styles.notificationWrap} ref={notificationsRef}>
            <button type="button" onClick={toggleNotifications} style={styles.notificationButton} title="Thông báo">
              <i className="fa-solid fa-bell" />
              {unreadNotificationCount > 0 && (
                <span style={styles.notificationBadge}>
                  {unreadNotificationCount > 99 ? '99+' : unreadNotificationCount}
                </span>
              )}
            </button>
            {showNotifications && (
              <div data-notification-dropdown className="customer-notification-dropdown" style={styles.notificationDropdown}>
                <div className="customer-notification-head" style={styles.notificationHead}>
                  <div>
                    <div style={styles.notificationTitle}>Thông báo</div>
                    <div style={styles.notificationSub}>{unreadNotificationCount} thông báo chưa đọc</div>
                  </div>
                  <button type="button" className="customer-notification-read-all" onClick={markAllNotificationsRead} style={styles.notificationReadAll}>
                    Đọc tất cả
                  </button>
                </div>
                <div className="customer-notification-list" style={styles.notificationList}>
                  {loadingNotifications && <div style={styles.notificationEmpty}>Đang tải thông báo...</div>}
                  {!loadingNotifications && notifications.length === 0 && (
                    <div style={styles.notificationEmpty}>Chưa có thông báo nào.</div>
                  )}
                  {!loadingNotifications && notifications.map((notification) => (
                    <button
                      key={notification.notificationId}
                      type="button"
                      className="customer-notification-item"
                      onClick={() => markNotificationRead(notification)}
                      style={{
                        ...styles.notificationItem,
                        ...(!notification.isRead ? styles.notificationItemUnread : {}),
                      }}
                    >
                      <div style={styles.notificationItemTitle}>{notification.title || 'Thông báo EVG'}</div>
                      <div style={styles.notificationItemMessage}>{notification.message || '-'}</div>
                      <div style={styles.notificationTime}>
                        {formatNotificationTime(notification.updatedAt || notification.createdAt || notification.releaseDate)}
                      </div>
                    </button>
                  ))}
                </div>
                <div className="customer-notification-footer" style={styles.notificationFooter}>
                  <button type="button" className="customer-notification-view-all" onClick={(e) => openAllNotifications(e)} style={styles.notificationViewAll}>
                    Xem tất cả thông báo
                  </button>
                </div>
              </div>
            )}
          </div>
          <div style={styles.accountMenuWrap} ref={accountMenuRef}>
            <button
              data-account-menu-btn
              type="button"
              onClick={() => setShowAccountMenu((prev) => !prev)}
              style={styles.accountMenuButton}
            >
              <span data-account-avatar style={styles.accountAvatar}>{(profile?.fullname || user?.fullname || profile?.email || user?.email || 'P').charAt(0).toUpperCase()}</span>
              <span data-account-info style={styles.accountMenuInfo}>
                <strong style={styles.accountMenuName}>{profile?.fullname || user?.fullname || 'Phùng Anh Nam'}</strong>
                <small style={styles.accountMenuCode}>{affiliateCode}</small>
              </span>
              <i className="fa-solid fa-chevron-down" style={{ display: 'none' }} />
            </button>
            {showAccountMenu && (
              <div style={styles.accountDropdown} data-account-dropdown>
                <div style={styles.accountDropdownHead}>
                  <span style={styles.accountAvatarLarge}>{(profile?.fullname || user?.fullname || profile?.email || user?.email || 'P').charAt(0).toUpperCase()}</span>
                  <div style={styles.accountIdentityText}>
                    <strong style={styles.accountIdentityName}>{profile?.fullname || user?.fullname || 'Phùng Anh Nam'}</strong>
                    <span style={styles.accountIdentityEmail}>{profile?.email || user?.email || 'AFF00123'}</span>
                    <span style={styles.accountModePill}>Customer CMS (Khách hàng)</span>
                  </div>
                </div>
                {RoleUtils.hasCmsUi(user, agencyRoleList) && (
                  <button type="button" data-account-switch-card onClick={handleSwitchToCms} style={styles.accountSwitchCard}>
                    <span style={styles.accountSwitchIcon}>
                      <i className="fa-solid fa-arrow-right-arrow-left" />
                    </span>
                    <span style={styles.accountSwitchText}>
                      <strong>Chuyển sang CMS {cmsRoleLabel}</strong>
                      <small>Sử dụng giao diện và tính năng cho {cmsRoleLabel.toLowerCase()}</small>
                    </span>
                    <i className="fa-solid fa-chevron-right" style={styles.accountSwitchArrow} />
                  </button>
                )}
                <button type="button" onClick={() => { setShowAccountMenu(false); changeTab('account'); }}>
                  <i className="fa-regular fa-user" />
                  Hồ sơ tài khoản
                </button>
                <button type="button" onClick={handleChangePassword}>
                  <i className="fa-solid fa-lock" />
                  Đổi mật khẩu
                </button>
                <button type="button" onClick={() => { setShowAccountMenu(false); handleCopyLink(); }}>
                  <i className="fa-solid fa-link" />
                  Sao chép liên kết giới thiệu
                </button>
                <button type="button" onClick={() => { setShowAccountMenu(false); changeTab('wallet'); }}>
                  <i className="fa-solid fa-briefcase" />
                  Hoa hồng và ví tiền
                </button>
                <button type="button" onClick={openReferralQR}>
                  <i className="fa-solid fa-qrcode" />
                  Hiển thị mã QR
                </button>
                <button type="button" onClick={handleSignOut} style={styles.accountLogoutItem}>
                  <i className="fa-solid fa-right-from-bracket" />
                  Đăng xuất
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      <div className="mobile-sticky-title">
        {sidebarItems.find(item => item[0] === activeTab)?.[2] || 'Trang chủ'}
      </div>

      <style>{`
        @media (max-width: 1024px) {
          main.evg-main-content {
            padding: 0 12px 56px !important;
          }
          .evg-breadcrumb {
            padding: 12px 4px 0 4px !important;
          }
          .evg-customer-shell-content {
            gap: 10px !important;
          }
          [data-commerce-hero], [data-network-hero], [data-wallet-hero], [data-account-hero], [data-overview-hero], .evg-cart-hero {
            padding: 16px !important;
            gap: 12px !important;
            margin-top: 4px !important;
          }
          .evg-customer-page .responsive-sidebar-wrapper.mobile-open {
            top: 0 !important;
            height: 100vh !important;
            position: fixed !important;
            z-index: 99999 !important;
          }
          .evg-customer-page .responsive-sidebar-wrapper.mobile-open .mobile-sidebar-content {
            top: 0 !important;
            height: 100vh !important;
          }
          .mobile-sidebar-content [data-side-menu] {
            gap: 0 !important;
            padding: 8px 0 !important;
          }
          .mobile-sidebar-content [data-side-menu] button {
            padding: 10px 14px !important;
          }
          .mobile-sidebar-content [data-side-menu] button > div > i {
            font-size: 16px !important;
            width: 20px !important;
          }
          .mobile-sidebar-content [data-side-menu] > div > div {
            margin-top: 0 !important;
            margin-bottom: 0 !important;
          }
          .mobile-sidebar-content > * {
            flex-shrink: 0 !important;
          }
          .sidebar-promo-card {
            margin: 12px !important;
            border-radius: 16px !important;
          }
          .sidebar-promo-card .promo-img-1 {
            height: 140px !important;
          }
          .sidebar-promo-card .promo-content-wrapper {
            padding: 12px !important;
          }
          .sidebar-promo-card .promo-badge-row {
            padding: 8px 10px !important;
            margin-bottom: 12px !important;
          }
          .sidebar-promo-card p {
            margin-bottom: 12px !important;
            font-size: 12px !important;
          }
          .sidebar-promo-card button {
            padding: 10px !important;
            margin-bottom: 12px !important;
            font-size: 13px !important;
          }
          .sidebar-promo-card .promo-img-2 {
            height: 120px !important;
            margin: 0 12px 12px 12px !important;
          }
          .sidebar-support-card {
            padding: 12px !important;
            gap: 8px !important;
            margin: 24px 12px 32px 12px !important;
          }
        }
      `}</style>
      <main style={styles.main} className="evg-main-content">
        <section
          data-customer-shell
          className={sidebarCollapsed ? "responsive-shell-collapsed" : "responsive-shell"}
          style={{
            ...styles.customerShell,
            ...(sidebarCollapsed ? styles.customerShellCollapsed : {}),
          }}
        >
          {/* Render in grid only when not in mobile open state */}
          {!mobileMenuOpen && renderCustomerSidebar()}
          <div className="evg-customer-shell-content" style={styles.customerShellContent}>
            {activeTab !== 'overview' && activeTab !== 'ride' && (
              <div className="evg-breadcrumb" style={{ ...styles.breadcrumb, padding: '24px 24px 0 24px', marginBottom: -8 }}>
                <span style={{ cursor: 'pointer', transition: 'color 0.2s ease' }} onMouseEnter={(e) => e.target.style.color = '#16a34a'} onMouseLeave={(e) => e.target.style.color = '#64748b'} onClick={() => changeTab('overview')}>Trang chủ</span>
                <i className="fa-solid fa-chevron-right" style={{ fontSize: 11, margin: '0 8px' }} />
                {activeTab === 'product' ? (
                  <>
                    <span style={{ cursor: 'pointer', transition: 'color 0.2s ease' }} onMouseEnter={(e) => e.target.style.color = '#16a34a'} onMouseLeave={(e) => e.target.style.color = '#64748b'} onClick={() => changeTab('commerce')}>Đơn hàng - Sản phẩm</span>
                    <i className="fa-solid fa-chevron-right" style={{ fontSize: 11, margin: '0 8px' }} />
                    <span style={{ color: '#16a34a', fontWeight: 600 }}>Chi tiết sản phẩm</span>
                  </>
                ) : (
                  <span style={{ color: '#16a34a', fontWeight: 600 }}>
                    {activeTab === 'notifications' ? 'Thông báo' : activeTab === 'cart' ? 'Giỏ hàng' : sidebarItems.find(item => item[0] === activeTab)?.[2] || 'Trang chủ'}
                  </span>
                )}
              </div>
            )}
        {activeTab === 'overview' && (
          <section style={styles.dashboardContent}>
              <section
                style={{
                  ...styles.hero,
                  aspectRatio: '2170/512',
                }}
              >
                {bannerImages.map((banner, idx) => (
                  <img
                    key={idx}
                    src={banner.src}
                    alt=""
                    style={{
                      position: 'absolute',
                      inset: 0,
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      display: 'block',
                      transform: 'scale(1.02)',
                      opacity: currentBannerIndex === idx ? 1 : 0,
                      visibility: currentBannerIndex === idx ? 'visible' : 'hidden',
                      transition: 'opacity 0.8s ease-in-out, visibility 0.8s ease-in-out',
                      zIndex: currentBannerIndex === idx ? 1 : 0,
                      borderRadius: '24px',
                    }}
                  />
                ))}
                
                <div style={styles.bannerDotsContainer}>
                  {bannerImages.map((_, idx) => (
                    <span
                      key={idx}
                      onClick={() => setCurrentBannerIndex(idx)}
                      style={{
                        ...styles.bannerDot,
                        backgroundColor: currentBannerIndex === idx ? '#22c55e' : 'rgba(34, 197, 94, 0.35)',
                        width: currentBannerIndex === idx ? '18px' : '6px',
                      }}
                    />
                  ))}
                </div>
              </section>
              <section className="responsive-stats-grid" style={styles.statsGrid} data-stats-grid>
                {overviewKpis.map((item) => (
                  <article
                    
                    
                    key={item.label}
                    role="button"
                    tabIndex={0}
                    onClick={() => setSelectedInsightKey(item.key)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        setSelectedInsightKey(item.key);
                      }
                    }}
                    style={{
                    background: '#fff',
                    borderRadius: 14,
                    padding: 24,
                    boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
                    border: '1px solid #f0f0f0',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    cursor: 'pointer',
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
                      <i className={item.icon} style={{ color: item.color, fontSize: '16px' }}></i>
                      <span style={{ color: '#475569', fontWeight: 700, fontSize: '14px' }}>{item.label}</span>
                    </div>
                    <div style={{ fontWeight: 700, color: '#0f172a', fontSize: '28px', marginBottom: 12 }}>
                      {item.value}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', color: '#16a34a', fontSize: '13px', fontWeight: 500 }}>
                      {item.change} <i className="fa-solid fa-chevron-right" style={{ marginLeft: 8, fontSize: '10px' }}></i>
                    </div>
                  </article>
                ))}
              </section>

              <section className="responsive-dashboard-grid" style={styles.dashboardGrid} data-dashboard-grid>
                <article style={{ ...styles.panel, gridColumn: 'span 2' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px', paddingBottom: '16px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '16px' }}>
                      <h2 style={{ ...styles.panelTitle, margin: 0 }}>Doanh Thu - Hoa hồng</h2>
                      <div style={{ width: '1px', height: '24px', background: '#e2e8f0' }} />
                      <div style={{ display: 'flex', alignItems: 'center', gap: '16px', fontSize: '15px', color: '#475569' }}>
                        <span><span style={{color: '#64748b'}}>•</span> Doanh thu <strong style={{ color: '#0f172a' }}>{formatCurrency(totalRevenueValue).replace('đ', '')}</strong></span>
                        <span><span style={{color: '#64748b'}}>•</span> Hoa hồng <strong style={{ color: '#0f172a' }}>{formatCurrency(totalCommissionValue).replace('đ', '')}</strong></span>
                      </div>
                    </div>
                    <button type="button" style={{ background: '#fff', border: '1px solid #22c55e', color: '#0f172a', padding: '6px 16px', borderRadius: '20px', fontSize: '13px', fontWeight: 500, cursor: 'pointer' }}>
                      6 tháng gần nhất
                    </button>
                  </div>
                  <div style={{ height: '300px', width: '100%', marginTop: '20px' }}>
                    <Line
                      data={{
                        labels: overviewChartData.map(item => {
                          const parts = item.month.split('-');
                          if (parts.length === 2) {
                            const monthIndex = parseInt(parts[1], 10) - 1;
                            return ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][monthIndex] || item.label;
                          }
                          return item.label;
                        }),
                        datasets: [
                          {
                            label: 'Doanh thu',
                            data: overviewChartData.map(item => Number(item.revenue || 0)),
                            borderColor: '#22c55e',
                            borderWidth: 1.5,
                            pointRadius: 0,
                            pointHoverRadius: 4,
                            fill: false,
                            tension: 0.4,
                          },
                          {
                            label: 'Hoa hồng',
                            data: overviewChartData.map(item => Number(item.commission || 0)),
                            borderColor: '#93c5fd',
                            borderWidth: 1.5,
                            borderDash: [5, 5],
                            pointRadius: 0,
                            pointHoverRadius: 4,
                            fill: false,
                            tension: 0.4,
                          },
                        ],
                      }}
                      options={{
                        responsive: true,
                        maintainAspectRatio: false,
                        plugins: {
                          legend: {
                            display: false,
                          },
                          tooltip: {
                            mode: 'index',
                            intersect: false,
                            callbacks: {
                              label: function(context) {
                                let label = context.dataset.label || '';
                                if (label) {
                                  label += ': ';
                                }
                                if (context.parsed.y !== null) {
                                  label += new Intl.NumberFormat('vi-VN', { style: 'currency', currency: 'VND' }).format(context.parsed.y);
                                }
                                return label;
                              }
                            }
                          },
                        },
                        scales: {
                          x: {
                            grid: {
                              display: false,
                              drawBorder: false,
                            },
                            ticks: {
                              color: '#94a3b8',
                              font: {
                                size: 12,
                                weight: 500,
                              }
                            }
                          },
                          y: {
                            border: { display: false },
                            grid: {
                              color: '#f1f5f9',
                            },
                            ticks: {
                              color: '#94a3b8',
                              stepSize: 10000000,
                              callback: function(value) {
                                if (value === 0) return '0';
                                return (value / 1000000) + 'tr';
                              }
                            }
                          }
                        },
                        interaction: {
                          mode: 'nearest',
                          axis: 'x',
                          intersect: false,
                        },
                      }}
                    />
                  </div>
                </article>

                <article style={styles.panel}>
                  <div style={{ ...styles.panelHeader, justifyContent: 'center' }}>
                    <h2 style={styles.panelTitle}>Tỉ lệ hoa hồng theo trạng thái</h2>
                  </div>
                  <div style={styles.donutWrap}>
                    <div style={{ width: 220, height: 220, position: 'relative', marginTop: 24 }}>
                      <Doughnut
                        data={{
                          labels: ['Bị từ chối', 'Chờ duyệt', 'Đã thanh toán'],
                          datasets: [
                            {
                              data: [
                                rejectedCommissionValue > 0 ? rejectedCommissionValue : 0.001,
                                pendingCommissionValue > 0 ? pendingCommissionValue : 0.001,
                                availableCommissionValue > 0 ? availableCommissionValue : 0.001
                              ],
                              backgroundColor: [
                                '#ff4d4f',
                                '#4ade80',
                                '#16a34a',
                              ],
                              borderWidth: 0,
                              hoverOffset: 4
                            }
                          ]
                        }}
                        options={{
                          responsive: true,
                          maintainAspectRatio: false,
                          cutout: '72%',
                          plugins: {
                            legend: {
                              display: false,
                            },
                            tooltip: {
                              callbacks: {
                                label: function(context) {
                                  let label = context.label || '';
                                  if (label) {
                                    label += ': ';
                                  }
                                  const val = context.parsed;
                                  if (val === 0.001) return label + '0 đ';
                                  if (val !== null) {
                                    label += new Intl.NumberFormat('vi-VN', { style: 'currency', currency: 'VND' }).format(val);
                                  }
                                  return label;
                                }
                              }
                            }
                          }
                        }}
                      />
                      <div style={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        pointerEvents: 'none'
                      }}>
                        <strong style={{ fontSize: 18, color: '#1e293b', fontWeight: 500 }}>{formatCurrency(totalCommissionValue)}</strong>
                        <span style={{ color: '#64748b', fontSize: 11, marginTop: 2 }}>Tổng hoa hồng</span>
                      </div>
                    </div>
                    <div style={styles.donutLegend}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <i style={{ width: 10, height: 10, borderRadius: '50%', background: '#16a34a' }} /> 
                        Đã thanh toán
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <i style={{ width: 10, height: 10, borderRadius: '50%', background: '#4ade80' }} /> 
                        Chờ duyệt
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <i style={{ width: 10, height: 10, borderRadius: '50%', background: '#ff4d4f' }} /> 
                        Bị từ chối
                      </div>
                    </div>
                  </div>
                </article>

                <article style={styles.panel}>
                  <div style={{...styles.panelHeader, paddingBottom: '16px'}}>
                    <h2 style={styles.panelTitle}>Thông báo</h2>
                    <button type="button" style={{...styles.linkButton, color: '#047857', fontWeight: 500, display: 'flex', alignItems: 'center', gap: '4px'}} onClick={openAllNotifications}>
                      Xem tất cả <i className="fa-solid fa-chevron-right" style={{ fontSize: '10px' }} />
                    </button>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                    {overviewNotifications.map((item, index) => {
                      let dotColor = '#16a34a';
                      if (item.type === 'SYSTEM') dotColor = '#ef4444';
                      if (item.type === 'COMMISSION') dotColor = '#4ade80';
                      
                      const date = new Date(item.createdAt || item.updatedAt || Date.now());
                      const timeStr = `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')} | ${String(date.getDate()).padStart(2, '0')}/${String(date.getMonth() + 1).padStart(2, '0')}/${date.getFullYear()}`;

                      return (
                        <div key={item.notificationId || index} style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                          <i style={{ color: dotColor, fontSize: '12px', marginTop: '4px' }} className="fa-solid fa-circle" />
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                            <strong style={{ color: '#0f172a', fontSize: '15px', lineHeight: '1.4' }}>{item.title || 'Thông báo EVG'}</strong>
                            <span style={{ color: '#475569', fontSize: '13px' }}>{timeStr}</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </article>

                <article className="span-3" style={{ ...styles.panel, ...styles.recentOrdersPanel, gridColumn: "span 3" }}>
                  <div style={styles.panelHeader}>
                    <h2 style={styles.panelTitle}>Đơn hàng mới nhất</h2>
                    <button type="button" style={styles.linkButton} onClick={() => changeTab('commerce')}>Xem tất cả</button>
                  </div>
                  <div className="responsive-table-wrapper" style={styles.miniTable}>
                    <div style={{ ...styles.miniTableRow, ...styles.miniTableHead }}>
                      <span>Mã đơn</span>
                      <span>Khách hàng</span>
                      <span>Sản phẩm</span>
                      <span>Giá trị</span>
                      <span>Hoa hồng</span>
                      <span>Trạng thái</span>
                    </div>
                    {recentOverviewOrders.map((order) => (
                      <div key={order.code} style={styles.miniTableRow}>
                        <span style={styles.orderCode}>{order.code}</span>
                        <span>{order.buyer}</span>
                        <span>{order.productNames}</span>
                        <span>{formatCurrency(order.amount)}</span>
                        <span>{formatCurrency(order.commission)}</span>
                        <span style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#0f172a', fontWeight: 500 }}>
                          <i className="fa-solid fa-circle" style={{
                            fontSize: '10px',
                            color: order.status === 'PAID' || order.status === 'COMPLETED' ? '#16a34a' :
                                   order.status === 'PENDING' ? '#4ade80' :
                                   order.status === 'CANCELLED' || order.status === 'REJECTED' ? '#ef4444' : '#94a3b8'
                          }} />
                          {getOrderStatusName(order.status)}
                        </span>
                      </div>
                    ))}
                  </div>
                </article>

                <article style={styles.panel}>
                  <div style={{...styles.panelHeader, paddingBottom: '16px'}}>
                    <h2 style={styles.panelTitle}>Sản phẩm bán chạy</h2>
                    <button type="button" style={{...styles.linkButton, color: '#047857', fontWeight: 500, display: 'flex', alignItems: 'center', gap: '4px'}} onClick={() => changeTab('commerce')}>
                      Xem tất cả <i className="fa-solid fa-chevron-right" style={{ fontSize: '10px' }} />
                    </button>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                    {bestSellingProducts.map((product, index) => (
                      <div key={product.id || product.name} style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <span style={{ background: '#4ade80', color: '#000', width: '22px', height: '22px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 500, fontSize: '12px', flexShrink: 0 }}>
                          {index + 1}
                        </span>
                        <div style={{ width: '56px', height: '56px', borderRadius: '8px', overflow: 'hidden', background: '#f1f5f9', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          {product.image ? <img src={product.image} alt={product.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : <i className="fa-solid fa-charging-station" style={{ color: '#94a3b8' }} />}
                        </div>
                        <div style={{ flex: 1, display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', gap: '4px' }}>
                          <div style={{ minWidth: '80px', flex: '1 1 100px' }}>
                            <strong style={{ display: 'block', color: '#0f172a', fontSize: '15px', marginBottom: '2px' }}>{product.name}</strong>
                            <span style={{ color: '#475569', fontSize: '13px' }}>Đã bán: {product.sold}</span>
                          </div>
                          <b style={{ color: '#0f172a', fontSize: '15px', whiteSpace: 'nowrap', paddingTop: '2px' }}>{Utils.formatNumber(product.rawPrice || 0)}</b>
                        </div>
                      </div>
                    ))}
                  </div>
                </article>
              </section>

              <section style={styles.quickToolsPanel}>
                <div style={styles.panelHeader}>
                  <h2 style={styles.panelTitle}>Công cụ Marketing nhanh</h2>
                </div>
                <div data-quick-tool-grid className="responsive-quick-tool-grid" style={styles.quickToolGrid}>
                  {[
                    ['fa-solid fa-link', 'Link giới thiệu', 'Lấy link & chia sẻ', handleCopyLink],
                    ['fa-solid fa-qrcode', 'Tạo mã QR', 'Chia sẻ nhanh', openReferralQR],
                    ['fa-regular fa-image', 'Banner & Ảnh', 'Tải tài nguyên', () => changeTab('tools')],
                    ['fa-regular fa-copy', 'Mẫu nội dung', 'Copy & sử dụng', () => changeTab('tools')],
                    ['fa-regular fa-circle-play', 'Video giới thiệu', 'Chia sẻ video', () => changeTab('tools')],
                    ['fa-regular fa-file-lines', 'Chính sách', 'Xem chính sách', () => changeTab('tools')],
                  ].map(([icon, title, sub, onClick]) => (
                    <button key={title} type="button" style={styles.quickToolItem} onClick={onClick}>
                      <i className={icon} />
                      <span><strong>{title}</strong><small>{sub}</small></span>
                    </button>
                  ))}
                </div>
              </section>
          </section>
        )}

        {activeTab === 'notifications' && (
          <section style={styles.commerceScreen}>
            <div style={styles.commerceHead}>
              <div>
                <div style={styles.eyebrow}>Thông báo</div>
                <h1 style={styles.moduleTitle}>Tất cả thông báo</h1>
                <p style={{ ...styles.desc, color: '#6b7280' }}>
                  Danh sách thông báo dành cho tài khoản, vai trò và các thông báo chung của hệ thống.
                </p>
              </div>
              <button type="button" onClick={markAllNotificationsRead} style={styles.primaryButton}>
                Đọc tất cả
              </button>
            </div>

            <section style={styles.ordersPanel}>
              <div style={styles.notificationFilterBar}>
                {[
                  { id: 'ALL', label: 'Tất cả' },
                  { id: 'PAYMENT', label: 'Thanh toán' },
                  { id: 'ORDER', label: 'Đơn hàng' },
                  { id: 'SYSTEM', label: 'Hệ thống' }
                ].map((cat) => (
                  <button
                    key={cat.id}
                    onClick={() => setNotificationCategory(cat.id)}
                    style={{
                      ...styles.filterTab,
                      ...(notificationCategory === cat.id ? styles.filterTabActive : {}),
                    }}
                  >
                    {cat.label}
                  </button>
                ))}
              </div>
              {filteredAllNotifications.length > 0 && (
                <div style={styles.notificationActionBar}>
                  <label style={styles.selectAllLabel}>
                    <input
                      type="checkbox"
                      checked={filteredAllNotifications.length > 0 && selectedNotificationIds.length === filteredAllNotifications.length}
                      onChange={handleToggleSelectAll}
                      style={styles.actionCheckbox}
                    />
                    Chọn tất cả ({selectedNotificationIds.length}/{filteredAllNotifications.length})
                  </label>
                  <div style={styles.actionButtonsGroup}>
                    <button
                      type="button"
                      onClick={handleBulkRead}
                      disabled={selectedNotificationIds.length === 0}
                      style={{
                        ...styles.actionButton,
                        ...(selectedNotificationIds.length === 0 ? styles.actionButtonDisabled : {}),
                      }}
                    >
                      <i className="fa-solid fa-envelope-open" style={{ marginRight: 6 }} /> Đọc mục đã chọn
                    </button>
                    <button
                      type="button"
                      onClick={handleBulkDelete}
                      disabled={selectedNotificationIds.length === 0}
                      style={{
                        ...styles.actionButton,
                        ...styles.actionButtonDelete,
                        ...(selectedNotificationIds.length === 0 ? styles.actionButtonDisabled : {}),
                      }}
                    >
                      <i className="fa-solid fa-trash" style={{ marginRight: 6 }} /> Xóa mục đã chọn
                    </button>
                  </div>
                </div>
              )}

              {loadingAllNotifications ? (
                <div style={styles.orderEmpty}>Đang tải tất cả thông báo...</div>
              ) : filteredAllNotifications.length === 0 ? (
                <div style={styles.orderEmpty}>Chưa có thông báo nào trong danh mục này.</div>
              ) : (
                <div style={styles.fullNotificationList}>
                  {filteredAllNotifications.map((notification) => (
                    <div
                      key={notification.notificationId}
                      style={{
                        ...styles.fullNotificationItem,
                        ...(!notification.isRead ? styles.fullNotificationItemUnread : {}),
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={selectedNotificationIds.includes(notification.notificationId)}
                        onChange={() => toggleSelectNotification(notification.notificationId)}
                        style={styles.actionCheckbox}
                      />
                      <div
                        style={{ flex: 1, cursor: 'pointer' }}
                        onClick={() => markNotificationRead(notification)}
                      >
                        <div style={styles.fullNotificationTop}>
                          <strong>{notification.title || 'Thông báo EVG'}</strong>
                          <span>{formatNotificationTime(notification.updatedAt || notification.createdAt || notification.releaseDate)}</span>
                        </div>
                        <p style={styles.fullNotificationMessage}>{notification.message || '-'}</p>
                      </div>
                      <button
                        type="button"
                        onClick={() => handleDeleteNotification(notification.notificationId)}
                        style={styles.singleDeleteButton}
                        title="Xóa thông báo"
                      >
                        <i className="fa-solid fa-trash" style={{ color: '#ef4444' }} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </section>
          </section>
        )}

        {(activeTab === 'commerce' || activeTab === 'product') && (
          <section style={styles.shopPage} data-shop-page>
            <div style={styles.shopMain}>
              <div style={styles.shopHero} data-commerce-hero>
                <div>
                  <h1 style={{ ...styles.shopTitle, display: 'flex', alignItems: 'center', gap: 16 }}>
                    {activeTab === 'product' && (
                      <button 
                        type="button" 
                        onClick={returnToPreviousProductTab}
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 8,
                          color: '#475569',
                          fontSize: 16,
                          fontWeight: 600,
                          background: '#f1f5f9',
                          border: 'none',
                          padding: '6px 14px',
                          borderRadius: 8,
                          whiteSpace: 'nowrap',
                          flexShrink: 0,
                          cursor: 'pointer',
                        }}
                      >
                        <i className="fa-solid fa-arrow-left" /> Quay lại
                      </button>
                    )}
                    {activeTab === 'product' ? 'Chi tiết sản phẩm' : 'Đơn hàng - Sản phẩm'}
                  </h1>
                  <p style={styles.shopDesc}>Khám phá danh mục thiết bị sạc EV chính hãng và quản lý đơn hàng của bạn một cách dễ dàng.</p>
                </div>
                {activeTab !== 'product' && (
                  <div style={styles.shopHeroActions}>
                    <button type="button" style={styles.shopPrimaryButton} onClick={() => productSectionRef.current?.scrollIntoView({ behavior: 'smooth' })}>
                      <i className="fa-solid fa-cart-shopping" style={{ opacity: 0.85, fontSize: '0.9em' }} /> Mua sắm ngay
                    </button>
                  </div>
                )}
              </div>

              {activeTab === 'product' ? (
                <div style={{ marginTop: 20 }}>
                  <CustomerProductDetailScreen
                    initialProduct={selectedProduct}
                    propProductId={selectedProduct?.id || selectedProduct?.categoryId || subPath}
                    onBack={returnToPreviousProductTab}
                    onCartChange={() => setCartItems(getSavedCartItems(cartStorageKey))}
                  />
                </div>
              ) : (
                <>

              <section style={{ ...styles.shopKpiGrid, gridTemplateColumns: kpiGridCols }} data-commerce-kpi-grid>
                {commerceKpis.map((item) => (
                  <article 
                    key={item.label} 
                    role="button"
                    tabIndex={0}
                    aria-label={`Xem ${item.label}`}
                    style={{ ...styles.shopKpiCard, ...styles.shopKpiCardClickable }}
                    onClick={() => handleCommerceKpiClick(item)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        handleCommerceKpiClick(item);
                      }
                    }}
                    
                    
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
                      <i className={item.icon} style={{ color: item.color, fontSize: '16px' }}></i>
                      <span style={{ color: '#475569', fontWeight: 700, fontSize: '14px' }}>{item.label}</span>
                    </div>
                    <div
                      className={`responsive-kpi-value${item.keepValueOnOneLine ? ' responsive-kpi-value--single-line' : ''}`}
                      style={{
                        fontWeight: 700,
                        color: '#0f172a',
                        fontSize: `${item.keepValueOnOneLine ? getSingleLineAmountFontSize(item.value) : 28}px`,
                        lineHeight: 1.2,
                        marginBottom: 12,
                        wordBreak: item.keepValueOnOneLine ? 'normal' : 'break-word',
                        whiteSpace: item.keepValueOnOneLine ? 'nowrap' : 'normal',
                      }}
                    >
                      {item.value}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', color: '#16a34a', fontSize: '13px', fontWeight: 500 }}>
                      {item.note} <i className="fa-solid fa-chevron-right" style={{ marginLeft: 8, fontSize: '10px' }}></i>
                    </div>
                  </article>
                ))}
              </section>

              {commercePinnedMarketingDocument ? (
                <section style={styles.promoBanner} data-commerce-promo>
                  <div style={styles.promoImage}>
                    {commercePinnedMarketingDocument.thumbnailUrl || /\.(png|jpg|jpeg|gif|webp|svg)(\?|$)/i.test(commercePinnedMarketingDocument.fileUrl || '')
                      ? <img src={commercePinnedMarketingDocument.thumbnailUrl || commercePinnedMarketingDocument.fileUrl} alt={commercePinnedMarketingDocument.title} style={styles.promoImageImg} />
                      : <i className={getMarketingDocumentIcon(commercePinnedMarketingDocument.type, commercePinnedMarketingDocument.fileUrl)} />}
                  </div>
                  <div style={styles.promoContent}>
                    <span>{getMarketingDocumentTypeName(commercePinnedMarketingDocument.type).toUpperCase()}</span>
                    <h2>{commercePinnedMarketingDocument.title}</h2>
                    <p>{getMarketingDocumentSummary(commercePinnedMarketingDocument)}</p>
                  </div>
                  <button type="button" style={styles.promoActionButton} onClick={openCommercePinnedMarketingDocument}>
                    <span>Xem chi tiết</span>
                    <i className="fa-solid fa-arrow-right" />
                  </button>
                </section>
              ) : null}

              <section style={styles.shopPanel} ref={commerceOrderSectionRef}>
                <div style={styles.panelHeader}>
                  <h2 style={styles.panelTitle}>Đơn hàng của tôi</h2>
                </div>
                <div style={styles.orderStatusTabs} data-commerce-order-tabs>
                  {commerceOrderTabs.map((tab) => (
                    <button
                      key={tab.key}
                      type="button"
                      style={{
                        ...styles.orderStatusTab,
                        ...(selectedCommerceStatus === tab.key ? styles.orderStatusTabActive : {}),
                      }}
                      onClick={() => {
                        setSelectedCommerceStatus(tab.key);
                        setShowAllCommerceOrders(false);
                      }}
                    >
                      {tab.label} ({tab.count})
                    </button>
                  ))}
                </div>
                {loadingOrders ? (
                  <div style={styles.orderEmpty}>Đang tải đơn hàng...</div>
                ) : (
                  <div data-table-wrapper>
                    <div style={styles.shopOrderTable}>
                      <div style={{ ...styles.shopOrderRow, ...styles.shopOrderHead }}>
                      <span>Mã đơn</span>
                      <span>Sản phẩm</span>
                      <span>Người mua</span>
                      <span style={styles.orderAmountCell}>Giá trị</span>
                      <span style={styles.orderCenterCell}>Trạng thái</span>
                      <span style={styles.orderCenterCell}>Cập nhật</span>
                      <span style={styles.orderActionHead}>Thao tác</span>
                    </div>
                    {displayedCommerceOrders.map((order) => (
                      <div
                        key={order.code}
                        style={{
                          ...styles.shopOrderRow,
                          ...(order.status === 'ORDERED' ? styles.shopOrderRowClickable : {}),
                        }}
                        onClick={() => {
                          if (order.status === 'ORDERED') openOrderPaymentQR(order);
                        }}
                      >
                        <span style={styles.orderCodeCell}>
                          <strong style={styles.orderCode}>{order.code}</strong>
                          <small>{formatNotificationTime(order.sourceOrder?.createdAt) || '-'}</small>
                        </span>
                        <span style={styles.shopOrderProduct}>
                          <span style={styles.shopOrderThumb}><i className="fa-solid fa-charging-station" /></span>
                          <span>{order.productNames}<small>x{order.quantity}</small></span>
                        </span>
                        <span style={styles.orderBuyerCell}>{order.buyer}<small>{order.phone}</small></span>
                        <strong style={styles.orderAmountCell}>{formatCurrency(order.amount)}</strong>
                        <span style={{
                          ...styles.statusBadge,
                          ...styles.orderStatusCell,
                          ...(['ORDERED', 'PENDING'].includes(order.status) ? styles.pendingStatusBadge : {}),
                          ...(['PAID', 'COMPLETED', 'SHIPPING', 'PROCESSING', 'INSTALLING', 'INSTALLTING', 'INSTALLED'].includes(order.status) ? styles.paidStatusBadge : {}),
                          ...(['CANCELLED', 'REJECTED'].includes(order.status) ? styles.cancelledStatusBadge : {}),
                        }}>
                          {getOrderStatusName(order.status)}
                        </span>
                        <span style={styles.orderUpdatedCell}>{formatNotificationTime(order.updatedAt) || '-'}</span>
                        <span style={styles.orderQuickActions}>
                          {order.status === 'ORDERED' && (
                            <button
                              type="button"
                              style={{ ...styles.orderActionButton, ...styles.orderPayButton }}
                              disabled={loadingPaymentQR}
                              onClick={(event) => {
                                event.stopPropagation();
                                openOrderPaymentQR(order);
                              }}
                            >
                              <i className="fa-solid fa-qrcode" /> {String(order.payment?.paymentMethod || '').toUpperCase() === 'VNPAY' ? 'Thanh toán' : 'Thanh toán QR'}
                            </button>
                          )}
                          <button
                            type="button"
                            style={{ ...styles.orderActionButton, ...styles.orderActionButtonPrimary }}
                            onClick={(event) => {
                              event.stopPropagation();
                              handleReorder(order);
                            }}
                          >
                            <i className="fa-solid fa-rotate-right" /> Đặt lại
                          </button>
                        </span>
                      </div>
                    ))}
                    {displayedCommerceOrders.length === 0 && (
                      <div style={styles.orderEmpty}>Không có đơn hàng trong trạng thái này.</div>
                    )}
                  </div>
                  </div>
                )}
                {filteredCommerceOrders.length > 5 && (
                  <button
                    type="button"
                    style={styles.viewAllOrderButton}
                    onClick={() => setShowAllCommerceOrders((prev) => !prev)}
                  >
                    {showAllCommerceOrders ? 'Thu gọn còn 5 đơn mới nhất' : `Xem tất cả ${filteredCommerceOrders.length} đơn hàng`}
                  </button>
                )}
              </section>

              <section className="evg-unified-filter-bar" data-filter-panel>
                <div className="search-box">
                  <input
                    value={productKeyword}
                    onChange={(event) => setProductKeyword(event.target.value)}
                    placeholder="Tìm kiếm sản phẩm, mã sản phẩm, thương hiệu..."
                  />
                  {productKeyword && <button type="button" onClick={() => setProductKeyword('')} title="Xóa từ khóa" style={{ border: 0, background: 'transparent', cursor: 'pointer', padding: 4 }}>×</button>}
                </div>
                <label className="select-wrap">
                  <EvgCustomSelect value={selectedProductCategoryId} onChange={(event) => setSelectedProductCategoryId(event.target.value)}>
                    <option value="ALL">Danh mục: Tất cả</option>
                    {productCategories.map((category) => (
                      <option key={category.id} value={category.id}>{category.name}</option>
                    ))}
                  </EvgCustomSelect>
                </label>
                <label className="select-wrap">
                  <EvgCustomSelect value={selectedPriceRange} onChange={(event) => setSelectedPriceRange(event.target.value)}>
                    {productPriceRanges.map((range) => (
                      <option key={range.value} value={range.value}>{range.label}</option>
                    ))}
                  </EvgCustomSelect>
                </label>
                <label className="select-wrap">
                  <EvgCustomSelect value={selectedStockStatus} onChange={(event) => setSelectedStockStatus(event.target.value)}>
                    {productStockStatuses.map((stock) => (
                      <option key={stock.value} value={stock.value}>{stock.label}</option>
                    ))}
                  </EvgCustomSelect>
                </label>
                <label className="select-wrap">
                  <EvgCustomSelect value={selectedProductSort} onChange={(event) => setSelectedProductSort(event.target.value)}>
                    {productSortOptions.map((sort) => (
                      <option key={sort.value} value={sort.value}>Sắp xếp: {sort.label}</option>
                    ))}
                  </EvgCustomSelect>
                </label>
                <button
                  type="button"
                  className="btn-export"
                  onClick={() => document.querySelector('[data-products]')?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
                >
                  Xem kết quả
                </button>
                <button type="button" className="btn-reset" onClick={() => {
                  setProductKeyword('');
                  setSelectedProductCategoryId('ALL');
                  setSelectedPriceRange('ALL');
                  setSelectedStockStatus('ALL');
                  setSelectedProductSort('DEFAULT');
                }}>
                  <i className="fa-solid fa-rotate-left" /> Đặt lại
                </button>
              </section>

              <div style={styles.shopCategoryTabs} data-commerce-category-tabs>
                <button
                  type="button"
                  style={{
                    ...styles.shopCategoryChip,
                    ...(selectedProductCategoryId === 'ALL' ? styles.shopCategoryChipActive : {}),
                  }}
                  onClick={() => setSelectedProductCategoryId('ALL')}
                >
                  Tất cả sản phẩm
                </button>
                {productCategories.map((category) => (
                  <button
                    key={category.id}
                    type="button"
                    style={{
                      ...styles.shopCategoryChip,
                      ...(String(selectedProductCategoryId) === String(category.id) ? styles.shopCategoryChipActive : {}),
                    }}
                    onClick={() => setSelectedProductCategoryId(category.id)}
                  >
                    {category.name}
                  </button>
                ))}
              </div>

              <section style={styles.shopPanel} data-products ref={productSectionRef}>
                <div style={styles.panelHeader}>
                  <h2 style={styles.panelTitle}>Sản phẩm nổi bật</h2>
                </div>

                <div style={styles.shopProductGrid} data-commerce-grid>
                  {loadingProducts && (
                    <div style={styles.productEmptyState}>Đang tải sản phẩm...</div>
                  )}
                  {!loadingProducts && products.length > 0 && filteredProducts.length === 0 && (
                    <div style={styles.productEmptyState}>Không có sản phẩm phù hợp. Thử đổi từ khóa hoặc danh mục khác.</div>
                  )}
                  {!loadingProducts && (windowWidth <= 768 ? filteredProducts.slice((productPage - 1) * 10, productPage * 10) : filteredProducts).map((product, index) => (
                    <article key={product.code || index} style={styles.shopProductCard} onClick={() => openProductDetail(product)}>
                      <div style={styles.shopProductVisual}>
                        {product.productBadges?.length > 0 && (
                          <div style={styles.productBadgeStack}>
                            {product.productBadges.map((badgeValue) => {
                              const badge = productBadgeOptions[badgeValue];
                              if (!badge) return null;
                              return (
                                <span
                                  key={badgeValue}
                                  style={{
                                    ...styles.productBadge,
                                    background: badge.color,
                                  }}
                                >
                                  {badge.label}
                                </span>
                              );
                            })}
                          </div>
                        )}
                        {product.images?.length > 1 && <span style={styles.imageCountBadge}><i className="fa-regular fa-images" /> {product.images.length} ảnh</span>}
                        {product.image ? (
                          <>
                            <img
                              src={product.images?.[0] || product.image}
                              alt={product.name}
                              style={styles.shopProductImage}
                              onError={(event) => {
                                event.currentTarget.style.display = 'none';
                                if (event.currentTarget.nextElementSibling) {
                                  event.currentTarget.nextElementSibling.style.display = 'flex';
                                }
                              }}
                            />
                            <div style={styles.productFallbackHidden}>
                              <i className="fa-solid fa-charging-station" />
                            </div>
                          </>
                        ) : (
                          <div style={styles.productFallback}>
                            <i className="fa-solid fa-charging-station" />
                          </div>
                        )}
                      </div>
                      <div style={styles.shopProductBody}>
                        <div style={styles.shopProductInfo}>
                          <strong style={styles.shopProductName}>{product.name || `EV 00${index + 1}`}</strong>
                          {product.shortDescription ? (
                            <div style={styles.shopProductShortDescription} dangerouslySetInnerHTML={{ __html: product.shortDescription }} />
                          ) : (
                            <>
                              {(product.productCategory || product.shortName) && (
                                <span style={styles.shopProductCategory}>{product.productCategory || product.shortName}</span>
                              )}
                              {product.productAttributes?.some((attribute) => attribute?.value) && (
                                <ul style={styles.shopProductFeatures}>
                                  {product.productAttributes
                                    .filter((attribute) => attribute?.value)
                                    .slice(0, 2)
                                    .map((attribute, attributeIndex) => (
                                      <li key={`${attribute.label || 'attr'}-${attributeIndex}`}>{attribute.value}</li>
                                    ))}
                                </ul>
                              )}
                            </>
                          )}
                          <div style={styles.stockLine}>
                            <span style={product.isInStock ? styles.inStockText : styles.outOfStockText}>
                              {product.isInStock ? 'Còn hàng' : 'Hết hàng'}
                            </span>
                          </div>
                        </div>
                        <div style={styles.shopProductBottom}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10 }}>
                            <div style={styles.shopProductPriceStack}>
                              {Number(product.rawOriginalPrice || 0) > Number(product.rawPrice || 0) && (
                                <div style={styles.shopProductOriginalRow}>
                                  <span style={styles.shopProductOriginalPrice}>
                                    {product.originalPrice || formatCurrency(product.rawOriginalPrice || 0)}
                                  </span>
                                  <span style={styles.shopProductDiscountBadge}>
                                    -{Math.round((1 - Number(product.rawPrice || 0) / Number(product.rawOriginalPrice || 1)) * 100)}%
                                  </span>
                                </div>
                              )}
                              <div style={styles.shopProductPrice}>{product.price || formatCurrency(product.rawPrice || 0)}</div>
                            </div>
                            <div style={styles.inlineQty} data-qty-control>
                              <button
                                type="button"
                                style={{ border: 0, background: '#f1f5f9', height: '100%', cursor: 'pointer', fontSize: 14, color: '#475569' }}
                                onClick={(event) => {
                                  event.stopPropagation();
                                  updateProductQuantity(product.id, getProductQuantity(product.id) - 1);
                                }}
                              >
                                −
                              </button>
                              <span style={{ fontSize: 13, fontWeight: 500, color: '#1e293b' }}>{getProductQuantity(product.id)}</span>
                              <button
                                type="button"
                                style={{ border: 0, background: '#f1f5f9', height: '100%', cursor: 'pointer', fontSize: 14, color: '#475569' }}
                                onClick={(event) => {
                                  event.stopPropagation();
                                  updateProductQuantity(product.id, getProductQuantity(product.id) + 1);
                                }}
                              >
                                +
                              </button>
                            </div>
                          </div>
                          <div style={styles.shopProductActions} data-product-actions>
                            <button
                              type="button"
                              style={styles.addCartButton}
                              onClick={(event) => {
                                event.stopPropagation();
                                addProductToCartFromCard(product);
                              }}
                            >
                              Thêm vào giỏ
                            </button>
                            <button
                              type="button"
                              style={styles.buyNowButton}
                              onClick={(event) => {
                                event.stopPropagation();
                                startBuyNow(product, getProductQuantity(product.id));
                              }}
                            >
                              Mua sắm ngay
                            </button>
                          </div>
                        </div>
                      </div>
                    </article>
                  ))}
                </div>

                {windowWidth <= 768 && filteredProducts.length > 10 && (() => {
                  const totalPages = Math.ceil(filteredProducts.length / 10);
                  const getPages = () => {
                    if (totalPages <= 5) return Array.from({ length: totalPages }, (_, i) => i + 1);
                    const pages = [];
                    pages.push(1);
                    if (productPage > 3) pages.push('...');
                    for (let i = Math.max(2, productPage - 1); i <= Math.min(totalPages - 1, productPage + 1); i++) pages.push(i);
                    if (productPage < totalPages - 2) pages.push('...');
                    pages.push(totalPages);
                    return pages;
                  };
                  return (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, marginTop: 20, flexWrap: 'wrap' }}>
                      <button type="button" disabled={productPage === 1} onClick={() => setProductPage(p => Math.max(1, p - 1))} style={{ border: '1px solid #e2e8f0', background: '#fff', borderRadius: 8, padding: '6px 12px', cursor: productPage === 1 ? 'not-allowed' : 'pointer', color: '#475569', opacity: productPage === 1 ? 0.4 : 1 }}><i className="fa-solid fa-chevron-left" /></button>
                      {getPages().map((page, i) => page === '...' ? (
                        <span key={`ellipsis-${i}`} style={{ padding: '0 4px', color: '#94a3b8' }}>...</span>
                      ) : (
                        <button key={page} type="button" onClick={() => setProductPage(page)} style={{ border: productPage === page ? '2px solid #16a34a' : '1px solid #e2e8f0', background: productPage === page ? '#dcfce7' : '#fff', color: productPage === page ? '#15803d' : '#475569', borderRadius: 8, padding: '6px 12px', fontWeight: productPage === page ? 700 : 400, cursor: 'pointer', minWidth: 36 }}>{page}</button>
                      ))}
                      <button type="button" disabled={productPage === totalPages} onClick={() => setProductPage(p => Math.min(totalPages, p + 1))} style={{ border: '1px solid #e2e8f0', background: '#fff', borderRadius: 8, padding: '6px 12px', cursor: productPage === totalPages ? 'not-allowed' : 'pointer', color: '#475569', opacity: productPage === totalPages ? 0.4 : 1 }}><i className="fa-solid fa-chevron-right" /></button>
                    </div>
                  );
                })()}

              </section>
              </>
              )}
            </div>

            <aside style={styles.cartSidebar} data-cart-sidebar>
              <section style={styles.cartSidebarPanel}>
                <h2>Giỏ hàng của bạn ({commerceCartDisplayItems.reduce((sum, item) => sum + Number(item.quantity || 1), 0)})</h2>
                <div style={styles.cartSidebarList}>
                  {commerceCartDisplayItems.map((item) => (
                    <div key={item.product?.id || item.product?.name} style={styles.cartSidebarItem}>
                      <div style={styles.cartSidebarImage}>
                        {item.product?.image ? <img src={item.product.image} alt={item.product.name} style={styles.cartSidebarImageImg} /> : <i className="fa-solid fa-charging-station" />}
                      </div>
                      <div style={styles.cartSidebarInfo}>
                        <strong>{item.product?.name}</strong>
                        {(item.product?.shortDescription || item.product?.productCategory || item.product?.shortName) && (
                          <div style={{ fontSize: 13, color: '#64748b' }} dangerouslySetInnerHTML={{ __html: item.product?.shortDescription || item.product?.productCategory || item.product?.shortName }} />
                        )}
                        <b>{item.product?.price || formatCurrency(item.product?.rawPrice)}</b>
                      </div>
                      <div style={styles.cartSidebarQty} data-qty-control>
                        <button type="button" disabled={item.isDemo} onClick={() => updateCartQuantity(item.product.id, Number(item.quantity || 1) - 1)}>−</button>
                        <span>{item.quantity}</span>
                        <button type="button" disabled={item.isDemo} onClick={() => updateCartQuantity(item.product.id, Number(item.quantity || 1) + 1)}>+</button>
                      </div>
                      <button type="button" style={styles.removeCartButton} disabled={item.isDemo} onClick={() => removeCartItem(item.product.id)}>
                        <i className="fa-regular fa-trash-can" />
                      </button>
                    </div>
                  ))}
                </div>
                <div style={styles.cartSidebarSummary}>
                  <div><span>Tạm tính{commerceCartVat.vat > 0 ? ' (chưa VAT)' : ''}</span><strong>{formatCurrency(commerceCartVat.beforeTax)}</strong></div>
                  <div><span>VAT ({commerceCartVat.percent}%)</span><strong>{formatCurrency(commerceCartVat.vat)}</strong></div>
                  <div><span>Phí vận chuyển</span><strong style={{ color: '#22c55e' }}>Miễn phí</strong></div>
                  <div style={styles.voucherRow} data-voucher-row>
                    <input placeholder="Mã giảm giá" />
                    <button type="button" style={styles.voucherButton}>Áp dụng</button>
                  </div>
                  <div style={styles.cartSidebarTotal}><span>Tổng cộng</span><strong>{formatCurrency(commerceCartVat.total)}</strong></div>
                  <small>Đã bao gồm VAT {commerceCartVat.percent}%</small>
                  <button type="button" style={styles.checkoutButton} onClick={() => {
                    if (cartItems.length) openOrderForm();
                    else changeTab('commerce');
                  }}>
                    <i className="fa-solid fa-lock" /> Thanh toán
                  </button>
                  <button type="button" style={styles.continueShoppingButton}>Tiếp tục mua hàng</button>
                  <p><i className="fa-solid fa-shield-halved" /> Thanh toán an toàn & bảo mật</p>
                </div>
              </section>

              <section style={styles.trustPanel}>
                <h2>An tâm mua sắm cùng EVG</h2>
                {[
                  ['fa-solid fa-shield-halved', 'Bảo hành chính hãng', '24 tháng cho toàn bộ sản phẩm'],
                  ['fa-solid fa-screwdriver-wrench', 'Hỗ trợ lắp đặt', 'Đội ngũ kỹ thuật chuyên nghiệp'],
                  ['fa-solid fa-truck-fast', 'Giao hàng nhanh chóng', 'Toàn quốc 1–3 ngày'],
                  ['fa-solid fa-phone-volume', 'Hotline hỗ trợ 24/7', `${SUPPORT_HOTLINE_DISPLAY} – cskh@evgroup.com.vn`],
                ].map(([icon, title, sub]) => (
                  <div key={title} style={styles.trustItem}>
                    <i className={icon} />
                    <div><strong>{title}</strong><span>{sub}</span></div>
                  </div>
                ))}
              </section>
            </aside>
          </section>
        )}

        {activeTab === 'network' && (
          <section style={styles.networkPage} data-network-page>
            <div style={styles.portalMain}>
              <div style={styles.shopHero} data-network-hero>
                <div>
                  <div style={styles.eyebrow}>CHƯƠNG TRÌNH GIỚI THIỆU ĐỐI TÁC</div>
                  <h1 style={styles.shopTitle}>Phát triển mạng lưới cùng EVG</h1>
                  <p style={styles.shopDesc}>Chia sẻ cơ hội hợp tác, theo dõi hành trình của người được giới thiệu và quản lý hiệu quả mạng lưới của bạn.</p>
                </div>
                <div style={styles.shopHeroActions}>
                  <button type="button" style={styles.shopExportButton} onClick={handleCopyLink}>
                    <i className="fa-solid fa-link" /> Sao chép liên kết
                  </button>
                  <button type="button" style={styles.shopPrimaryButton} onClick={openReferralQR}>
                    <i className="fa-solid fa-qrcode" style={{ opacity: 0.85, fontSize: '0.9em' }} /> Hiển thị mã QR
                  </button>
                </div>
              </div>

              <section style={styles.networkKpiGrid} data-network-grid>
                {[
                  ['Tổng số liên hệ', networkMetrics.contacts, 'fa-solid fa-users', '#3b82f6', 'ALL'],
                  ['Chủ mặt bằng', filteredNetworkGroups.landlords.length, 'fa-solid fa-house-chimney', '#10b981', 'LANDLORD'],
                  ['Đối tác đầu tư', filteredNetworkGroups.investors.length, 'fa-solid fa-handshake', '#f59e0b', 'INVESTOR'],
                  ['Khách hàng được giới thiệu', filteredNetworkGroups.retailCustomers.length, 'fa-solid fa-user-tag', '#8b5cf6', 'CUSTOMER'],
                  ['Tỷ lệ chuyển đổi', networkMetrics.conversionRate, 'fa-solid fa-percent', '#ec4899', 'ALL'],
                ].map(([label, value, icon, color, targetGroup]) => (
                  <article 
                    key={label} 
                    style={{ ...styles.shopKpiCard, ...styles.networkKpiCard, cursor: 'pointer' }}
                    role="button"
                    tabIndex={0}
                    onClick={() => handleNetworkKpiClick(targetGroup)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        handleNetworkKpiClick(targetGroup);
                      }
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
                      <i className={icon} style={{ color, fontSize: '16px' }}></i>
                      <span style={{ color: '#475569', fontWeight: 700, fontSize: '14px' }}>{label}</span>
                    </div>
                    <div style={{ fontWeight: 700, color: '#0f172a', fontSize: '28px', marginBottom: 12 }}>
                      {value}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', color: '#16a34a', fontSize: '13px', fontWeight: 500 }}>
                      Xem dữ liệu 30 ngày gần nhất <i className="fa-solid fa-chevron-right" style={{ marginLeft: 8, fontSize: '10px' }}></i>
                    </div>
                  </article>
                ))}
              </section>

              <section style={styles.networkInsightGrid} data-network-insights>
                <article style={{ ...styles.shopPanel, ...styles.networkInsightCard }}>
                  <div style={styles.panelHeader}><div><h2 style={styles.panelTitle}>Hành trình giới thiệu</h2><p style={styles.panelDescription}>Tổng quan mức độ tương tác của các liên hệ trong mạng lưới.</p></div><span style={styles.selectBadge}>30 ngày gần nhất</span></div>
                  <div style={styles.journeyList}>
                    {[
                      [networkMetrics.contacts, 'Liên hệ được giới thiệu', 'Đã ghi nhận trong mạng lưới'],
                      [networkMetrics.contacts, 'Đăng ký thành công', 'Đã tạo tài khoản trên hệ thống'],
                      [networkMetrics.orders, 'Phát sinh đơn hàng', 'Đơn hàng được ghi nhận từ mạng lưới'],
                    ].map(([value, label, description], index) => (
                      <div key={label} style={styles.journeyItem}>
                        <span style={styles.journeyIndex}>{index + 1}</span>
                        <div><strong>{label}</strong><small>{description}</small></div>
                        <b>{value}</b>
                      </div>
                    ))}
                  </div>
                </article>
                <article style={{ ...styles.shopPanel, ...styles.networkInsightCard }}>
                  <div style={styles.panelHeader}><div><h2 style={styles.panelTitle}>Hiệu quả liên kết giới thiệu</h2><p style={styles.panelDescription}>Kết quả ghi nhận từ liên kết và mã giới thiệu của bạn.</p></div><span style={styles.selectBadge}>30 ngày gần nhất</span></div>
                  <div style={styles.linkStats}>{[
                    ['—', 'Lượt truy cập'],
                    [networkMetrics.contacts, 'Đăng ký thành công'],
                    [networkMetrics.orders, 'Đơn hàng phát sinh'],
                    [formatCurrency(networkMetrics.revenue), 'Doanh thu ghi nhận'],
                  ].map(([value, label]) => <div key={label} style={styles.linkStatItem}><span>{label}</span><strong>{value}</strong></div>)}</div>
                  <div style={styles.networkDataNotice}><span>Biểu đồ lượt truy cập sẽ hiển thị khi hệ thống ghi nhận dữ liệu từ liên kết giới thiệu.</span></div>
                </article>
                <article style={{ ...styles.shopPanel, ...styles.networkInsightCard }}>
                  <h2 style={styles.panelTitle}>Doanh thu từ mạng lưới</h2>
                  <p style={styles.panelDescription}>Tổng giá trị đơn hàng được ghi nhận qua chương trình giới thiệu.</p>
                  {networkMetrics.revenue > 0
                    ? <div style={styles.donutWrap}><div style={styles.donutChart}><div style={styles.donutCenter}><span>Tổng doanh thu</span><strong>{formatCurrency(networkMetrics.revenue)}</strong></div></div></div>
                    : <div style={styles.networkRevenueEmpty}><strong>Chưa phát sinh doanh thu</strong><span>Doanh thu từ các đơn hàng hợp lệ sẽ được tổng hợp tại đây.</span></div>}
                </article>
              </section>

              <section className="evg-unified-filter-bar" data-network-filter>
                <div className="search-box">
                  <input type="text" placeholder="Tìm theo mã hoặc tên..." value={networkKeyword} onChange={e => setNetworkKeyword(e.target.value)} />
                  {networkKeyword && <button type="button" onClick={() => setNetworkKeyword('')} title="Xóa từ khóa" style={{ border: 0, background: 'transparent', cursor: 'pointer', padding: 4 }}>×</button>}
                </div>
                <label className="select-wrap">
                  <EvgCustomSelect value={networkGroup} onChange={(event) => setNetworkGroup(event.target.value)}><option value="ALL">Nhóm đối tác: Tất cả</option><option value="LANDLORD">Chủ mặt bằng</option><option value="INVESTOR">Đối tác đầu tư</option><option value="CUSTOMER">Khách hàng giới thiệu</option></EvgCustomSelect>
                </label>
                <label className="select-wrap">
                  <EvgCustomSelect value={networkStatus} onChange={(event) => setNetworkStatus(event.target.value)}><option value="ALL">Trạng thái: Tất cả</option>{networkStatusOptions.map((status) => <option key={status} value={status}>{getNetworkStatusName(status)}</option>)}</EvgCustomSelect>
                </label>
                <label className="select-wrap">
                  <EvgCustomSelect value={networkPeriod} onChange={(event) => setNetworkPeriod(event.target.value)}><option value="ALL">Toàn bộ thời gian</option><option value="30">30 ngày gần nhất</option><option value="90">90 ngày gần nhất</option><option value="365">12 tháng gần nhất</option></EvgCustomSelect>
                </label>
                <label className="select-wrap">
                  <EvgCustomSelect value={networkSort} onChange={(event) => setNetworkSort(event.target.value)}><option value="UPDATED_DESC">Sắp xếp: Mới cập nhật</option><option value="NAME_ASC">Sắp xếp: Tên A–Z</option><option value="ORDERS_DESC">Sắp xếp: Nhiều đơn</option><option value="REVENUE_DESC">Sắp xếp: Doanh thu</option></EvgCustomSelect>
                </label>
                <button type="button" className="btn-reset" onClick={resetNetworkFilters}><i className="fa-solid fa-rotate-left" /> Đặt lại</button>
              </section>

              {[
                ['LANDLORD', 'Chủ mặt bằng', filteredNetworkGroups.landlords],
                ['INVESTOR', 'Đối tác đầu tư', filteredNetworkGroups.investors],
                ['CUSTOMER', 'Khách hàng được giới thiệu', filteredNetworkGroups.retailCustomers],
              ].filter(([groupKey]) => networkGroup === 'ALL' || networkGroup === groupKey).map(([, title, rows]) => (
                <section key={title} style={styles.shopPanel}>
                  <div style={{ ...styles.networkSectionHeader, flexWrap: 'wrap', gap: 12 }}><div><h2 style={styles.panelTitle}>{title}</h2><p style={styles.panelDescription}>{rows.length ? `${rows.length} liên hệ đang được quản lý` : 'Chưa có liên hệ nào trong nhóm này'}</p></div><span style={styles.networkCountBadge}>{rows.length}</span></div>
                  <div data-table-wrapper>
                    <div style={styles.networkTable}>
                      <div style={{ ...styles.networkTableRow, ...styles.shopOrderHead }}><span>Họ và tên</span><span>Thông tin liên hệ</span><span>Sản phẩm quan tâm</span><span>Trạng thái</span><span>Đơn hàng</span><span>Doanh thu</span><span>Thao tác</span></div>
                    {rows.map((item) => {
                      const memberIdentity = getNetworkMemberIdentity(item);
                      return (
                        <div key={item.id || item.name} style={styles.networkTableRow}>
                          <strong>{item.name}</strong><span>{item.phone}<small>{item.email}</small></span><span>{item.productName || '-'}</span><span style={{ ...styles.statusBadge, ...(['Đã ký HĐ', 'Đã mua', 'COMPLETED', 'PAID', 'ACTIVE', 'NORMAL'].includes(item.status) ? styles.paidStatusBadge : {}), ...(['Quan tâm', 'Đang đàm phán', 'PENDING', 'REQUESTED', 'REGISTERED', 'AVAILABLE'].includes(item.status) ? styles.pendingStatusBadge : {}) }}>{getNetworkStatusName(item.status || 'Mới')}</span><span>{item.orders || 0}</span><span>{formatCurrency(item.revenue || 0)}</span>
                          <span style={styles.rowActionDots}>
                            <button
                              type="button"
                              style={styles.rowActionDotsButton}
                              title="Xem mạng lưới"
                              onClick={() => handleOpenMemberTree(memberIdentity)}
                            >
                              •••
                            </button>
                          </span>
                        </div>
                      );
                    })}
                    {!rows.length && <div style={styles.networkEmptyState}><div><strong>Chưa có dữ liệu</strong><p>Các liên hệ được giới thiệu thành công sẽ xuất hiện tại đây.</p></div></div>}
                    </div>
                  </div>
                </section>
              ))}

              {/* MODAL CHI TIẾT MẠNG LƯỚI CỦA THÀNH VIÊN */}
              {selectedMemberTreeModal && typeof document !== 'undefined' && createPortal((
                <div style={styles.memberTreeOverlay}>
                  <div style={styles.memberTreeModal}>
                    <div style={styles.memberTreeHeader}>
                      <div style={styles.memberTreeHeaderMain}>
                        <span style={styles.memberTreeHeaderIcon}><i className="fa-solid fa-sitemap" /></span>
                        <div>
                          <h3 style={styles.memberTreeTitle}>Sơ đồ mạng lưới của {selectedMemberTreeModal.fullName}</h3>
                          <div style={styles.memberTreeSubtitle}>
                            <span>{selectedMemberTreeModal.memberCode}</span>
                            <span>{memberTreeData?.nodes?.length || 0} thành viên hiển thị</span>
                            <span>Hiển thị tối đa 10 cấp</span>
                          </div>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => { setSelectedMemberTreeModal(null); setMemberTreeData(null); }}
                        style={styles.memberTreeCloseButton}
                      >
                        ×
                      </button>
                    </div>

                    <div style={styles.memberTreeBody}>
                      {loadingMemberTree ? (
                        <div style={styles.memberTreeLoading}>
                          <i className="fa-solid fa-spinner fa-spin" />
                          <div>Đang tải cây mạng lưới...</div>
                        </div>
                      ) : memberTreeData ? (
                        <>
                          <div style={styles.memberTreeStats}>
                            {[
                              ['Giới thiệu trực tiếp', memberTreeData.summary?.directReferralCount || 0, '#16a34a'],
                              ['Tổng thành viên tuyến dưới', memberTreeData.summary?.totalDownlineCount || 0, '#2563eb'],
                              ['Độ sâu mạng lưới', getNetworkLevelName(memberTreeData.summary?.maxLevel), '#d97706'],
                            ].map(([label, value, color]) => (
                              <div key={label} style={styles.memberTreeStatCard}>
                                <span>{label}</span>
                                <strong style={{ color }}>{value}</strong>
                              </div>
                            ))}
                          </div>

                          <div style={styles.memberTreeCanvas}>
                            <div style={styles.memberTreeRootRow}>
                              <div style={styles.memberTreeRootCard}>
                                <span style={styles.memberTreeAvatar}>
                                  {(memberTreeData.rootMember?.fullName || selectedMemberTreeModal.fullName || 'T').charAt(0).toUpperCase()}
                                </span>
                                <div>
                                  <strong>{memberTreeData.rootMember?.fullName || selectedMemberTreeModal.fullName}</strong>
                                  <small>{memberTreeData.rootMember?.memberCode || selectedMemberTreeModal.memberCode}</small>
                                </div>
                                <b>Tài khoản gốc</b>
                              </div>
                            </div>

                            {(!memberTreeData.nodes || memberTreeData.nodes.length === 0) ? (
                              <div style={styles.memberTreeEmpty}>
                                <i className="fa-regular fa-folder-open" />
                                <strong>Chưa có thành viên tuyến dưới</strong>
                                <span>Thành viên này chưa giới thiệu thêm người nào.</span>
                              </div>
                            ) : (
                              <div style={styles.memberTreeList}>
                              {memberTreeData.nodes.map((node) => (
                                <div
                                  key={node.userId}
                                  style={{
                                    ...styles.memberTreeNode,
                                    marginLeft: Math.min((node.level - 1) * 38, 304),
                                  }}
                                >
                                  <span style={styles.memberTreeConnector} />
                                  <div style={styles.memberTreeNodeMain}>
                                    <span style={{ ...styles.memberTreeLevelBadge, background: node.level === 1 ? '#16a34a' : node.level <= 3 ? '#2563eb' : '#7c3aed' }}>
                                      {getNetworkLevelName(node.level)}
                                    </span>
                                    <span style={styles.memberTreeAvatarSmall}>
                                      {(node.fullName || 'T').charAt(0).toUpperCase()}
                                    </span>
                                    <div style={styles.memberTreeNodeInfo}>
                                      <strong>{node.fullName}</strong>
                                      <small>{node.memberCode}</small>
                                    </div>
                                  </div>
                                  <span style={styles.memberTreeNodeCount}>Giới thiệu trực tiếp: <b>{node.directReferralCount}</b></span>
                                </div>
                              ))}
                              </div>
                            )}
                          </div>
                        </>
                      ) : (
                        <div style={styles.memberTreeError}>
                          Không thể tải dữ liệu mạng lưới thành viên.
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ), document.body)}
            </div>
          </section>
        )}

        {activeTab === 'wallet' && (
          <section style={{ width: '100%' }} data-wallet-page>
            <div style={styles.walletMain}>
              <div style={styles.shopHero} data-wallet-hero>
                <div>
                  <div style={styles.eyebrow}>QUẢN LÝ THU NHẬP</div>
                  <h1 style={styles.shopTitle}>Hoa hồng và thanh toán</h1>
                  <p style={styles.shopDesc}>Theo dõi các khoản hoa hồng, lịch sử giao dịch và gửi yêu cầu rút tiền về tài khoản ngân hàng của bạn.</p>
                </div>
              </div>
              <section className="responsive-wallet-top-grid" style={styles.walletTopGrid} data-wallet-top>
                <article style={styles.shopPanel}>
                  <div style={styles.panelHeader}><div><h2 style={styles.panelTitle}>Tổng quan số dư</h2><p style={styles.panelDescription}>Số liệu được tổng hợp theo trạng thái xử lý thực tế.</p></div></div>
                  <div className="responsive-wallet-summary-grid" style={styles.walletSummaryGrid} data-wallet-summary-grid>{walletSummaryCards.map((item) => <div key={item.label} style={styles.walletSummaryCard}><span style={{ ...styles.walletSummaryIcon, color: item.color, background: `${item.color}12` }}><i className={item.icon} /></span><span>{item.label}</span><strong>{item.value}</strong><small>{item.change}</small></div>)}</div>
                </article>
                <article style={{ ...styles.shopPanel, ...styles.incomeTrendPanel }}>
                  <div style={styles.incomeTrendHeader}><div><h2 style={styles.panelTitle}>Xu hướng thu nhập</h2><p style={styles.panelDescription}>Doanh thu và hoa hồng trong 6 tháng gần nhất.</p></div><span style={styles.selectBadge}>6 tháng gần nhất</span></div>
                  <div style={styles.incomeTrendStats}>
                    <div style={styles.incomeTrendStat}>
                      <span style={styles.incomeTrendStatLabel}><i style={{ color: '#22c55e' }} className="fa-solid fa-circle" /> Doanh thu</span>
                      <strong style={styles.incomeTrendStatValue}>{formatCurrency(incomeTrendSummary.totalRevenue)}</strong>
                    </div>
                    <div style={styles.incomeTrendStat}>
                      <span style={styles.incomeTrendStatLabel}><i style={{ color: '#60a5fa' }} className="fa-solid fa-circle" /> Hoa hồng</span>
                      <strong style={styles.incomeTrendStatValue}>{formatCurrency(incomeTrendSummary.totalCommission)}</strong>
                    </div>
                  </div>
                  {overviewChartData.some((item) => Number(item.revenue || 0) > 0 || Number(item.commission || 0) > 0)
                    ? <div style={styles.incomeTrendChart}>
                        <div style={styles.incomeTrendGrid}>
                          <span style={styles.incomeTrendGridLine} />
                          <span style={styles.incomeTrendGridLine} />
                          <span style={styles.incomeTrendGridLine} />
                        </div>
                        <div style={styles.incomeTrendBars}>
                          {overviewChartData.map((item) => {
                            const revenue = Number(item.revenue || 0);
                            const commission = Number(item.commission || 0);
                            const revenueHeight = incomeTrendSummary.maxValue > 0 ? Math.max(10, Math.round((revenue / incomeTrendSummary.maxValue) * 122)) : 0;
                            const commissionHeight = incomeTrendSummary.maxValue > 0 ? Math.max(8, Math.round((commission / incomeTrendSummary.maxValue) * 122)) : 0;
                            return (
                              <div key={item.month} style={styles.incomeTrendBarItem}>
                                <div style={styles.incomeTrendBarWrap} title={`${item.label}: ${formatCurrency(revenue)} / ${formatCurrency(commission)}`}>
                                  <span style={{ ...styles.incomeTrendRevenueBar, height: revenueHeight }} />
                                  <span style={{ ...styles.incomeTrendCommissionBar, height: commissionHeight }} />
                                </div>
                                <span style={styles.incomeTrendMonth}>{item.label}</span>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    : <div style={styles.walletEmptyChart}><strong>Chưa có dữ liệu biểu đồ</strong><span>Biểu đồ sẽ được cập nhật khi phát sinh doanh thu hoặc hoa hồng.</span></div>}
                </article>
              </section>
              <section className="responsive-wallet-content-grid" style={styles.walletContentGrid} data-wallet-content>
                <article style={styles.shopPanel}>
                  <div style={styles.walletTabs} data-wallet-tabs>{[
                    ['direct', 'fa-solid fa-bag-shopping', 'Hoa hồng bán hàng', '#16a34a', '#dcfce7'],
                    ['passive', 'fa-solid fa-people-arrows', 'Hoa hồng từ mạng lưới', '#7c3aed', '#ede9fe'],
                    ['transactions', 'fa-solid fa-clock-rotate-left', 'Lịch sử giao dịch', '#2563eb', '#dbeafe'],
                  ].map(([key, icon, label, color, background]) => (
                    <button key={key} type="button" style={{ ...styles.walletTabButton, ...(commissionTab === key ? { ...styles.walletTabButtonActive, color, borderColor: color } : { borderColor: 'transparent' }) }} onClick={() => setCommissionTab(key)}>
                      <span style={{ ...styles.walletTabIcon, color: commissionTab === key ? color : '#64748b', background: commissionTab === key ? background : '#f1f5f9' }}><i className={icon} /></span>
                      <span>{label}<small>{key === 'direct' ? walletTransactionRows.directRows.length : key === 'passive' ? walletTransactionRows.passiveRows.length : walletTransactionRows.transactionRows.length} giao dịch</small></span>
                    </button>
                  ))}</div>
                  <section className="evg-unified-filter-bar" data-wallet-filter>
                    <div className="search-box">
                      <input type="text" placeholder="Mã giao dịch, nội dung hoặc khách hàng" value={commissionKeyword} onChange={e => setCommissionKeyword(e.target.value)} />
                      {commissionKeyword && <button type="button" onClick={() => setCommissionKeyword('')} title="Xóa từ khóa" style={{ border: 0, background: 'transparent', cursor: 'pointer', padding: 4 }}>×</button>}
                    </div>
                    <label className="select-wrap">
                      <EvgCustomSelect value={commissionPeriod} onChange={(event) => setCommissionPeriod(event.target.value)}><option value="ALL">Thời gian: Toàn bộ</option><option value="30">30 ngày gần nhất</option><option value="90">90 ngày gần nhất</option><option value="365">12 tháng gần nhất</option></EvgCustomSelect>
                    </label>
                    <label className="select-wrap">
                      <EvgCustomSelect value={commissionStatus} onChange={(event) => setCommissionStatus(event.target.value)}><option value="ALL">Trạng thái: Tất cả</option>{commissionStatusOptions.map((status) => <option key={status} value={status}>{getOrderStatusName(status)}</option>)}</EvgCustomSelect>
                    </label>
                    <button type="button" className="btn-reset" onClick={resetCommissionFilters}><i className="fa-solid fa-rotate-left" /> Đặt lại</button>
                    <button type="button" className="btn-export" onClick={exportWalletRows}>Xuất dữ liệu</button>
                  </section>
                  <div style={styles.networkSectionHeader}><div><h2 style={styles.panelTitle}>{commissionTab === 'direct' ? 'Hoa hồng bán hàng' : commissionTab === 'passive' ? 'Hoa hồng từ mạng lưới' : 'Lịch sử giao dịch'}</h2><p style={styles.panelDescription}>{commissionTab === 'direct' ? 'Các khoản hoa hồng phát sinh trực tiếp từ đơn hàng bạn giới thiệu.' : commissionTab === 'passive' ? 'Các khoản hoa hồng phát sinh từ đối tác thuộc mạng lưới của bạn.' : 'Toàn bộ lịch sử ghi nhận hoa hồng và yêu cầu rút tiền.'}</p></div><span style={styles.networkCountBadge}>{walletCommissionRows.length}</span></div>
                  <div data-table-wrapper>
                    <div style={styles.walletTable}>
                      <div style={{ ...styles.walletTableRow, ...styles.shopOrderHead }}><span>Mã giao dịch</span><span>Loại giao dịch</span><span>Khách hàng/Người nhận</span><span>Số tiền</span><span>Ngày tạo</span><span>Trạng thái</span></div>
                      {walletCommissionRows.map((item) => <div key={item.rowId} style={styles.walletTableRow}><span><strong>{item.saleCode || '-'}</strong><small>{item.productName}</small></span><span><span style={{ ...styles.walletTypeBadge, ...(item.rowType === 'passive' ? styles.walletTypePassive : {}), ...(item.rowType === 'withdrawal' ? styles.walletTypeWithdrawal : {}), ...(item.rowType === 'order' ? styles.walletTypeOrder : {}) }}><i className={item.rowType === 'direct' ? 'fa-solid fa-bag-shopping' : item.rowType === 'passive' ? 'fa-solid fa-people-arrows' : item.rowType === 'order' ? 'fa-solid fa-cart-shopping' : 'fa-solid fa-building-columns'} /> {item.typeLabel}</span></span><span>{item.customerName}<small>{item.customerEmail}</small></span><strong>{item.amountDisplay || formatCurrency(item.amount)}</strong><span>{formatDateTime(item.createdAt || item.time)}</span><span style={{ ...styles.statusBadge, ...(['ORDERED', 'PENDING'].includes(item.status) ? styles.pendingStatusBadge : {}), ...(['SHIPPING', 'PROCESSING', 'INSTALLING', 'INSTALLTING', 'INSTALLED', 'PAID', 'COMPLETED'].includes(item.status) ? styles.paidStatusBadge : {}), ...(['CANCELLED', 'REJECTED', 'FAILED'].includes(item.status) ? styles.cancelledStatusBadge : {}) }}>{getOrderStatusName(item.status)}</span></div>)}
                      {!walletCommissionRows.length && <div style={styles.walletTableEmpty}><span style={styles.walletEmptyIcon}><i className={commissionTab === 'direct' ? 'fa-solid fa-bag-shopping' : commissionTab === 'passive' ? 'fa-solid fa-people-arrows' : 'fa-solid fa-clock-rotate-left'} /></span><strong>Chưa có dữ liệu phù hợp</strong><span>Giao dịch sẽ xuất hiện tại đây khi hệ thống ghi nhận phát sinh thực tế.</span></div>}
                    </div>
                  </div>
                </article>
                <aside style={styles.walletAside}>
                  <section style={styles.shopPanel}>
                    <div style={styles.withdrawHeader}>
                      <span style={styles.withdrawHeaderIcon}>
                        <i className="fa-solid fa-building-columns" />
                      </span>
                      <div>
                        <h2 style={styles.panelTitle}>Rút tiền về ngân hàng</h2>
                        <p>Số dư khả dụng <strong>{formatCurrency(walletWithdrawableValue)}</strong></p>
                      </div>
                    </div>
                    
                    <div style={styles.withdrawForm}>
                      <div style={styles.withdrawFormField}>
                        <label style={styles.withdrawInputLabel}>Số tiền muốn rút</label>
                        <input
                          style={styles.formInput}
                          type="text"
                          inputMode="numeric"
                          value={withdrawAmount ? Utils.formatNumber(Number(withdrawAmount)) : ''}
                          onChange={(event) => {
                            const digits = event.target.value.replace(/\D/g, '').replace(/^0+(?=\d)/, '');
                            setWithdrawAmount(digits);
                          }}
                          placeholder="Nhập số tiền muốn rút"
                          autoComplete="off"
                        />
                      </div>
                      
                      <div style={styles.withdrawFormField}>
                        <label style={styles.withdrawInputLabel}>Tài khoản nhận tiền <RequiredMark /></label>
                        <EvgCustomSelect
                          value={effectiveWithdrawBankSource}
                          onChange={(event) => {
                            setWithdrawBankSource(event.target.value);
                            setWithdrawBankErrors({});
                          }}
                          style={{ height: 49, border: '1px solid #d1d5db', borderRadius: 14 }}
                        >
                          {savedWithdrawBankAccounts.map((item) => (
                            <option key={item.value} value={item.value}>
                              {item.bankName} {maskBankAccountNumber(item.bankAccount)}
                            </option>
                          ))}
                          <option value={MANUAL_WITHDRAW_BANK_SOURCE}>Nhập tài khoản khác</option>
                        </EvgCustomSelect>

                        {selectedWithdrawBankAccount && (
                          <div style={styles.withdrawBankSummary}>
                            <strong>{selectedWithdrawBankAccount.bankBeneficiary}</strong>
                            <span>{selectedWithdrawBankAccount.bankName} - {maskBankAccountNumber(selectedWithdrawBankAccount.bankAccount)}</span>
                          </div>
                        )}

                        {effectiveWithdrawBankSource === MANUAL_WITHDRAW_BANK_SOURCE && (
                          <div style={styles.withdrawBankFields}>
                            <div style={styles.withdrawFormField}>
                              <label style={styles.withdrawInputLabel}>Mã ngân hàng <RequiredMark /></label>
                              <input
                                style={{ ...styles.formInput, ...(withdrawBankErrors.bankName ? styles.formInputError : {}) }}
                                type="text"
                                value={withdrawBankForm.bankName}
                                onChange={(event) => updateWithdrawBankField('bankName', event.target.value.toUpperCase())}
                                placeholder="VD: BIDV, VCB, TCB"
                              />
                              {withdrawBankErrors.bankName && <small style={styles.formErrorText}>{withdrawBankErrors.bankName}</small>}
                            </div>
                            <div style={styles.withdrawFormField}>
                              <label style={styles.withdrawInputLabel}>Số tài khoản <RequiredMark /></label>
                              <input
                                style={{ ...styles.formInput, ...(withdrawBankErrors.bankAccountNumber ? styles.formInputError : {}) }}
                                type="text"
                                inputMode="numeric"
                                autoComplete="off"
                                value={withdrawBankForm.bankAccountNumber}
                                onChange={(event) => updateWithdrawBankField('bankAccountNumber', event.target.value)}
                                placeholder="Nhập số tài khoản"
                              />
                              {withdrawBankErrors.bankAccountNumber && <small style={styles.formErrorText}>{withdrawBankErrors.bankAccountNumber}</small>}
                            </div>
                            <div style={styles.withdrawFormField}>
                              <label style={styles.withdrawInputLabel}>Tên chủ tài khoản <RequiredMark /></label>
                              <input
                                style={{ ...styles.formInput, ...(withdrawBankErrors.accountHolder ? styles.formInputError : {}) }}
                                type="text"
                                value={withdrawBankForm.accountHolder}
                                onChange={(event) => updateWithdrawBankField('accountHolder', event.target.value)}
                                placeholder="Nhập tên chủ tài khoản"
                              />
                              {withdrawBankErrors.accountHolder && <small style={styles.formErrorText}>{withdrawBankErrors.accountHolder}</small>}
                            </div>
                          </div>
                        )}
                      </div>
                      
                      <div style={styles.withdrawFormField}>
                        <label style={styles.withdrawInputLabel}>Ghi chú</label>
                        <textarea style={{ ...styles.formInput, minHeight: 76, resize: 'vertical' }} value={withdrawNote} onChange={(event) => setWithdrawNote(event.target.value)} placeholder="Nội dung ghi chú (không bắt buộc)" />
                      </div>
                      
                      <div style={styles.walletRules} data-wallet-rules>
                        <span><i className="fa-regular fa-clock" style={{ marginRight: 6 }} /> Thời gian xử lý từ 1–3 ngày làm việc</span>
                        <span><i className="fa-solid fa-receipt" style={{ marginRight: 6 }} /> Không thu phí giao dịch</span>
                        <span><i className="fa-solid fa-circle-info" style={{ marginRight: 6 }} /> Số tiền rút tối thiểu 50.000đ</span>
                      </div>
                      
                      <button type="button" style={{ ...styles.checkoutButton, width: '100%', marginTop: 8 }} onClick={submitWithdrawal} disabled={submittingWithdraw}>
                        {submittingWithdraw ? 'Đang gửi...' : 'Gửi yêu cầu rút tiền'}
                      </button>
                    </div>
                  </section>
                  <section style={styles.shopPanel}><div style={styles.panelHeader}><div><h2 style={styles.panelTitle}>Lịch sử rút tiền</h2><p style={styles.panelDescription}>Theo dõi trạng thái các yêu cầu gần đây.</p></div><button type="button" style={styles.linkButton}>Xem tất cả</button></div>{(affiliateData.withdrawals || []).map((item) => <div key={item.walletTransactionId} style={styles.withdrawItem}><strong>{item.walletTransactionId}</strong><span>{item.description}</span><b>{formatCurrency(Math.abs(item.amount || 0))}</b><em>{getOrderStatusName(item.status)}</em></div>)}{!(affiliateData.withdrawals || []).length && <div style={styles.compactEmptyState}>Chưa có yêu cầu rút tiền</div>}</section>
                </aside>
              </section>
              <section style={styles.shopPanel}><div><h2 style={styles.panelTitle}>Chính sách và hỗ trợ thanh toán</h2><p style={styles.panelDescription}>Thông tin cần biết về cách ghi nhận, đối soát và thanh toán hoa hồng.</p></div><div className="responsive-policy-quick-grid" style={styles.policyQuickGrid} data-policy-grid>{[
                ['fa-solid fa-file-contract', 'Chính sách hoa hồng'],
                ['fa-solid fa-list-check', 'Điều kiện thanh toán'],
                ['fa-solid fa-building-columns', 'Phương thức nhận tiền'],
                ['fa-solid fa-headset', 'Hỗ trợ thanh toán'],
              ].map(([icon, item]) => <div key={item} style={styles.policyQuickItem}><span style={styles.policyQuickIcon}><i className={icon} /></span><strong>{item}</strong><span>Xem thông tin</span></div>)}</div></section>
            </div>
          </section>
        )}

        {activeTab === 'tools' && (
          <section style={{ width: '100%' }} data-tools-page>
            <div style={styles.portalMain}>
              <div style={styles.shopHero} data-commerce-hero>
                <div><div style={styles.eyebrow}>CÔNG CỤ HỖ TRỢ BÁN HÀNG</div><h1 style={styles.shopTitle}>Tài liệu, chính sách và hướng dẫn</h1><p style={styles.shopDesc}>Truy cập tài liệu sản phẩm, nội dung truyền thông, chính sách hoa hồng và hướng dẫn sử dụng chính thức dành cho đối tác EVG.</p></div>
                <div style={styles.shopHeroActions}><button type="button" style={styles.shopExportButton} onClick={handleCopyLink}><i className="fa-solid fa-link" /> Sao chép liên kết giới thiệu</button><button type="button" style={styles.shopExportButton} onClick={() => showToolList('MARKETING', 'DOCUMENT')}><i className="fa-solid fa-file-arrow-down" /> Tải bộ tài liệu</button><button type="button" style={styles.shopPrimaryButton} onClick={() => activePolicyDocument ? openMarketingDocument(activePolicyDocument) : showToolList('POLICY', 'POLICY')}><i className="fa-solid fa-file-shield" style={{ opacity: 0.85, fontSize: '0.9em' }} /> Chính sách mới nhất</button></div>
              </div>
              <section className="evg-unified-filter-bar" data-filter-panel>
                <div className="search-box">
                  <input type="text" placeholder="Tìm kiếm tài liệu..." value={toolKeyword} onChange={e => setToolKeyword(e.target.value)} />
                  {toolKeyword && <button type="button" onClick={() => setToolKeyword('')} title="Xóa từ khóa" style={{ border: 0, background: 'transparent', cursor: 'pointer', padding: 4 }}>×</button>}
                </div>
                <label className="select-wrap">
                  <EvgCustomSelect value={toolCategory} onChange={(e) => setToolCategory(e.target.value)}><option value="ALL">Danh mục: Tất cả</option><option value="MARKETING">Tài liệu</option><option value="POLICY">Chính sách</option><option value="GUIDE">Hướng dẫn</option></EvgCustomSelect>
                </label>
                <label className="select-wrap">
                  <EvgCustomSelect value={toolFileType} onChange={(e) => setToolFileType(e.target.value)}><option value="ALL">Nội dung: Tất cả</option><option value="DOCUMENT">Tài liệu</option><option value="POLICY">Chính sách</option><option value="GUIDE">Hướng dẫn</option><option value="VIDEO">Video</option></EvgCustomSelect>
                </label>
                <label className="select-wrap">
                  <EvgCustomSelect value={toolFormat} onChange={(e) => setToolFormat(e.target.value)}><option value="ALL">Định dạng: Tất cả</option><option value="PNG">PNG / JPG</option><option value="PDF">PDF</option><option value="MP4">MP4</option><option value="EXCEL">Excel</option></EvgCustomSelect>
                </label>
                <label className="select-wrap">
                  <EvgCustomSelect value={toolSortBy} onChange={(e) => setToolSortBy(e.target.value)}><option value="NEWEST">Sắp xếp: Mới nhất</option><option value="OLDEST">Sắp xếp: Cũ nhất</option></EvgCustomSelect>
                </label>
                <button type="button" className="btn-reset" onClick={() => { setToolKeyword(''); setToolCategory('ALL'); setToolFileType('ALL'); setToolFormat('ALL'); setToolSortBy('NEWEST'); }}><i className="fa-solid fa-rotate-left" /> Đặt lại</button>
              </section>
              <section className="responsive-tools-content-grid" style={styles.toolsContentGrid} data-tools-content>
                <div style={styles.portalMain}>
                  <section style={styles.shopPanel}>
                    <div style={styles.panelHeader}><h2 style={styles.panelTitle}>Tài liệu Marketing <span style={styles.badge}>{documentToolDocuments.length} tài liệu</span></h2></div>
                    {loadingMarketingDocuments ? (
                      <div style={styles.largeEmptyState}><i className="fa-solid fa-spinner fa-spin" /> Đang tải tài liệu...</div>
                    ) : documentToolDocuments.length ? (
                      <>
                        <div style={styles.toolsDocumentGrid} data-tools-doc-grid>
                          {documentToolDocuments.map((document, index) => (
                            <article key={document.marketingDocumentId || index} style={{ ...styles.toolDocCard, cursor: 'pointer' }} role="button" tabIndex={0} onClick={() => openMarketingDocument(document)} onKeyDown={(event) => { if (event.key === 'Enter' || event.key === ' ') openMarketingDocument(document); }}>
                              <div style={styles.toolDocPreview}>
                                {index === 0 && <span style={styles.hotBadge}>Mới</span>}
                                {document.thumbnailUrl || /\.(png|jpg|jpeg|gif|webp|svg)(\?|$)/i.test(document.fileUrl || '') ? <img src={document.thumbnailUrl || document.fileUrl} alt={document.title} style={styles.toolDocPreviewImage} /> : <div><i className={getMarketingDocumentIcon(document.type, document.fileUrl)} /><span>EVG</span></div>}
                              </div>
                              <div style={styles.toolDocInfo}><strong>{document.title}</strong><span>{getMarketingDocumentTypeName(document.type)}</span><small>{getMarketingDocumentSummary(document)}</small><small>Cập nhật: {formatDocumentDate(document.updatedAt || document.createdAt)}</small></div>
                              <div style={styles.toolDocActions} data-tool-actions><button type="button" onClick={(event) => { event.stopPropagation(); openMarketingDocument(document); }}><i className="fa-regular fa-eye" /> Xem chi tiết</button><button type="button" onClick={(event) => { event.stopPropagation(); openMarketingDocumentFile(document); }}><i className="fa-solid fa-download" /> Tải xuống</button><button type="button" onClick={(event) => { event.stopPropagation(); copyMarketingDocument(document); }}><i className="fa-regular fa-copy" /> Sao chép</button><button type="button" onClick={(event) => { event.stopPropagation(); copyMarketingDocument(document, 'Đã sao chép nội dung chia sẻ'); }}><i className="fa-solid fa-share-nodes" /> Chia sẻ</button></div>
                            </article>
                          ))}
                        </div>
                        <button type="button" style={styles.viewAllOrderButton} onClick={() => showToolList('MARKETING', 'DOCUMENT')}>Xem tất cả tài liệu ({documentToolDocuments.length})</button>
                      </>
                    ) : (
                      <div style={styles.largeEmptyState}><i className="fa-regular fa-folder-open" /> Chưa có tài liệu marketing nào.</div>
                    )}
                  </section>
                  <section style={styles.shopPanel}>
                    <h2 style={styles.panelTitle}>Chính sách Hoa hồng</h2>
                    {activePolicyDocument ? (
                      <article style={{ ...styles.toolDocCard, cursor: 'pointer', marginTop: 16 }} role="button" tabIndex={0} onClick={() => openMarketingDocument(activePolicyDocument)} onKeyDown={(event) => { if (event.key === 'Enter' || event.key === ' ') openMarketingDocument(activePolicyDocument); }}>
                        <div style={styles.toolDocPreview}>
                          {activePolicyDocument.thumbnailUrl || /\.(png|jpg|jpeg|gif|webp|svg)(\?|$)/i.test(activePolicyDocument.fileUrl || '')
                            ? <img src={activePolicyDocument.thumbnailUrl || activePolicyDocument.fileUrl} alt={activePolicyDocument.title} style={styles.toolDocPreviewImage} />
                            : <div><i className={getMarketingDocumentIcon(activePolicyDocument.type, activePolicyDocument.fileUrl)} /><span>EVG</span></div>}
                        </div>
                        <div style={styles.toolDocInfo}>
                          <strong>{activePolicyDocument.title}</strong>
                          <span>{getMarketingDocumentTypeName(activePolicyDocument.type)}</span>
                          <small>{getMarketingDocumentSummary(activePolicyDocument)}</small>
                          {activePolicyDetail.timeText ? <small>Thời gian áp dụng: {activePolicyDetail.timeText}</small> : null}
                          {activePolicyDetail.recordFormat ? <small>Ghi nhận: {activePolicyDetail.recordFormat}</small> : null}
                          {activePolicyDetail.payoutTime ? <small>Chi trả: {activePolicyDetail.payoutTime}</small> : null}
                          <small>Cập nhật: {formatDocumentDate(activePolicyDocument.updatedAt || activePolicyDocument.createdAt)}</small>
                        </div>
                        <div style={styles.toolDocActions} data-tool-actions>
                          <button type="button" onClick={(event) => { event.stopPropagation(); openMarketingDocument(activePolicyDocument); }}>
                            <i className="fa-regular fa-eye" /> Xem chi tiết
                          </button>
                          <button type="button" onClick={(event) => { event.stopPropagation(); copyMarketingDocument(activePolicyDocument, 'Đã sao chép chính sách'); }}>
                            <i className="fa-regular fa-copy" /> Sao chép
                          </button>
                          {hasMarketingDocumentFile(activePolicyDocument) ? <button type="button" onClick={(event) => { event.stopPropagation(); openMarketingDocumentFile(activePolicyDocument); }}>
                            <i className="fa-solid fa-download" /> Tải xuống
                          </button> : null}
                        </div>
                      </article>
                    ) : (
                      <div style={styles.largeEmptyState}><i className="fa-regular fa-folder-open" /> Chưa có chính sách hoa hồng nào.</div>
                    )}
                  </section>
                </div>
                <aside style={styles.portalRight}>
                  <section style={styles.shopPanel}>
                    <div style={styles.panelHeader}>
                      <h2 style={styles.panelTitle}>Tài liệu được quan tâm</h2>
                      <button type="button" style={styles.linkButton} onClick={() => showToolList('MARKETING', 'DOCUMENT')}>Xem tất cả</button>
                    </div>
                    {interestedToolDocuments.length ? interestedToolDocuments.map((item, index) => (
                      <div key={item.marketingDocumentId || index} style={{ ...styles.popularDoc, cursor: 'pointer' }} role="button" tabIndex={0} onClick={() => openMarketingDocument(item)} onKeyDown={(event) => { if (event.key === 'Enter' || event.key === ' ') openMarketingDocument(item); }}>
                        <span>{index + 1}</span>
                        <strong>{item.title}</strong>
                        <button type="button" title="Xem chi tiết" onClick={(event) => { event.stopPropagation(); openMarketingDocument(item); }}>
                          <i className="fa-regular fa-eye" />
                        </button>
                      </div>
                    )) : <div style={styles.compactEmptyState}>Chưa có dữ liệu</div>}
                  </section>
                  <section style={styles.shopPanel}>
                    <div style={styles.panelHeader}>
                      <h2 style={styles.panelTitle}>Hướng dẫn sử dụng</h2>
                      <button type="button" style={styles.linkButton} onClick={() => showToolList('GUIDE', 'GUIDE')}>Xem tất cả</button>
                    </div>
                    {guideToolDocuments.length ? guideToolDocuments.map((item) => (
                      <button key={item.marketingDocumentId || item.title} type="button" style={styles.tipItemButton} onClick={() => openMarketingDocument(item)}>
                        <i className={getMarketingDocumentIcon(item.type, item.fileUrl)} />
                        <span>{item.title}</span>
                      </button>
                    )) : <div style={styles.compactEmptyState}>Chưa có hướng dẫn</div>}
                  </section>
                </aside>
              </section>
            </div>
          </section>
        )}

        {activeTab === 'cart' && (
          <section className="dashboard-section animate-fade-in cart-page-container" style={{ padding: '0 24px', flex: 1, display: 'flex', flexDirection: 'column' }}>
            <div className="evg-cart-hero" style={{ ...styles.shopHero, marginBottom: 24, marginTop: 24, padding: 24 }}>
              <div>
                <div style={styles.eyebrow}>GIỎ HÀNG CỦA BẠN</div>
                <h1 style={styles.shopTitle}>{cartItems.length} sản phẩm</h1>
              </div>
            </div>

            <div style={{ ...styles.cartContent, padding: 0, background: 'transparent' }} className="cart-content">
              {cartItems.length === 0 ? (
                <div style={{ ...styles.cartEmpty, background: '#fff', borderRadius: 24, padding: 40 }}>
                  <div style={styles.cartEmptyIconWrap}>
                    <i className="fa-solid fa-cart-arrow-down" style={styles.cartEmptyIcon}></i>
                  </div>
                  <h3 style={styles.cartEmptyTitle}>Giỏ hàng trống</h3>
                  <span>Chọn sản phẩm và bấm “Thêm vào giỏ”.</span>
                  <button type="button" style={{ ...styles.checkoutButton, marginTop: 20, padding: '0 24px', height: 44, fontSize: 15 }} onClick={() => changeTab('commerce')}>
                    Tiếp tục mua sắm
                  </button>
                </div>
              ) : (
                <div className="cart-list-wrapper" style={{ background: '#fff', borderRadius: 24, padding: 24, boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}>
                  <div style={styles.cartTableHeader} className="cart-table-header">
                    <span style={{ flex: '0 0 50px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <button
                        type="button"
                        style={{ border: 0, background: 'transparent', padding: 0, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                        onClick={() => toggleAllCartItems(!allCartChecked)}
                      >
                        <i className={allCartChecked ? "fa-solid fa-square-check" : "fa-regular fa-square"} style={{ color: '#16a34a', fontSize: 18 }} />
                      </button>
                    </span>
                    <span style={{ flex: 4, fontWeight: 500, color: '#64748b', fontSize: 14 }}>Sản phẩm</span>
                    <span style={{ flex: 2, textAlign: 'center', fontWeight: 500, color: '#64748b', fontSize: 14 }}>Đơn giá</span>
                    <span style={{ flex: 2, textAlign: 'center', fontWeight: 500, color: '#64748b', fontSize: 14 }}>Số lượng</span>
                    <span style={{ flex: 2, textAlign: 'center', fontWeight: 500, color: '#64748b', fontSize: 14 }}>Số tiền</span>
                    <span style={{ flex: '0 0 80px', textAlign: 'center', fontWeight: 500, color: '#64748b', fontSize: 14 }}>Thao tác</span>
                  </div>

                  <div style={{ borderTop: '1px solid #f1f5f9', paddingTop: 12 }}>
                    {cartItems.map((item, index) => {
                      const discount = item.product.originalPrice ? Math.round(((item.product.rawOriginalPrice - item.product.rawPrice) / item.product.rawOriginalPrice) * 100) : 0;
                      return (
                        <motion.div 
                          key={item.product.id} 
                          style={{ ...styles.cartItem, borderBottom: '1px solid #f8fafc' }} 
                          className="cart-item-row"
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          exit={{ opacity: 0, scale: 0.95 }}
                          transition={{ delay: index * 0.05, type: "spring", stiffness: 300, damping: 24 }}
                        >
                          <div className="cart-item-content" style={{ display: 'flex', alignItems: 'center', width: '100%', gap: 0, padding: '12px 0' }}>
                            <div className="cart-col-checkbox" style={{ flex: '0 0 50px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                              <button
                                type="button"
                                style={{ border: 0, background: 'transparent', padding: 0, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                onClick={() => toggleCartItemChecked(item.product.id)}
                              >
                                <i className={item.checked !== false ? "fa-solid fa-square-check" : "fa-regular fa-square"} style={{ color: '#16a34a', fontSize: 18 }} />
                              </button>
                            </div>

                            <div className="cart-col-product" style={{ flex: 4, display: 'flex', alignItems: 'center', minWidth: 0, gap: 12 }}>
                              <div className="cart-item-image-wrap" style={{ ...styles.cartItemImageWrap, flexShrink: 0, position: 'relative' }}>

                                {item.product.image ? (
                                  <img src={item.product.image} alt={item.product.name} style={styles.cartItemImage} />
                                ) : (
                                  <div style={styles.cartItemFallback}>EVG</div>
                                )}
                              </div>
                              <div className="cart-item-info" style={{ flex: 1, minWidth: 0 }}>
                                <strong className="cart-item-name" style={{ ...styles.cartItemName, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', display: 'block' }}>{item.product.name}</strong>
                                <span className="cart-item-code" style={{ ...styles.productCode, display: 'block' }}>Mã: {item.product.code}</span>
                              </div>
                            </div>

                            <div className="cart-col-price" style={{ flex: 2, textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                              <span style={{ fontWeight: 600, color: '#ef4444', fontSize: 14 }}>{item.product.price}</span>
                            </div>

                            <div className="cart-col-quantity" style={{ flex: 2, display: 'flex', justifyContent: 'center' }}>
                              <div style={styles.quantityControl}>
                                <button style={styles.quantityButton} type="button" onClick={() => updateCartQuantity(item.product.id, Math.max(1, item.quantity - 1))}>−</button>
                                <input
                                  className="cart-qty-input-no-spin"
                                  style={styles.quantityInput}
                                  type="number"
                                  min="1"
                                  max="100"
                                  value={item.quantity}
                                  onChange={(event) => {
                                    const val = parseInt(event.target.value, 10);
                                    if (!isNaN(val) && val >= 1) {
                                      updateCartQuantity(item.product.id, Math.min(100, val));
                                    } else if (event.target.value === '') {
                                      updateCartQuantity(item.product.id, '');
                                    }
                                  }}
                                  onBlur={(e) => {
                                    if (item.quantity === '' || item.quantity < 1) {
                                      updateCartQuantity(item.product.id, 1);
                                    }
                                  }}
                                />
                                <button style={styles.quantityButton} type="button" onClick={() => updateCartQuantity(item.product.id, Math.min(100, item.quantity + 1))}>+</button>
                              </div>
                            </div>

                            <div className="cart-col-total" style={{ flex: 2, textAlign: 'center', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                              <span style={{ fontWeight: 600, color: '#ef4444', fontSize: 14 }}>
                                {Utils.formatNumber(item.product.rawPrice * item.quantity)}đ
                              </span>
                            </div>

                            <div className="cart-col-action" style={{ flex: '0 0 80px', display: 'flex', justifyContent: 'center' }}>
                              <button
                                type="button"
                                className="cart-remove-btn"
                                style={{ background: 'transparent', border: 0, padding: 8, cursor: 'pointer', color: '#94a3b8', transition: 'color 0.2s' }}
                                onClick={() => removeCartItem(item.product.id)}
                                title="Xóa sản phẩm"
                              >
                                <i className="fa-regular fa-trash-can" style={{ fontSize: 16 }}></i>
                              </button>
                            </div>
                          </div>
                        </motion.div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            {cartItems.length > 0 && (
              <div style={{ ...styles.cartFooter, marginTop: 24, background: '#fff', borderRadius: 24, padding: '24px 32px', boxShadow: '0 4px 20px rgba(0,0,0,0.05)', display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 24 }} className="cart-footer-bar">
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                  <span style={{ color: '#475569', fontSize: 16, fontWeight: 500 }}>
                    Tổng thanh toán ({cartItems.filter((i) => i.checked !== false).length} sản phẩm):
                  </span>
                  <span style={{ color: '#ef4444', fontSize: 28, fontWeight: 700 }}>
                    {Utils.formatNumber(cartVat.total)}đ
                  </span>
                  <small style={{ color: '#64748b', fontSize: 13 }}>
                    (đã gồm VAT {cartVat.percent}%: {Utils.formatNumber(cartVat.vat)}đ)
                  </small>
                </div>
                <div>
                  <button
                    type="button"
                    style={{
                      ...styles.checkoutButton,
                      width: 220,
                      height: 52,
                      fontSize: 18,
                      borderRadius: 12,
                      cursor: cartItems.filter(i => i.checked !== false).length === 0 ? 'not-allowed' : 'pointer',
                      opacity: cartItems.filter(i => i.checked !== false).length === 0 ? 0.6 : 1
                    }}
                    onClick={() => openOrderForm()}
                    disabled={cartItems.filter(i => i.checked !== false).length === 0}
                  >
                    Mua hàng
                  </button>
                </div>
              </div>
            )}
          </section>
        )}

        {activeTab === 'ride' && <CustomerRideTab />}

        {activeTab === 'account' && (
          <section style={styles.commerceScreen} data-account-page>
            <div style={styles.shopHero} data-account-hero>
              <div>
                <div style={styles.eyebrow}>TÀI KHOẢN</div>
                <h1 style={styles.shopTitle}>Hồ sơ CTV/Đối tác</h1>
                <p style={styles.shopDesc}>
                  Cập nhật thông tin cá nhân, eKYC và tài khoản ngân hàng để nhận hoa hồng.
                </p>
              </div>
              <div style={styles.shopHeroActions}>
                <button type="button" style={styles.shopExportButton} onClick={handleCopyLink}>
                  <i className="fa-solid fa-link" /> Copy link
                </button>
                <button
                  type="button"
                  style={{ ...styles.shopPrimaryButton, background: '#16a34a', color: '#fff', boxShadow: '0 14px 28px rgba(22, 163, 74, 0.2)' }}
                  onClick={saveAccountProfile}
                  disabled={savingProfile || loadingProfile}
                >
                  <i className="fa-solid fa-floppy-disk" /> {savingProfile ? 'Đang lưu...' : 'Lưu thông tin'}
                </button>
              </div>
            </div>

            {loadingProfile ? (
              <article style={styles.ordersPanel}>
                <div style={styles.orderEmpty}>Đang tải thông tin tài khoản...</div>
              </article>
            ) : (
              <section style={styles.accountGrid} data-account-profile-grid>
                <article style={{ ...styles.ordersPanel, ...styles.accountPanel }}>
                  <div style={styles.ordersHeader}>
                    <div>
                      <h2 style={styles.panelTitle}>Thông tin cá nhân</h2>
                      <p style={styles.specSub}>Thông tin định danh cơ bản của tài khoản khách hàng/CTV.</p>
                    </div>
                    <span style={profile?.ekycVerified ? styles.successBadge : styles.warningBadge}>
                      {profile?.ekycVerified ? 'Đã eKYC' : 'Chưa eKYC'}
                    </span>
                  </div>

	                  <div style={styles.formGridTwo} data-account-form-grid>
	                    <label style={styles.formField}>
	                      <span style={styles.formLabel}>Họ và tên <RequiredMark /></span>
	                      <input
	                        style={{ ...styles.formInput, ...(accountErrors.fullname ? styles.formInputError : {}) }}
	                        value={accountForm.fullname}
	                        onChange={(event) => updateAccountField('fullname', event.target.value)}
	                        disabled={profile?.ekycVerified === 1}
	                      />
	                      {accountErrors.fullname && <small style={styles.formErrorText}>{accountErrors.fullname}</small>}
	                    </label>
	                    <label style={styles.formField}>
	                      <span style={styles.formLabel}>Số điện thoại <RequiredMark /></span>
	                      <input
	                        style={{ ...styles.formInput, ...(accountErrors.phone ? styles.formInputError : {}) }}
	                        value={accountForm.phone}
	                        onChange={(event) => updateAccountField('phone', event.target.value)}
	                      />
	                      {accountErrors.phone && <small style={styles.formErrorText}>{accountErrors.phone}</small>}
	                    </label>
	                    <label style={styles.formField}>
	                      <span style={styles.formLabel}>Email <RequiredMark /></span>
	                      <input
	                        style={{ ...styles.formInput, ...(accountErrors.email ? styles.formInputError : {}) }}
	                        value={accountForm.email}
	                        onChange={(event) => updateAccountField('email', event.target.value)}
	                      />
	                      {accountErrors.email && <small style={styles.formErrorText}>{accountErrors.email}</small>}
	                    </label>
	                    <label style={styles.formField}>
	                      <span style={styles.formLabel}>Loại giấy tờ <RequiredMark /></span>
	                      <EvgCustomSelect
	                        style={{ height: 49, border: '1px solid #d1d5db', borderRadius: 14, ...(accountErrors.idCardType ? styles.formInputError : {}) }}
	                        value={accountForm.idCardType}
	                        onChange={(event) => updateAccountField('idCardType', event.target.value)}
	                        disabled={profile?.ekycVerified === 1}
                      >
	                        <option value="CC">CCCD/CMND</option>
	                        <option value="PASSPORT">Hộ chiếu</option>
	                      </EvgCustomSelect>
	                      {accountErrors.idCardType && <small style={styles.formErrorText}>{accountErrors.idCardType}</small>}
	                    </label>
                    <label style={styles.formField}>
                      <span style={styles.formLabel}>Số giấy tờ</span>
                      <input
                        style={styles.formInput}
                        value={accountForm.idCardNumber}
                        onChange={(event) => updateAccountField('idCardNumber', event.target.value)}
                        disabled={profile?.ekycVerified === 1}
                      />
                    </label>
                  </div>
                </article>

                <article className="account-bank-panel" style={{ ...styles.ordersPanel, ...styles.accountPanel }}>
                  <div style={styles.ordersHeader}>
                    <div>
                      <h2 style={styles.panelTitle}>Thông tin ngân hàng</h2>
                      <p style={styles.specSub}>Bắt buộc để hệ thống và kế toán trả hoa hồng.</p>
                    </div>
                    <span style={styles.badge}>Bắt buộc</span>
                  </div>

	                  <div style={styles.formGridTwo} data-account-form-grid>
	                    <label style={{ ...styles.formField, gridColumn: '1 / -1' }}>
	                      <span style={styles.formLabel}>Ngân hàng <RequiredMark /></span>
	                      <div className="account-bank-search">
	                        <InputSearchDropdown
	                          inputName="account-bank-code"
	                          selectedValue={accountForm.bankCode}
	                          items={accountBankOptions}
	                          onSelectedValueChanged={(value) => {
	                            updateAccountBank(value);
	                            setAccountBankQuery('');
	                          }}
	                          onSubmitSearch={(text) => {
	                            updateAccountBank('');
	                            setAccountBankQuery(text);
	                          }}
	                          selectedText={selectedAccountBank
	                            ? `${selectedAccountBank.bankName} (${selectedAccountBank.bankCode})`
	                            : accountBankQuery}
	                          disabled={!bankList.length}
	                          enableCheckValid
	                          isTouched={Boolean(accountErrors.bankCode || accountErrors.bankName)}
	                          isValid={!accountErrors.bankCode && !accountErrors.bankName}
	                          feedbackText={accountErrors.bankCode || accountErrors.bankName || ''}
	                          placeholder={bankList.length
	                            ? 'Theo tên ngân hàng, tên rút gọn hoặc mã ngân hàng'
	                            : 'Đang tải danh sách ngân hàng...'}
	                        />
	                      </div>
	                    </label>
	                    <label style={styles.formField}>
	                      <span style={styles.formLabel}>Số tài khoản <RequiredMark /></span>
	                      <input
	                        style={{ ...styles.formInput, ...(accountErrors.bankAccount ? styles.formInputError : {}) }}
	                        value={accountForm.bankAccount}
	                        onChange={(event) => updateAccountField('bankAccount', event.target.value)}
	                        placeholder="Nhập số tài khoản"
	                      />
	                      {accountErrors.bankAccount && <small style={styles.formErrorText}>{accountErrors.bankAccount}</small>}
	                    </label>
	                    <label style={styles.formField}>
	                      <span style={styles.formLabel}>Tên chủ tài khoản <RequiredMark /></span>
	                      <input
	                        style={{ ...styles.formInput, ...(accountErrors.bankBeneficiary ? styles.formInputError : {}) }}
	                        value={accountForm.bankBeneficiary}
	                        onChange={(event) => updateAccountField('bankBeneficiary', event.target.value.toUpperCase())}
	                        placeholder="Tên in hoa không dấu"
	                      />
	                      {accountErrors.bankBeneficiary && <small style={styles.formErrorText}>{accountErrors.bankBeneficiary}</small>}
	                    </label>
                  </div>
                </article>

                <article style={{ ...styles.ordersPanel, ...styles.accountPanel }}>
                  <div style={styles.ordersHeader}>
                    <div>
                      <h2 style={styles.panelTitle}>eKYC</h2>
                      <p style={styles.specSub}>
                        Tải ảnh CCCD/CMND hoặc giấy tờ định danh. Sau khi admin xác thực, họ tên/số giấy tờ sẽ bị khóa.
                      </p>
                    </div>
                    <label style={styles.uploadButton}>
                      {uploadingKyc ? 'Đang upload...' : 'Tải giấy tờ'}
                      <input
                        type="file"
                        accept="image/*,.pdf"
                        onChange={uploadKycDocument}
                        style={{ display: 'none' }}
                        disabled={uploadingKyc || profile?.ekycVerified === 1}
                      />
                    </label>
                  </div>

                  {(accountForm.idCardDocuments || []).length ? (
                    <div style={styles.documentList}>
                      {accountForm.idCardDocuments.map((document, index) => (
                        <div key={`${document}-${index}`} style={styles.documentItem}>
                          <button
                            type="button"
                            style={styles.documentLink}
                            onClick={() => window.open(Utils.getFullUrl(document), '_blank')}
                          >
                            Giấy tờ {index + 1}
                          </button>
                          {profile?.ekycVerified !== 1 && (
                            <button type="button" style={styles.dangerTextButton} onClick={() => removeKycDocument(index)}>
                              Xóa
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div style={styles.orderEmpty}>Chưa có giấy tờ eKYC nào.</div>
                  )}
                </article>

                <article style={{ ...styles.ordersPanel, ...styles.accountPanel }}>
                  <div style={styles.ordersHeader}>
                    <div>
                      <h2 style={styles.panelTitle}>Người giới thiệu</h2>
                      <p style={styles.specSub}>Bổ sung mã giới thiệu (chỉ được điền 1 lần duy nhất).</p>
                    </div>
                  </div>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                    <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-end' }}>
                      <div style={{ ...styles.formField, flex: 1, marginBottom: 0 }}>
                        <label style={{ display: 'block', marginBottom: 8 }}>
                          <span style={styles.formLabel}>Mã người giới thiệu (Mã/SĐT/Email)</span>
                        </label>
                        <div style={{ position: 'relative' }}>
                          <input
                            style={{ ...styles.formInput, paddingRight: !profile?.referredById ? '120px' : '16px' }}
                            value={referralInput}
                            onChange={(e) => {
                              if (!profile?.referredById) {
                                setReferralInput(e.target.value);
                                setVerifiedReferrer(null);
                              }
                            }}
                            disabled={!!profile?.referredById}
                            placeholder={profile?.referredById ? "Đã liên kết mã giới thiệu" : "Nhập mã, số điện thoại hoặc email người giới thiệu"}
                          />
                          {!profile?.referredById && (
                              <button
                              type="button"
                              className="referral-check-btn"
                              style={{
                                ...styles.softButton,
                                position: 'absolute',
                                right: '4px',
                                top: '50%',
                                transform: 'translateY(-50%)',
                                height: '38px',
                                padding: '0 16px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                whiteSpace: 'nowrap',
                                background: referralInput.trim() ? '#10b981' : '#f8fafc',
                                color: referralInput.trim() ? '#ffffff' : '#64748b',
                                fontWeight: 700,
                                cursor: referralInput.trim() ? 'pointer' : 'default',
                                pointerEvents: referralInput.trim() ? 'auto' : 'none',
                                transition: 'all 0.2s ease',
                              }}
                              onClick={handleVerifyReferral}
                              disabled={verifyingReferral || !referralInput.trim()}
                            >
                              {verifyingReferral ? 'Đang kiểm tra...' : 'Kiểm tra'}
                            </button>
                          )}
                        </div>
                      </div>
                    </div>

                    {verifiedReferrer && (
                      <div
                        style={{
                          backgroundColor: profile?.referredById ? '#f3f4f6' : '#ecfdf5',
                          border: profile?.referredById ? '1px solid #e5e7eb' : '1px solid #a7f3d0',
                          padding: '12px 16px',
                          borderRadius: '8px',
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '4px'
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <span style={{ fontSize: '14px', fontWeight: 500, color: profile?.referredById ? '#374151' : '#047857' }}>
                            {profile?.referredById ? 'Người giới thiệu của bạn:' : 'Người giới thiệu hợp lệ:'}
                          </span>
                          <span style={{ fontSize: '14px', fontWeight: 500, color: profile?.referredById ? '#111827' : '#065f46' }}>
                            {verifiedReferrer.fullname}
                          </span>
                        </div>
                        <div style={{ fontSize: '13px', color: profile?.referredById ? '#6b7280' : '#047857' }}>
                          Mã: <strong style={{ fontFamily: 'monospace' }}>{verifiedReferrer.userCode}</strong>
                          {verifiedReferrer.phone && ` - SĐT: ${verifiedReferrer.phone}`}
                          {verifiedReferrer.email && ` - Email: ${verifiedReferrer.email}`}
                        </div>
                        {profile?.referredById && (
                          <div style={{ fontSize: '12px', color: '#9ca3af', marginTop: '4px', fontStyle: 'italic' }}>
                            * Mã giới thiệu đã được áp dụng và không thể thay đổi.
                          </div>
                        )}
                      </div>
                    )}

                    {!profile?.referredById && verifiedReferrer && (
                      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <button
                          type="button"
                          style={{
                            ...styles.productButton,
                            backgroundColor: '#10b981',
                            padding: '10px 24px',
                            fontSize: '14px',
                            fontWeight: 500
                          }}
                          onClick={handleApplyReferral}
                          disabled={applyingReferral}
                        >
                          {applyingReferral ? 'Đang áp dụng...' : 'Áp dụng mã giới thiệu'}
                        </button>
                      </div>
                    )}
                  </div>
                </article>
              </section>
            )}
          </section>
        )}
          </div>
        </section>
      </main>

      {walletNotice && (
        <div style={styles.modalBackdrop} onClick={() => setWalletNotice(null)}>
          <div style={styles.walletNoticeModal} onClick={(event) => event.stopPropagation()}>
            <button type="button" style={{ ...styles.insightCloseButton, ...styles.walletNoticeCloseButton }} onClick={() => setWalletNotice(null)}>×</button>
            <div style={{
              ...styles.walletNoticeIcon,
              ...(walletNotice.type === 'success' ? styles.walletNoticeIconSuccess : {}),
            }}>
              <i className={walletNotice.type === 'success' ? 'fa-solid fa-check' : 'fa-solid fa-circle-exclamation'} />
            </div>
            <h2 style={styles.walletNoticeTitle}>{walletNotice.title}</h2>
            <p style={styles.walletNoticeMessage}>{walletNotice.message}</p>
            <button type="button" style={styles.walletNoticeButton} onClick={() => setWalletNotice(null)}>
              Đã hiểu
            </button>
          </div>
        </div>
      )}

      {showQR && (
        <div style={styles.modalBackdrop} onClick={closeReferralQR}>
          <div style={styles.referralQRModal} onClick={(event) => event.stopPropagation()}>
            <button type="button" style={styles.referralQRCloseButton} onClick={closeReferralQR}>×</button>
            <div style={styles.referralQRHeader}>
              <span style={styles.referralQRBadge}>
                <i className="fa-solid fa-qrcode" /> Mã giới thiệu
              </span>
              <h2 style={styles.referralQRTitle}>Chia sẻ liên kết đối tác</h2>
              <p style={styles.referralQRDesc}>Quét mã để mở trang đăng ký kèm mã giới thiệu của bạn.</p>
            </div>
            <div style={styles.referralQRBox}>
              <QRCode value={affiliateLink} size={220} />
            </div>
            <div style={styles.referralQRInfo}>
              <span>Mã giới thiệu</span>
              <strong>{affiliateCode}</strong>
            </div>
            <div style={styles.referralQRActions}>
              <button type="button" style={styles.marketingDocSecondaryButton} onClick={handleCopyLink}>
                <i className="fa-regular fa-copy" /> Sao chép link
              </button>
              <button type="button" style={styles.marketingDocPrimaryButton} onClick={closeReferralQR}>
                Đóng
              </button>
            </div>
          </div>
        </div>
      )}

      {selectedInsight && (
        <div style={styles.modalBackdrop} onClick={() => setSelectedInsightKey(null)}>
          <div style={styles.insightModal} onClick={(event) => event.stopPropagation()}>
            <div style={styles.insightModalHeader}>
              <div>
                <h2 style={styles.insightModalTitle}>{selectedInsight.title}</h2>
                <p style={styles.insightModalDescription}>{selectedInsight.description}</p>
              </div>
              <button type="button" style={styles.insightCloseButton} onClick={() => setSelectedInsightKey(null)}>×</button>
            </div>
            <div style={styles.insightSummaryBand}>
              <span>{selectedInsight.totalLabel}</span>
              <strong>{selectedInsight.isCount ? selectedInsight.totalValue : formatCurrency(selectedInsight.totalValue)}</strong>
            </div>
            <div style={styles.insightTable}>
              <div style={styles.insightTableHead}>
                <span>Nội dung</span>
                <span>Ngày</span>
                <span>Trạng thái</span>
                <span>{selectedInsight.isCount ? 'Giá trị' : 'Số tiền'}</span>
              </div>
              {selectedInsight.rows.map((row) => (
                <div key={row.id} style={styles.insightTableRow}>
                  <span>
                    <strong>{row.title}</strong>
                    <small>{row.subtitle}</small>
                  </span>
                  <span>{formatNotificationTime(row.time) || '-'}</span>
                  <span>
                    <em style={{
                      ...styles.statusBadge,
                      ...(['ORDERED', 'PENDING'].includes(String(row.status || '').toUpperCase()) ? styles.pendingStatusBadge : {}),
                      ...(['SHIPPING', 'PROCESSING', 'INSTALLING', 'INSTALLTING'].includes(String(row.status || '').toUpperCase()) ? styles.processingStatusBadge : {}),
                      ...(['PAID', 'COMPLETED'].includes(String(row.status || '').toUpperCase()) ? styles.paidStatusBadge : {}),
                      ...(['CANCELLED', 'REJECTED'].includes(String(row.status || '').toUpperCase()) ? styles.cancelledStatusBadge : {}),
                    }}>
                      {getOrderStatusName(row.status)}
                    </em>
                  </span>
                  <strong>{formatCurrency(row.amount)}</strong>
                </div>
              ))}
              {!selectedInsight.rows.length && (
                <div style={styles.insightEmptyState}>
                  <i className="fa-regular fa-folder-open" />
                  <strong>{selectedInsight.emptyText}</strong>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {selectedMarketingDocument && (() => {
        const detail = selectedMarketingDocumentDetail || {};
        const docType = String(selectedMarketingDocument.type || '').toUpperCase();
        const isPolicy = docType === 'POLICY';
        const isGuide = docType === 'GUIDE';
        const detailMeta = [
          ['Loại nội dung', getMarketingDocumentTypeName(selectedMarketingDocument.type)],
          ['Mã/Tệp', detail.campaignCode || detail.guideCode || selectedMarketingDocument.fileName || '-'],
          ['Cập nhật', formatDocumentDate(selectedMarketingDocument.updatedAt || selectedMarketingDocument.createdAt)],
          ['Trạng thái', detail.statusText || 'Đang hiển thị'],
          ['Đối tượng', detail.applyTarget],
          [isPolicy ? 'Thời gian áp dụng' : 'Thời lượng', detail.timeText],
          [isPolicy ? 'Cách ghi nhận' : 'Mục tiêu', detail.recordFormat || detail.goal],
          [isPolicy ? 'Chi trả' : 'Khi nên dùng', detail.payoutTime || detail.useCase],
        ].filter(([, value]) => value);
        const firstListTitle = isGuide ? 'Các bước thực hiện' : 'Điều kiện áp dụng';
        const secondListTitle = isGuide ? 'Mẹo sử dụng / gợi ý' : 'Cơ chế ghi nhận & chi trả';
        const firstList = normalizeList(isGuide ? detail.steps : detail.conditions);
        const secondList = normalizeList(isGuide ? (detail.tips || detail.mechanisms) : detail.mechanisms);
        const notes = normalizeList(detail.notes);
        const previewImage = selectedMarketingDocument.thumbnailUrl || (
          /\.(png|jpg|jpeg|gif|webp|svg)(\?|$)/i.test(selectedMarketingDocument.fileUrl || '')
            ? selectedMarketingDocument.fileUrl
            : ''
        );
        const summaryText = detail.plainDescription || detail.summary || detail.content || selectedMarketingDocument.description;

        return (
          <div style={styles.modalBackdrop} onClick={() => setSelectedMarketingDocument(null)}>
            <div style={styles.marketingDocModal} onClick={(event) => event.stopPropagation()}>
              <div style={styles.marketingDocModalHeader}>
                <div style={styles.marketingDocHeading}>
                  <span style={styles.marketingDocTypeBadge}>{getMarketingDocumentTypeName(selectedMarketingDocument.type)}</span>
                  <h2 style={styles.insightModalTitle}>{selectedMarketingDocument.title}</h2>
                  <p style={styles.insightModalDescription}>{getMarketingDocumentSummary(selectedMarketingDocument)}</p>
                </div>
                <button type="button" style={styles.insightCloseButton} onClick={() => setSelectedMarketingDocument(null)}>×</button>
              </div>

              <div className="marketing-doc-modal-body" style={styles.marketingDocModalBody}>
                <aside style={styles.marketingDocPreviewPanel}>
                  <div style={styles.marketingDocPreview}>
                    {previewImage ? (
                      <img src={previewImage} alt={selectedMarketingDocument.title} style={styles.marketingDocPreviewImage} />
                    ) : (
                      <div style={styles.marketingDocPreviewIcon}>
                        <i className={getMarketingDocumentIcon(selectedMarketingDocument.type, selectedMarketingDocument.fileUrl)} />
                        <span>EVG</span>
                      </div>
                    )}
                  </div>
                  {hasMarketingDocumentFile(selectedMarketingDocument) ? (
                    <button type="button" style={styles.marketingDocOpenButton} onClick={() => openMarketingDocumentFile(selectedMarketingDocument)}>
                      <i className="fa-solid fa-arrow-up-right-from-square" /> Mở file
                    </button>
                  ) : null}
                </aside>

                <div style={styles.marketingDocDetailPanel}>
                  <div style={styles.marketingDocMetaGrid}>
                    {detailMeta.map(([label, value]) => (
                      <div key={label} style={styles.marketingDocMetaItem}>
                        <span>{label}</span>
                        <strong>{value}</strong>
                      </div>
                    ))}
                  </div>

                  {summaryText ? (
                    <section style={styles.marketingDocSection}>
                      <h3>Tóm tắt nội dung</h3>
                      <p>{summaryText}</p>
                    </section>
                  ) : null}

                  {firstList.length ? (
                    <section style={styles.marketingDocSection}>
                      <h3>{firstListTitle}</h3>
                      <ul style={styles.marketingDocList}>
                        {firstList.map((item, index) => <li key={`${firstListTitle}-${index}`}>{item}</li>)}
                      </ul>
                    </section>
                  ) : null}

                  {secondList.length ? (
                    <section style={styles.marketingDocSection}>
                      <h3>{secondListTitle}</h3>
                      <ul style={styles.marketingDocList}>
                        {secondList.map((item, index) => <li key={`${secondListTitle}-${index}`}>{item}</li>)}
                      </ul>
                    </section>
                  ) : null}

                  {notes.length ? (
                    <section style={styles.marketingDocSection}>
                      <h3>Lưu ý</h3>
                      <ul style={styles.marketingDocList}>
                        {notes.map((item, index) => <li key={`note-${index}`}>{item}</li>)}
                      </ul>
                    </section>
                  ) : null}

                  {(detail.supportHotline || detail.supportEmail) ? (
                    <section style={styles.marketingDocSection}>
                      <h3>Hỗ trợ</h3>
                      <p>{[detail.supportHotline, detail.supportEmail].filter(Boolean).join(' · ')}</p>
                    </section>
                  ) : null}

                  {!detailMeta.length && !summaryText ? (
                    <div style={styles.compactEmptyState}>Tài liệu này chưa có nội dung mô tả.</div>
                  ) : null}
                </div>
              </div>

              <div style={styles.marketingDocActionRow}>
                <button type="button" style={styles.marketingDocSecondaryButton} onClick={() => copyMarketingDocument(selectedMarketingDocument)}>
                  <i className="fa-regular fa-copy" /> Sao chép nội dung
                </button>
                {hasMarketingDocumentFile(selectedMarketingDocument) ? (
                  <button type="button" style={styles.marketingDocPrimaryButton} onClick={() => openMarketingDocumentFile(selectedMarketingDocument)}>
                    <i className="fa-solid fa-arrow-up-right-from-square" /> Xem tài liệu
                  </button>
                ) : null}
              </div>
            </div>
          </div>
        );
      })()}


      <AnimatePresence>
      {showCheckout && (
        <motion.div 
          style={styles.modalBackdrop} 
          onClick={() => setShowCheckout(false)}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
        >
          <motion.div 
            style={styles.orderModal} 
            onClick={(event) => event.stopPropagation()}
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            transition={{ type: "spring", stiffness: 300, damping: 25, mass: 0.8 }}
          >
            <div style={styles.modalHeader}>
              <div>
                <div style={styles.eyebrow}>Thanh toán</div>
                <h2 style={styles.modalTitle}>Xác nhận thông tin nhận hàng</h2>
              </div>
              <button type="button" style={styles.closeButton} onClick={() => setShowCheckout(false)}>
                ×
              </button>
            </div>
            <div style={styles.orderForm}>
              <div style={styles.checkoutSummary}>
                <div style={styles.checkoutSummaryTitle}>
                  <span>Sản phẩm trong đơn</span>
                  <strong>{Utils.formatNumber(cartTotal)}đ</strong>
                </div>
                {cartItems.filter((item) => item.checked !== false).map((item) => (
                  <div key={item.product.id} style={styles.checkoutSummaryRow}>
                    <span>{item.product.name} × {item.quantity}</span>
                    <strong>{Utils.formatNumber(item.product.rawPrice * item.quantity)}đ</strong>
                  </div>
                ))}
              </div>

              <div style={styles.checkoutSectionTitle}>Thông tin người nhận</div>
              <div style={styles.checkoutFieldGrid}>
                <label style={styles.checkoutField}>
                  <span>Tên người mua *</span>
                  <input
                    style={styles.formInput}
                    value={orderForm.buyerName}
                    onChange={(event) => setOrderForm({ ...orderForm, buyerName: event.target.value })}
                    placeholder="VD: Anh B - Chủ quán Cafe"
                  />
                </label>

                <label style={styles.checkoutField}>
                  <span>Số điện thoại *</span>
                  <input
                    style={styles.formInput}
                    value={orderForm.buyerPhone}
                    onChange={(event) => setOrderForm({ ...orderForm, buyerPhone: event.target.value })}
                    placeholder="Nhập số điện thoại người mua"
                  />
                </label>
              </div>

              <label style={styles.checkoutField}>
                <span>Địa chỉ giao/lắp đặt</span>
                <input
                  style={styles.formInput}
                  value={orderForm.shippingAddress}
                  onChange={(event) => setOrderForm({ ...orderForm, shippingAddress: event.target.value })}
                  placeholder="Nhập địa chỉ nhận hàng hoặc địa điểm lắp đặt"
                />
              </label>

              <label style={styles.checkoutField}>
                <span>Ghi chú</span>
                <textarea
                  style={{ ...styles.formInput, minHeight: 86, resize: 'vertical' }}
                  value={orderForm.note}
                  onChange={(event) => setOrderForm({ ...orderForm, note: event.target.value })}
                  placeholder="Thông tin thêm cho admin/kế toán"
                />
              </label>

              <div style={styles.checkoutSectionTitle}>Hóa đơn điện tử</div>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#334155', fontSize: 14, cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={Boolean(orderForm.invoiceCompany)}
                  onChange={(event) => setOrderForm({ ...orderForm, invoiceCompany: event.target.checked })}
                />
                <span>Xuất hóa đơn cho công ty (nhập mã số thuế)</span>
              </label>
              <small style={{ color: '#64748b' }}>
                {orderForm.invoiceCompany
                  ? 'Hóa đơn VAT sẽ do admin xuất theo thông tin công ty bên dưới (bắt buộc có mã số thuế). Vui lòng liên hệ admin để nhận hóa đơn sau khi thanh toán.'
                  : 'Khách lẻ không cần mã số thuế — không tích mục này. Hóa đơn (nếu cần) do admin xuất; vui lòng liên hệ admin để nhận sau khi thanh toán.'}
              </small>
              {orderForm.invoiceCompany && (
                <div style={styles.checkoutFieldGrid}>
                  <label style={styles.checkoutField}>
                    <span>Tên đơn vị *</span>
                    <input style={styles.formInput} value={orderForm.invoiceCompanyName}
                      onChange={(event) => setOrderForm({ ...orderForm, invoiceCompanyName: event.target.value })}
                      placeholder="VD: Công ty TNHH ABC" />
                  </label>
                  <label style={styles.checkoutField}>
                    <span>Mã số thuế *</span>
                    <input style={styles.formInput} value={orderForm.invoiceTaxCode}
                      onChange={(event) => setOrderForm({ ...orderForm, invoiceTaxCode: event.target.value })}
                      placeholder="10 hoặc 13 chữ số" inputMode="numeric" />
                  </label>
                  <label style={styles.checkoutField}>
                    <span>Địa chỉ đơn vị</span>
                    <input style={styles.formInput} value={orderForm.invoiceAddress}
                      onChange={(event) => setOrderForm({ ...orderForm, invoiceAddress: event.target.value })}
                      placeholder="Địa chỉ ghi trên hóa đơn" />
                  </label>
                  <label style={styles.checkoutField}>
                    <span>Email nhận hóa đơn</span>
                    <input style={styles.formInput} value={orderForm.invoiceEmail}
                      onChange={(event) => setOrderForm({ ...orderForm, invoiceEmail: event.target.value })}
                      placeholder="email@congty.vn" />
                  </label>
                </div>
              )}

              <div style={styles.paymentMethodBlock}>
                <div style={styles.paymentMethodHeader}>
                  <span>Phương thức thanh toán</span>
                  <small>Hỗ trợ VNPAY, MoMo, chuyển khoản và tiền mặt</small>
                </div>
                <div style={styles.paymentMethodGrid}>
                  {productPaymentMethods.map((method) => {
                    const isSelected = orderForm.paymentMethod === method.value;
                    return (
                      <button
                        key={method.value}
                        type="button"
                        style={{
                          ...styles.paymentMethodCard,
                          ...(isSelected ? styles.paymentMethodCardActive : {}),
                          ...(!method.available ? styles.paymentMethodCardDisabled : {}),
                        }}
                        disabled={!method.available}
                        onClick={() => setOrderForm({ ...orderForm, paymentMethod: method.value })}
                      >
                        <span style={styles.paymentMethodIcon}>
                          {method.logo ? (
                            <img src={method.logo} alt={method.label} style={styles.paymentMethodLogo} />
                          ) : (
                            <i className={`fa-solid ${method.icon}`} />
                          )}
                        </span>
                        <span style={styles.paymentMethodContent}>
                          <strong>{method.label}</strong>
                          <small>{method.description}</small>
                        </span>
                        {isSelected && <i className="fa-solid fa-circle-check" style={styles.paymentMethodCheck} />}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div style={styles.checkoutSummary}>
                <div style={styles.checkoutSummaryRow}>
                  <span>Tạm tính (chưa VAT)</span>
                  <strong>{Utils.formatNumber(cartVat.beforeTax)}đ</strong>
                </div>
                <div style={styles.checkoutSummaryRow}>
                  <span>Thuế GTGT (VAT {cartVat.percent}%)</span>
                  <strong>{Utils.formatNumber(cartVat.vat)}đ</strong>
                </div>
              </div>
              <div style={styles.orderTotal}>
                <span>Tổng thanh toán</span>
                <strong>{Utils.formatNumber(cartVat.total)}đ</strong>
              </div>

              <button type="button" style={styles.productButton} onClick={submitOrder} disabled={submittingOrder}>
                {getProductPaymentSubmitLabel(orderForm.paymentMethod, submittingOrder)}
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
      </AnimatePresence>

      <AnimatePresence>
      {productPaymentQR && (
        <motion.div 
          style={styles.modalBackdrop} 
          onClick={() => setProductPaymentQR(null)}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
        >
          <motion.div 
            style={styles.paymentQRModal} 
            onClick={(event) => event.stopPropagation()}
            initial={{ scale: 0.9, opacity: 0, y: 50 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.9, opacity: 0, y: 50 }}
            transition={{ type: "spring", stiffness: 300, damping: 25 }}
          >
            <div style={styles.modalHeader}>
              <div>
                <div style={styles.eyebrow}>Thanh toán QR</div>
                <h2 style={styles.modalTitle}>Quét mã để thanh toán đơn hàng</h2>
              </div>
              <button type="button" style={styles.closeButton} onClick={() => setProductPaymentQR(null)}>
                ×
              </button>
            </div>

            <div style={styles.paymentQRContent}>
              <div style={styles.paymentQRBox}>
                <QRCode
                  value={productPaymentQR.qrData?.qrCode || productPaymentQR.qrData?.checkoutUrl || ''}
                  size={220}
                />
              </div>
              <div style={styles.paymentInfoList}>
                <div style={styles.paymentInfoRow}>
                  <span>Phương thức</span>
                  <strong>{getProductPaymentMethodLabel(productPaymentQR.qrData?.paymentMethod || productPaymentQR.payment?.paymentMethod)}</strong>
                </div>
                <div style={styles.paymentInfoRow}>
                  <span>Số tiền</span>
                  <strong>{Utils.formatNumber(productPaymentQR.qrData?.amount || productPaymentQR.payment?.amount || 0)}đ</strong>
                </div>
                <div style={styles.paymentInfoRow}>
                  <span>Nội dung</span>
                  <strong>{productPaymentQR.qrData?.description || `PRODUCT ORDER ${productPaymentQR.order?.saleId}`}</strong>
                </div>
                {productPaymentQR.qrData?.accountNumber && (
                  <div style={styles.paymentInfoRow}>
                    <span>Tài khoản</span>
                    <strong>{productPaymentQR.qrData.accountNumber}</strong>
                  </div>
                )}
                {productPaymentQR.qrData?.accountName && (
                  <div style={styles.paymentInfoRow}>
                    <span>Chủ tài khoản</span>
                    <strong>{productPaymentQR.qrData.accountName}</strong>
                  </div>
                )}
                <div style={styles.paymentQRNote}>
                  Sau khi ngân hàng xác nhận thanh toán, trạng thái đơn sẽ tự cập nhật.
                </div>
              </div>
            </div>

            <div style={styles.paymentQRActions}>
              <button
                type="button"
                style={styles.secondaryButton}
                disabled={loadingPaymentQR}
                onClick={() => openOrderPaymentQR({
                  id: productPaymentQR.order?.saleId,
                  status: productPaymentQR.order?.status,
                  sourceOrder: productPaymentQR.order,
                })}
              >
                {loadingPaymentQR ? 'Đang kiểm tra...' : 'Kiểm tra thanh toán'}
              </button>
              <button type="button" style={styles.productButton} onClick={() => setProductPaymentQR(null)}>
                Đóng
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
      </AnimatePresence>
      <AnimatePresence>
        {topupWalletScope && (
          <div
            className="customer-topup-backdrop"
            onClick={closeWalletTopup}
          >
            <div
              className="customer-topup-modal"
              onClick={(event) => event.stopPropagation()}
            >
              <div className="customer-topup-header">
                <div>
                  <span className="customer-topup-eyebrow">
                    {topupWalletResult?.qrData ? 'Thông tin thanh toán' : 'Nạp tiền vào ví'}
                  </span>
                  <h2 className="customer-topup-title">
                    {topupWalletScope === 'RIDE' ? 'Nạp Ví Xe ghép' : 'Nạp Ví EVG'}
                  </h2>
                </div>
                <button
                  type="button"
                  className="customer-topup-close"
                  onClick={closeWalletTopup}
                  aria-label="Đóng"
                >
                  <i className="fa-solid fa-xmark" />
                </button>
              </div>

              <div className="customer-topup-body">
                {!topupWalletResult && (
                  <>
                    <div className="customer-topup-balance-card">
                      <img src={greenWalletIcon} alt="Wallet" className="customer-topup-wallet-icon" />
                      <div className="customer-topup-balance-info">
                        <span className="customer-topup-balance-label">
                          Số dư {topupWalletScope === 'RIDE' ? 'Ví Xe ghép' : 'Ví EVG'} hiện tại
                        </span>
                        <strong className="customer-topup-balance-value">
                          {topupWalletScope === 'RIDE' ? rideWalletBalance : evgWalletBalance}
                        </strong>
                      </div>
                    </div>

                    <div className="customer-topup-field">
                      <label className="customer-topup-label">Số tiền nạp (VNĐ)</label>
                      <div className="customer-topup-input-box">
                        <input
                          type="text"
                          inputMode="numeric"
                          placeholder="100.000"
                          value={String(topupWalletAmount).replace(/\B(?=(\d{3})+(?!\d))/g, '.')}
                          onChange={(event) => setTopupWalletAmount(event.target.value.replace(/[^0-9]/g, ''))}
                          className="customer-topup-input"
                          aria-label="Số tiền nạp"
                        />
                        <span className="customer-topup-currency-tag">VNĐ</span>
                      </div>
                      <small className="customer-topup-hint">
                        Số tiền nạp tối thiểu là 100.000đ
                      </small>
                    </div>

                    <div className="customer-topup-presets">
                      {[100000, 200000, 300000, 500000, 1000000, 2000000].map((amount) => {
                        const isSelected = Number(topupWalletAmount) === amount;
                        return (
                          <button
                            key={amount}
                            type="button"
                            className={`customer-topup-preset-btn ${isSelected ? 'selected' : ''}`}
                            onClick={() => setTopupWalletAmount(String(amount))}
                          >
                            {formatCurrency(amount)}
                          </button>
                        );
                      })}
                    </div>

                    <div className="customer-topup-methods">
                      {(topupWalletScope === 'RIDE'
                        ? walletTopupPaymentMethods.filter((method) => method.value === 'BIDV')
                        : walletTopupPaymentMethods
                      ).map((method) => {
                        const selected = (topupWalletScope === 'RIDE' ? 'BIDV' : topupPaymentMethod) === method.value;
                        return (
                          <button
                            key={method.value}
                            type="button"
                            className={`customer-topup-method-card ${selected ? 'selected' : ''}`}
                            onClick={() => setTopupPaymentMethod(method.value)}
                            aria-pressed={selected}
                          >
                            <div className="customer-topup-method-icon">
                              <img src={method.logo} alt={method.label} />
                            </div>
                            <div className="customer-topup-method-details">
                              <div className="customer-topup-method-title">{method.label}</div>
                              <div className="customer-topup-method-sub">
                                {method.value === 'BIDV'
                                  ? `Quét mã QR BIDV để cộng tiền đúng vào ${topupWalletScope === 'RIDE' ? 'Ví Xe ghép' : 'Ví EVG'}.`
                                  : method.description}
                              </div>
                            </div>
                            <span className={`customer-topup-method-radio ${selected ? 'selected' : ''}`} aria-hidden="true" />
                          </button>
                        );
                      })}
                    </div>

                    <div className="customer-topup-total-row">
                      <span>Tổng thanh toán:</span>
                      <strong>{formatCurrency(Number(topupWalletAmount) || 0)}</strong>
                    </div>

                    {topupWalletError && (
                      <div className="customer-topup-error">
                        <i className="fa-solid fa-circle-exclamation" />
                        <span>{topupWalletError}</span>
                      </div>
                    )}

                    <button
                      type="button"
                      className="customer-topup-submit-btn"
                      onClick={createWalletTopup}
                      disabled={topupWalletState === 'loading'}
                    >
                      {topupWalletState === 'loading' ? (
                        <>
                          <i className="fa-solid fa-spinner fa-spin" />
                          <span>{getWalletTopupSubmitLabel(topupPaymentMethod, true)}</span>
                        </>
                      ) : (
                        <>
                          <i className={`fa-solid ${topupPaymentMethod === 'BIDV' ? 'fa-qrcode' : 'fa-arrow-up-right-from-square'}`} />
                          <span>{getWalletTopupSubmitLabel(topupPaymentMethod, false)}</span>
                        </>
                      )}
                    </button>
                  </>
                )}

                {topupWalletResult?.qrData && (
                  <div className="customer-topup-qr-view">
                    <div className="customer-topup-qr-card">
                      <div className="customer-topup-qr-wrapper">
                        <QRCode value={String(topupWalletResult.qrData.qrCode)} size={200} />
                      </div>
                      <div className="customer-topup-qr-amount">
                        <span>Số tiền cần thanh toán</span>
                        <strong>{formatCurrency(topupWalletResult.qrData.amount)}</strong>
                      </div>
                      <button
                        type="button"
                        className="customer-topup-download-btn"
                        onClick={handleDownloadQrCode}
                      >
                        <i className="fa-solid fa-arrow-down-to-line" />
                        <span>Tải mã QR về máy</span>
                      </button>
                    </div>

                    <div className="customer-topup-instruction-card">
                      <img src={paymentInstructionImg} alt="Hướng dẫn" className="instruction-icon" />
                      <div className="instruction-text">
                        <div className="instruction-title">Hướng dẫn thanh toán</div>
                        <div className="instruction-desc">
                          Mở ứng dụng Ngân hàng (hoặc app BIDV SmartBanking), chọn <strong>Quét mã QR</strong> để thanh toán nhanh hoặc chuyển khoản theo thông tin bên dưới.
                        </div>
                      </div>
                    </div>

                    <div className="customer-topup-bank-card">
                      <div className="customer-topup-bank-card-header">
                        <span className="card-title">Thông tin chuyển khoản</span>
                        <span className="status-badge pending">
                          <span className="status-dot" /> Chờ thanh toán
                        </span>
                      </div>

                      <div className="customer-topup-copy-list">
                        <div className="customer-topup-copy-item">
                          <div className="copy-item-meta">
                            <span className="copy-item-label">Ví nhận tiền</span>
                            <div className="copy-item-value">
                              <span className="wallet-badge">
                                {topupWalletScope === 'RIDE' ? 'Ví Xe ghép' : 'Ví EVG'}
                              </span>
                            </div>
                          </div>
                        </div>

                        <div className="customer-topup-copy-item">
                          <div className="copy-item-meta">
                            <span className="copy-item-label">Ngân hàng nhận</span>
                            <div className="copy-item-value bank-value">
                              <img src={bidvLogoIcon} alt="BIDV" className="bank-logo-mini" />
                              <span>BIDV (Ngân hàng TMCP Đầu tư & Phát triển VN)</span>
                            </div>
                          </div>
                        </div>

                        {topupWalletResult.qrData.accountName && (
                          <div className="customer-topup-copy-item">
                            <div className="copy-item-meta">
                              <span className="copy-item-label">Chủ tài khoản</span>
                              <div className="copy-item-value bold uppercase">
                                {topupWalletResult.qrData.accountName}
                              </div>
                            </div>
                          </div>
                        )}

                        {topupWalletResult.qrData.accountNumber && (
                          <div className="customer-topup-copy-item">
                            <div className="copy-item-meta">
                              <span className="copy-item-label">Số tài khoản</span>
                              <div className="copy-item-value account-number">
                                {topupWalletResult.qrData.accountNumber}
                              </div>
                            </div>
                            <button
                              type="button"
                              className="copy-btn"
                              onClick={() => handleCopyToClipboard(topupWalletResult.qrData.accountNumber, 'Số tài khoản')}
                            >
                              <i className="fa-regular fa-copy" />
                              <span>Sao chép</span>
                            </button>
                          </div>
                        )}

                        <div className="customer-topup-copy-item">
                          <div className="copy-item-meta">
                            <span className="copy-item-label">Số tiền</span>
                            <div className="copy-item-value amount-number">
                              {formatCurrency(topupWalletResult.qrData.amount)}
                            </div>
                          </div>
                          <button
                            type="button"
                            className="copy-btn"
                            onClick={() => handleCopyToClipboard(String(topupWalletResult.qrData.amount), 'Số tiền')}
                          >
                            <i className="fa-regular fa-copy" />
                            <span>Sao chép</span>
                          </button>
                        </div>

                        <div className="customer-topup-copy-item highlight">
                          <div className="copy-item-meta">
                            <span className="copy-item-label">Nội dung chuyển khoản (bắt buộc)</span>
                            <div className="copy-item-value transfer-code">
                              {topupWalletResult.qrData.description || '-'}
                            </div>
                          </div>
                          {topupWalletResult.qrData.description && (
                            <button
                              type="button"
                              className="copy-btn primary"
                              onClick={() => handleCopyToClipboard(topupWalletResult.qrData.description, 'Nội dung chuyển khoản')}
                            >
                              <i className="fa-regular fa-copy" />
                              <span>Sao chép</span>
                            </button>
                          )}
                        </div>
                      </div>

                      <div className="customer-topup-card-footer-tip">
                        Vui lòng nhập chính xác <strong>Số tiền</strong> và <strong>Nội dung chuyển khoản</strong> khi thao tác để hệ thống tự động cộng tiền vào ví ngay lập tức.
                      </div>
                    </div>

                    <div className="customer-topup-qr-actions">
                      <button
                        type="button"
                        className="customer-topup-submit-btn"
                        onClick={async () => {
                          await loadProfile();
                          ToastHelper.showSuccess('Đã cập nhật số dư ví');
                        }}
                      >
                        <i className="fa-solid fa-rotate-right" />
                        <span>Tôi đã chuyển tiền, cập nhật số dư</span>
                      </button>
                      <button
                        type="button"
                        className="customer-topup-secondary-btn"
                        onClick={closeWalletTopup}
                      >
                        Đóng
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </AnimatePresence>
      {mobileMenuOpen && renderCustomerSidebar()}
    </div>
  );
}

const styles = {
  page: {
    minHeight: '100vh',
    background: '#f6f8f5',
    color: '#111827',
    fontFamily: '"SVN-Gilroy", Inter, Arial, sans-serif',
  },
  withdrawForm: {
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
    marginTop: 8,
  },
  withdrawFormField: {
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
  },
  withdrawInputLabel: {
    fontSize: 13,
    fontWeight: 500,
    color: '#475569',
    textAlign: 'left',
    marginBottom: 0,
  },
  withdrawBankSummary: {
    display: 'grid',
    gap: 3,
    padding: '11px 13px',
    border: '1px solid #dcfce7',
    borderRadius: 12,
    background: '#f0fdf4',
    color: '#475569',
    fontSize: 12,
    lineHeight: 1.45,
  },
  withdrawBankFields: {
    display: 'grid',
    gap: 12,
    padding: 12,
    border: '1px solid #e2e8f0',
    borderRadius: 14,
    background: '#f8fafc',
  },
  withdrawLinkWrap: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: 2,
  },
  header: {
    minHeight: 76,
    display: 'flex',
    alignItems: 'center',
    gap: 18,
    padding: '10px 30px',
    background: 'rgba(255, 255, 255, 0.96)',
    borderBottom: '1px solid #edf1ee',
    position: 'sticky',
    top: 0,
    zIndex: 1000,
    backdropFilter: 'blur(14px)',
    boxShadow: '0 10px 30px rgba(15, 23, 42, 0.03)',
  },
  brand: { display: 'flex', alignItems: 'center', gap: 12, minWidth: 230, flex: '0 0 230px' },
  logo: { fontSize: 34, fontWeight: 700, color: '#18b24a', lineHeight: 1, letterSpacing: -1 },
  brandTitle: { fontWeight: 700, fontSize: 12, textTransform: 'uppercase', letterSpacing: 2.2, color: '#0f172a' },
  brandSub: { color: '#9ca3af', fontSize: 11, marginTop: 4, whiteSpace: 'nowrap' },
  nav: { display: 'flex', gap: 7, flex: 1, justifyContent: 'center', flexWrap: 'nowrap', minWidth: 0 },
  navButton: {
    border: 0,
    background: '#eef2f7',
    color: '#6b7280',
    borderRadius: 24,
    padding: '10px 12px',
    fontWeight: 500,
    fontSize: 12.5,
    whiteSpace: 'nowrap',
  },
  navButtonActive: { background: '#22c55e', color: '#fff' },
  headerSpacer: { flex: 1, minWidth: 24 },
  headerActions: {
    display: 'flex',
    alignItems: 'center',
    gap: 18,
    position: 'relative',
    flex: '0 0 auto',
  },
  cartHeaderButton: {
    height: 46,
    border: '1px solid #bbf7d0',
    borderRadius: 999,
    background: '#f0fdf4',
    color: '#15803d',
    padding: '0 18px',
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    fontWeight: 500,
    position: 'relative',
  },
  cartBadge: {
    minWidth: 22,
    height: 22,
    borderRadius: 11,
    background: '#ef4444',
    color: '#fff',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '0 6px',
    fontSize: 12,
  },
  notificationWrap: { position: 'relative' },
  notificationButton: {
    width: 46,
    height: 46,
    border: '1px solid #bbf7d0',
    borderRadius: '50%',
    background: '#f0fdf4',
    color: '#15803d',
    fontSize: 18,
    fontWeight: 500,
    position: 'relative',
  },
  notificationBadge: {
    position: 'absolute',
    top: 4,
    right: 2,
    minWidth: 20,
    height: 20,
    borderRadius: 10,
    background: '#ef4444',
    color: '#fff',
    fontSize: 11,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '0 5px',
    border: '2px solid #fff',
  },
  notificationDropdown: {
    position: 'absolute',
    top: 58,
    right: 0,
    width: 380,
    maxWidth: 'calc(100vw - 32px)',
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: 18,
    boxShadow: '0 24px 70px rgba(15, 23, 42, 0.18)',
    zIndex: 20,
    overflow: 'hidden',
  },
  notificationHead: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 12,
    padding: 18,
    borderBottom: '1px solid #edf1ee',
  },
  notificationTitle: { fontSize: 18, fontWeight: 500, color: '#111827' },
  notificationSub: { marginTop: 4, color: '#94a3b8', fontSize: 12, fontWeight: 500 },
  notificationReadAll: {
    border: 0,
    borderRadius: 12,
    background: '#dcfce7',
    color: '#16a34a',
    padding: '9px 12px',
    fontWeight: 500,
    whiteSpace: 'nowrap',
  },
  notificationList: { maxHeight: 420, overflowY: 'auto', padding: 8 },
  notificationFooter: {
    padding: 12,
    borderTop: '1px solid #edf1ee',
    background: '#f8fafc',
  },
  notificationViewAll: {
    width: '100%',
    border: 0,
    borderRadius: 12,
    background: '#22c55e',
    color: '#fff',
    padding: 12,
    fontWeight: 500,
  },
  notificationItem: {
    width: '100%',
    border: 0,
    background: '#fff',
    textAlign: 'left',
    borderRadius: 14,
    padding: 14,
    display: 'grid',
    gap: 6,
  },
  notificationItemUnread: { background: '#f0fdf4' },
  notificationItemTitle: { color: '#111827', fontWeight: 500, lineHeight: 1.35 },
  notificationItemMessage: { color: '#64748b', fontSize: 13, lineHeight: 1.45 },
  notificationTime: { color: '#94a3b8', fontSize: 12, fontWeight: 500 },
  notificationEmpty: { color: '#94a3b8', padding: 24, textAlign: 'center', fontWeight: 500 },
  accountMenuWrap: { position: 'relative' },
  accountMenuButton: {
    border: 0,
    background: '#fff',
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    padding: '6px 12px 6px 4px',
    borderRadius: 18,
    color: '#0f172a',
    fontWeight: 500,
    width: 'auto',
    justifyContent: 'flex-start',
  },
  accountMenuInfo: {
    display: 'grid',
    gap: 2,
    minWidth: 0,
    flex: 1,
    textAlign: 'left',
  },
  accountMenuName: {
    display: 'block',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  accountMenuCode: {
    display: 'block',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    color: '#64748b',
  },
  accountAvatar: {
    width: 46,
    height: 46,
    borderRadius: '50%',
    background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
    color: '#fff',
    display: 'grid',
    placeItems: 'center',
    fontWeight: 600,
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.08)',
  },
  accountAvatarLarge: {
    width: 46,
    height: 46,
    borderRadius: '50%',
    background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
    color: '#fff',
    display: 'grid',
    placeItems: 'center',
    fontWeight: 600,
    flex: '0 0 auto',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.08)',
  },
  accountDropdown: {
    position: 'absolute',
    top: 58,
    right: 0,
    width: 360,
    maxWidth: 'calc(100vw - 32px)',
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: 18,
    boxShadow: '0 26px 70px rgba(15, 23, 42, .18)',
    padding: 14,
    zIndex: 25,
    display: 'grid',
    gap: 8,
  },
  accountDropdownHead: {
    display: 'flex',
    gap: 12,
    alignItems: 'center',
    padding: '8px 8px 14px',
    borderBottom: '1px solid #eef2f7',
    marginBottom: 4,
  },
  accountIdentityText: {
    flex: 1,
    minWidth: 0,
    display: 'grid',
    gap: 4,
  },
  accountIdentityName: {
    display: 'block',
    color: '#0f172a',
    fontSize: 16,
    lineHeight: 1.25,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  accountIdentityEmail: {
    display: 'block',
    color: '#334155',
    fontSize: 14,
    lineHeight: 1.35,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  accountModePill: {
    justifySelf: 'start',
    maxWidth: '100%',
    padding: '4px 9px',
    borderRadius: 8,
    background: '#eef2ff',
    color: '#4f46e5',
    fontSize: 12,
    fontWeight: 500,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  accountSwitchCard: {
    border: 0,
    background: '#f0fdf4',
    color: '#15803d',
    minHeight: 74,
    padding: '12px 14px',
    borderRadius: 16,
    display: 'grid',
    gridTemplateColumns: '40px minmax(0, 1fr) auto',
    alignItems: 'center',
    gap: 12,
    textAlign: 'left',
  },
  accountSwitchIcon: {
    width: 40,
    height: 40,
    borderRadius: 20,
    background: '#dcfce7',
    display: 'grid',
    placeItems: 'center',
    color: '#16a34a',
    fontSize: 16,
  },
  accountSwitchText: {
    minWidth: 0,
    display: 'grid',
    gap: 3,
  },
  accountSwitchArrow: {
    color: '#16a34a',
    fontSize: 14,
  },
  accountLogoutItem: { color: '#ef4444' },
  fullNotificationList: {
    display: 'grid',
    gap: 12,
  },
  fullNotificationItem: {
    width: '100%',
    border: '1px solid #e5e7eb',
    borderRadius: 16,
    background: '#fff',
    padding: 18,
    textAlign: 'left',
    display: 'flex',
    alignItems: 'center',
    gap: 16,
  },
  fullNotificationItemUnread: {
    borderColor: '#bbf7d0',
    background: '#f0fdf4',
  },
  fullNotificationTop: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 16,
    color: '#111827',
    fontSize: 16,
  },
  fullNotificationMessage: {
    margin: '10px 0 0',
    color: '#64748b',
    lineHeight: 1.6,
    fontWeight: 500,
  },
  notificationActionBar: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    background: '#fff',
    padding: '12px 20px',
    borderRadius: 12,
    border: '1px solid #e5e7eb',
    marginBottom: 16,
  },
  notificationFilterBar: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 10,
    justifyContent: 'flex-start',
    alignItems: 'center',
    marginBottom: 22,
  },
  filterTab: {
    border: 0,
    borderRadius: 999,
    background: '#f8fafc',
    color: '#475569',
    padding: '10px 18px',
    fontSize: 14,
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'all 0.2s ease',
  },
  filterTabActive: {
    background: '#16a34a',
    color: '#fff',
  },
  selectAllLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    fontWeight: 500,
    color: '#374151',
    cursor: 'pointer',
    userSelect: 'none',
  },
  actionCheckbox: {
    width: 18,
    height: 18,
    cursor: 'pointer',
  },
  actionButtonsGroup: {
    display: 'flex',
    gap: 10,
  },
  actionButton: {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '8px 16px',
    borderRadius: 8,
    border: '1px solid #d1d5db',
    background: '#fff',
    color: '#374151',
    fontWeight: 500,
    fontSize: 14,
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  actionButtonDelete: {
    background: '#fee2e2',
    borderColor: '#fca5a5',
    color: '#dc2626',
  },
  actionButtonDisabled: {
    opacity: 0.5,
    cursor: 'not-allowed',
    background: '#f3f4f6',
    borderColor: '#e5e7eb',
    color: '#9ca3af',
  },
  singleDeleteButton: {
    border: 0,
    background: 'transparent',
    cursor: 'pointer',
    padding: 8,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '50%',
    transition: 'background 0.2s',
  },
  logoutButton: {
    border: '1px solid #22c55e',
    color: '#16a34a',
    background: '#fff',
    borderRadius: 24,
    padding: '12px 20px',
    fontWeight: 500,
  },
  main: { width: '100%', maxWidth: 'none', margin: 0, padding: '20px 18px 56px' },
  customerShell: {
    display: 'grid',
    gridTemplateColumns: '260px minmax(0, 1fr)',
    gap: 16,
    alignItems: 'stretch',
    transition: 'grid-template-columns .22s ease',
  },
  customerShellCollapsed: {
    gridTemplateColumns: '76px minmax(0, 1fr)',
  },
  customerShellContent: { minWidth: 0, display: 'flex', flexDirection: 'column', gap: 16, flex: 1 },
  dashboardShell: {
    display: 'grid',
    gridTemplateColumns: '240px minmax(0, 1fr)',
    gap: 22,
    alignItems: 'start',
  },
  dashboardSidebar: {
    display: 'grid',
    gap: 16,
    position: 'sticky',
    top: 100,
  },
  dashboardSidebarCollapsed: { gap: 14 },
  sideMenu: {
    background: '#ffffff',
    borderRadius: 20,
    padding: 12,
    display: 'flex',
    flexDirection: 'column',
    boxShadow: '0 8px 30px rgba(15, 23, 42, 0.04)',
    overflow: 'hidden',
    fontFamily: "'SVN-Gilroy', 'Inter', sans-serif",
  },
  sideMenuToggle: {
    background: 'transparent',
    border: 'none',
    color: '#64748b',
    padding: '12px 14px',
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    cursor: 'pointer',
    fontSize: 14,
    fontWeight: 500,
    marginBottom: 8,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    width: '100%',
    transition: 'all 0.2s ease',
    fontFamily: "'SVN-Gilroy', 'Inter', sans-serif",
  },
  sideMenuToggleCollapsed: {
    justifyContent: 'center',
    padding: '12px 0',
  },
  sideMenuItem: {
    border: 0,
    borderRadius: 12,
    background: 'transparent',
    color: '#475569',
    padding: '12px 16px 12px 10px',
    marginLeft: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
    fontWeight: 500,
    fontSize: 15,
    textAlign: 'left',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    width: '100%',
    position: 'relative',
    transition: 'all .16s ease',
    fontFamily: "'SVN-Gilroy', 'Inter', sans-serif",
  },
  sideMenuItemCollapsed: {
    justifyContent: 'center',
    padding: '12px 0',
    gap: 0,
    width: '100%',
    whiteSpace: 'nowrap',
  },
  sideMenuItemActive: {
    background: '#ebf7ed',
    color: '#047857',
  },
  rankCard: {
    background: '#1f1f1f',
    borderRadius: 16,
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    boxShadow: '0 18px 55px rgba(15, 23, 42, 0.08)',
    marginTop: 8,
  },
  supportCard: {
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: 16,
    padding: 16,
    display: 'grid',
    gap: 12,
    color: '#475569',
    fontWeight: 500,
    fontSize: 13,
  },
  supportRow: { display: 'flex', alignItems: 'center', gap: 10 },
  dashboardContent: { display: 'grid', gap: 20 },
  hero: {
    position: 'relative',
    width: '100%',
    aspectRatio: '2170 / 512',
    borderRadius: '12px',
    boxShadow: '0 20px 50px rgba(15, 23, 42, 0.04)',
    border: '1px solid #e2e8f0',
    overflow: 'hidden',
  },
  bannerDotsContainer: {
    position: 'absolute',
    bottom: '16px',
    right: '24px',
    display: 'flex',
    gap: '6px',
    zIndex: 10,
  },
  bannerDot: {
    height: '6px',
    borderRadius: '99px',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
  },
  quickBox: { background: '#fff', color: '#111827', borderRadius: 20, padding: 24, display: 'grid', gap: 14 },
  quickTitle: { textTransform: 'uppercase', letterSpacing: 4, color: '#16a34a', fontWeight: 500 },
  primaryButton: { border: 0, borderRadius: 18, padding: 16, background: '#22c55e', color: '#fff', fontWeight: 500 },
  softButton: { border: 0, borderRadius: 18, padding: 16, background: '#dcfce7', color: '#16a34a', fontWeight: 500 },
  outlineButton: { border: '1px solid #22c55e', borderRadius: 18, padding: 16, background: '#fff', color: '#16a34a', fontWeight: 500 },
  statsGrid: { display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 16 },
  statCard: {
    background: '#fff',
    borderRadius: 14,
    padding: 20,
    boxShadow: '0 14px 38px rgba(15, 23, 42, 0.06)',
    border: '1px solid #eef2f7',
    display: 'flex',
    alignItems: 'center',
    gap: 16,
  },
  statIcon: {
    width: 44,
    height: 44,
    borderRadius: 14,
    display: 'grid',
    placeItems: 'center',
    fontSize: 18,
    flex: '0 0 auto',
  },
  statLabel: { color: '#64748b', fontWeight: 500, fontSize: 13 },
  statValue: { fontSize: 24, fontWeight: 500, marginTop: 8, color: '#0f172a' },
  statNote: { color: '#16a34a', marginTop: 6, fontWeight: 500, fontSize: 12 },
  gridTwo: { display: 'grid', gridTemplateColumns: '1fr 420px', gap: 22, marginTop: 24 },
  dashboardGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
    gap: 16,
    alignItems: 'stretch',
  },
  panel: {
    background: '#fff',
    borderRadius: 14,
    padding: 20,
    border: '1px solid #eef2f7',
    boxShadow: '0 14px 38px rgba(15, 23, 42, 0.05)',
  },
  panelHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap' },
  panelTitle: { margin: 0, fontSize: 16, fontWeight: 700, color: '#0f172a', position: 'relative', zIndex: 10 },
  badge: { background: '#dcfce7', color: '#16a34a', borderRadius: 10, padding: '6px 12px', fontWeight: 500 },
  selectBadge: {
    border: '1px solid #e5e7eb',
    borderRadius: 10,
    padding: '8px 12px',
    color: '#475569',
    fontWeight: 500,
    fontSize: 12,
  },
  chartLegend: { display: 'flex', gap: 26, marginTop: 22, color: '#475569', fontWeight: 500, fontSize: 13 },
  comboChart: { height: 220, display: 'flex', alignItems: 'end', gap: 26, paddingTop: 20 },
  comboItem: { display: 'grid', justifyItems: 'center', gap: 9, color: '#64748b', fontWeight: 500, fontSize: 12, flex: 1 },
  comboBars: { height: 176, display: 'flex', alignItems: 'end', gap: 8 },
  comboBarRevenue: { width: 18, borderRadius: '10px 10px 0 0', background: 'linear-gradient(#86efac, #22c55e)' },
  comboBarCommission: { width: 4, borderRadius: 999, background: '#5b5cf6' },
  donutWrap: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, marginTop: 16 },
  donutWrap: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, marginTop: 16 },
  donutChart: {
    width: 200,
    height: 200,
    borderRadius: '50%',
    display: 'grid',
    placeItems: 'center',
    position: 'relative',
    marginTop: 24,
  },
  donutCenter: {
    width: 144,
    height: 144,
    borderRadius: '50%',
    background: '#f8fafc',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    textAlign: 'center',
  },
  donutLegend: { 
    display: 'flex', 
    justifyContent: 'center',
    alignItems: 'center',
    gap: 24, 
    color: '#475569', 
    fontWeight: 500, 
    fontSize: 12,
    marginTop: 24
  },
  linkButton: {
    border: 0,
    background: 'transparent',
    color: '#16a34a',
    fontWeight: 500,
    fontSize: 13,
    cursor: 'pointer',
    padding: 0,
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
  },
  noticeList: { display: 'grid', gap: 18, marginTop: 24 },
  noticeItem: { display: 'flex', gap: 12, alignItems: 'start', color: '#0f172a' },
  recentOrdersPanel: { gridColumn: 'span 2' },
  miniTable: { marginTop: 18, display: 'grid', border: '1px solid #e5e7eb', borderRadius: 12, overflowX: 'auto', overflowY: 'hidden' },
  miniTableRow: {
    display: 'grid',
    gridTemplateColumns: '120px 1.5fr 2fr 130px 130px 130px',
    gap: 12,
    alignItems: 'center',
    padding: '13px 16px',
    borderBottom: '1px solid #edf2f7',
    color: '#334155',
    fontWeight: 500,
    fontSize: 13,
    minWidth: 800,
  },
  miniTableHead: { background: '#f8fafc', color: '#0f172a', fontWeight: 500 },
  pendingStatusBadge: { background: '#ffedd5', color: '#ea580c' },
  paidStatusBadge: { background: '#dcfce7', color: '#15803d' },
  processingStatusBadge: { background: '#fef3c7', color: '#b45309' },
  cancelledStatusBadge: { background: '#fee2e2', color: '#dc2626' },
  bestProductList: { display: 'grid', gap: 16, marginTop: 20 },
  bestProductItem: { display: 'grid', gridTemplateColumns: '30px 54px 1fr auto', gap: 12, alignItems: 'center' },
  bestProductImage: { width: 54, height: 54, borderRadius: 12, background: '#ecfdf5', display: 'grid', placeItems: 'center', overflow: 'hidden', color: '#16a34a' },
  bestProductThumb: { width: '100%', height: '100%', objectFit: 'contain', padding: 5 },
  rankNumber: { width: 26, height: 26, borderRadius: '50%', background: '#fef3c7', color: '#d97706', display: 'grid', placeItems: 'center', fontWeight: 500 },
  quickToolsPanel: {
    background: '#fff',
    border: '1px solid #eef2f7',
    borderRadius: 14,
    padding: 20,
    boxShadow: '0 14px 38px rgba(15, 23, 42, 0.05)',
  },
  quickToolGrid: { display: 'grid', gridTemplateColumns: 'repeat(6, minmax(0, 1fr))', gap: 14, marginTop: 18 },
  quickToolItem: {
    border: '1px solid #e5e7eb',
    borderRadius: 12,
    background: '#f8fafc',
    padding: 16,
    display: 'flex',
    gap: 12,
    alignItems: 'center',
    textAlign: 'left',
    color: '#334155',
    fontWeight: 500,
  },
  chart: { height: 220, display: 'flex', alignItems: 'end', gap: 18, paddingTop: 36 },
  chartItem: { display: 'grid', justifyItems: 'center', gap: 8, color: '#9ca3af', fontWeight: 500, fontSize: 12 },
  bar: { width: 42, borderRadius: '14px 14px 0 0', background: 'linear-gradient(#2dff79, #19b65a)' },
  darkPanel: { background: '#053827', color: '#fff', borderRadius: 20, padding: 28 },
  darkTitle: { margin: 0, fontSize: 24, fontWeight: 500 },
  linkBox: { background: 'rgba(255,255,255,.1)', borderRadius: 14, padding: 16, marginTop: 22, wordBreak: 'break-all' },
  darkText: { color: '#86efac', marginTop: 18, fontWeight: 500 },
  commerceScreen: { display: 'grid', gap: 24 },
  shopPage: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1fr)',
    gap: 16,
    alignItems: 'start',
  },
  shopMain: { display: 'grid', gap: 16, minWidth: 0 },
  breadcrumb: {
    color: '#64748b',
    fontWeight: 500,
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    fontSize: 14,
  },
  shopHero: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 22,
    background: '#fff',
    padding: '24px 28px',
    borderRadius: 16,
    boxShadow: '0 4px 20px rgba(15, 23, 42, 0.03)',
    border: '1px solid #edf1ee',
  },
  shopTitle: { margin: 0, color: '#16a34a', fontSize: 36, lineHeight: 1.15, fontWeight: 700 },
  eyebrow: { fontSize: 13, fontWeight: 700, color: '#64748b', letterSpacing: 1.5, textTransform: 'uppercase', marginBottom: 8 },
  shopDesc: { color: '#475569', margin: '12px 0 0', fontWeight: 500 },
  shopHeroActions: { display: 'flex', flexWrap: 'wrap', gap: 12 },
  shopPrimaryButton: {
    border: 0,
    minHeight: 46,
    borderRadius: 14,
    padding: '0 24px',
    background: '#dcfce7',
    color: '#15803d',
    fontWeight: 600,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 10,
    whiteSpace: 'nowrap',
    transition: 'all 0.2s ease',
  },
  shopExportButton: {
    border: '1px solid #bbf7d0',
    minHeight: 46,
    borderRadius: 14,
    padding: '0 20px',
    background: '#f0fdf4',
    color: '#16a34a',
    fontWeight: 600,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 10,
    whiteSpace: 'nowrap',
    transition: 'all 0.2s ease',
  },
  shopKpiGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(6, minmax(0, 1fr))',
    gap: 16,
    marginBottom: 20,
  },
  shopKpiCard: {
    background: '#fff',
    borderRadius: 14,
    padding: 24,
    boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
    border: '1px solid #f0f0f0',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
  },
  shopKpiCardClickable: {
    cursor: 'pointer',
    outline: 'none',
  },
  shopKpiIcon: {
    width: 42,
    height: 42,
    borderRadius: 14,
    display: 'grid',
    placeItems: 'center',
    flex: '0 0 auto',
  },
  shopFilterPanel: {
    display: 'grid',
    gridTemplateColumns: 'minmax(200px, 1.5fr) minmax(160px, 1.2fr) minmax(140px, 1fr) minmax(140px, 1fr) minmax(180px, 1.2fr) 130px 140px',
    gap: 10,
    alignItems: 'center',
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: 14,
    padding: 12,
  },
  shopSearchBox: {
    height: 46,
    border: '1px solid #e5e7eb',
    borderRadius: 14,
    background: '#fff',
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: '0 14px',
    color: '#94a3b8',
    marginBottom: 0,
  },
  shopSearchInput: {
    border: 0,
    outline: 'none',
    background: 'transparent',
    width: '100%',
    height: '100%',
    color: '#1e293b',
    fontSize: 14,
  },
  shopFilterButton: {
    width: '100%',
    height: 46,
    border: '1px solid #e5e7eb',
    borderRadius: 14,
    background: '#fff',
    color: '#334155',
    padding: '0 12px',
    fontWeight: 500,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8,
    whiteSpace: 'nowrap',
    boxShadow: '0 6px 18px rgba(15, 23, 42, .03)',
    overflow: 'hidden',
  },
  shopSelectWrap: {
    width: '100%',
    height: 46,
    border: '1px solid #e5e7eb',
    borderRadius: 14,
    background: '#fff',
    color: '#94a3b8',
    padding: '0 12px',
    fontWeight: 500,
    display: 'grid',
    gridTemplateColumns: '18px 1fr',
    alignItems: 'center',
    gap: 8,
    boxShadow: '0 6px 18px rgba(15, 23, 42, .03)',
    marginBottom: 0,
  },
  shopFilterSelect: {
    width: '100%',
    height: '100%',
    minWidth: 0,
    border: 0,
    outline: 'none',
    background: 'transparent',
    color: '#334155',
    fontSize: 13,
    fontWeight: 500,
    cursor: 'pointer',
  },
  shopSortButton: { minWidth: 0 },
  shopFilterActive: {
    width: '100%',
    height: 46,
    border: 0,
    borderRadius: 14,
    background: 'linear-gradient(180deg, #ecfdf5 0%, #dcfce7 100%)',
    color: '#16a34a',
    padding: '0 14px',
    fontWeight: 500,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 9,
    whiteSpace: 'nowrap',
  },
  resetFilterButton: {
    width: '100%',
    height: 46,
    border: '1px solid #bbf7d0',
    borderRadius: 14,
    background: '#f0fdf4',
    color: '#15803d',
    fontWeight: 500,
    padding: '0 10px',
    whiteSpace: 'nowrap',
    cursor: 'pointer',
  },
  shopCategoryTabs: { display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' },
  shopCategoryChip: {
    border: '1px solid rgba(226, 232, 240, 0.8)',
    borderRadius: 999,
    background: '#ffffff',
    color: '#475569',
    height: 46,
    minWidth: 126,
    padding: '0 22px',
    fontWeight: 500,
    boxShadow: '0 2px 10px rgba(15, 23, 42, 0.03)',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  shopCategoryChipActive: {
    color: '#fff',
    background: 'linear-gradient(180deg, #22c55e 0%, #16a34a 100%)',
    boxShadow: '0 12px 24px rgba(22, 163, 74, .25)',
  },
  promoBanner: {
    minHeight: 92,
    border: '1px solid #bbf7d0',
    borderRadius: 16,
    background: 'radial-gradient(circle at 92% 45%, rgba(34, 197, 94, .18) 0, rgba(34, 197, 94, 0) 26%), linear-gradient(90deg, #ecfdf5 0%, #f8fffb 68%, #dcfce7 100%)',
    display: 'grid',
    gridTemplateColumns: '120px 1fr auto',
    alignItems: 'center',
    gap: 26,
    padding: '18px 28px',
    overflow: 'hidden',
    boxShadow: '0 18px 42px rgba(22, 163, 74, .08)',
  },
  promoImage: {
    height: 78,
    borderRadius: 14,
    background: 'rgba(255, 255, 255, 0.2)',
    color: '#fff',
    display: 'grid',
    placeItems: 'center',
    fontSize: 34,
    overflow: 'hidden',
  },
  promoImageImg: { width: '100%', height: '100%', objectFit: 'cover', display: 'block' },
  promoContent: {
    display: 'grid',
    gap: 6,
    minWidth: 0,
  },
  promoActionButton: {
    height: 44,
    border: 0,
    borderRadius: 999,
    background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
    color: '#fff',
    padding: '0 18px',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 9,
    fontWeight: 800,
    fontSize: 13,
    whiteSpace: 'nowrap',
    cursor: 'pointer',
    boxShadow: '0 14px 28px rgba(22, 163, 74, .24)',
  },
  shopPanel: {
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: 14,
    padding: 18,
    boxShadow: '0 16px 44px rgba(15, 23, 42, .04)',
    minWidth: 0,
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
  },
  orderStatusTabs: { display: 'flex', gap: 28, marginTop: 18, borderBottom: '1px solid #e5e7eb' },
  orderStatusTab: {
    border: 0,
    background: 'transparent',
    color: '#64748b',
    padding: '0 0 14px',
    fontWeight: 500,
  },
  orderStatusTabActive: { color: '#16a34a', boxShadow: 'inset 0 -2px 0 #22c55e' },
  shopOrderTable: {
    marginTop: 14,
    border: '1px solid #e5e7eb',
    borderRadius: 12,
    overflow: 'hidden',
  },
  shopOrderRow: {
    display: 'grid',
    gridTemplateColumns: '130px minmax(230px, 1.35fr) minmax(150px, .9fr) 120px 130px 140px minmax(270px, 310px)',
    gap: 12,
    alignItems: 'center',
    padding: '14px 16px',
    borderBottom: '1px solid #eef2f7',
    color: '#334155',
    fontWeight: 500,
    fontSize: 13,
  },
  shopOrderRowClickable: {
    cursor: 'pointer',
    background: '#fffaf3',
  },
  shopOrderHead: { background: '#f8fafc', color: '#0f172a', fontWeight: 700 },
  shopOrderProduct: { display: 'grid', gridTemplateColumns: '52px minmax(0, 1fr)', gap: 12, alignItems: 'center', minWidth: 0 },
  shopOrderThumb: {
    width: 42,
    height: 42,
    borderRadius: 8,
    background: '#ecfdf5',
    color: '#16a34a',
    display: 'grid',
    placeItems: 'center',
    flex: '0 0 auto',
  },
  orderCodeCell: { display: 'grid', gap: 4, minWidth: 0 },
  orderBuyerCell: { display: 'grid', gap: 4, minWidth: 0 },
  orderAmountCell: { textAlign: 'right', justifySelf: 'end', whiteSpace: 'nowrap' },
  orderCenterCell: { textAlign: 'center', justifySelf: 'center', whiteSpace: 'nowrap' },
  orderActionHead: { textAlign: 'right', justifySelf: 'end', whiteSpace: 'nowrap' },
  orderStatusCell: { justifySelf: 'center', width: 'max-content' },
  orderUpdatedCell: { justifySelf: 'center', textAlign: 'center', whiteSpace: 'nowrap' },
  orderQuickActions: {
    display: 'flex',
    flexWrap: 'nowrap',
    gap: 6,
    justifyContent: 'end',
    alignItems: 'center',
    minWidth: 0,
  },
  orderActionButton: {
    minHeight: 30,
    border: '1px solid #cbd5e1',
    borderRadius: 8,
    background: '#fff',
    color: '#475569',
    fontWeight: 500,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 5,
    padding: '0 9px',
    fontSize: 12,
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    boxShadow: '0 1px 2px rgba(15, 23, 42, .05)',
  },
  orderActionButtonPrimary: {
    borderColor: '#bbf7d0',
    background: '#f0fdf4',
    color: '#15803d',
  },
  orderPayButton: {
    borderColor: '#fed7aa',
    background: '#fff7ed',
    color: '#ea580c',
  },
  viewAllOrderButton: {
    display: 'block',
    margin: '14px auto 0',
    border: 0,
    background: 'transparent',
    color: '#475569',
    fontWeight: 500,
  },
  carouselButtons: { display: 'flex', gap: 8 },
  shopProductGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
    gap: 16,
    marginTop: 16,
    minHeight: 470,
    alignItems: 'stretch',
  },
  productEmptyState: {
    gridColumn: '1 / -1',
    minHeight: 430,
    border: '1px dashed #d1d5db',
    borderRadius: 16,
    padding: 28,
    color: '#64748b',
    background: '#f8fafc',
    fontWeight: 500,
    textAlign: 'center',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  shopProductCard: {
    border: '1px solid #e5e7eb',
    borderRadius: 16,
    background: '#fff',
    overflow: 'hidden',
    cursor: 'pointer',
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    height: 'auto',
    minHeight: 440,
    boxShadow: '0 12px 30px rgba(15, 23, 42, .04)',
  },
  shopProductVisual: {
    height: 162,
    background: 'linear-gradient(145deg, #ecfdf5 0%, #f8fafc 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
    color: '#16a34a',
    fontSize: 34,
    flex: '0 0 162px',
    overflow: 'hidden',
  },
  shopProductImage: {
    width: '100%',
    height: '100%',
    objectFit: 'contain',
    padding: 12,
    boxSizing: 'border-box',
    display: 'block',
    animation: 'evgProductImageFade .45s ease',
  },
  productBadgeStack: {
    position: 'absolute',
    top: 10,
    left: 10,
    zIndex: 3,
    display: 'flex',
    flexWrap: 'wrap',
    gap: 6,
    maxWidth: 'calc(100% - 20px)',
  },
  productBadge: {
    color: '#fff',
    borderRadius: 6,
    padding: '4px 8px',
    fontWeight: 700,
    fontSize: 11,
    lineHeight: 1.2,
    boxShadow: '0 8px 16px rgba(15, 23, 42, .14)',
  },
  hotBadge: {
    position: 'absolute',
    top: 10,
    left: 10,
    background: '#f97316',
    color: '#fff',
    borderRadius: 6,
    padding: '4px 8px',
    fontWeight: 500,
    fontSize: 11,
  },
  newBadge: {
    position: 'absolute',
    top: 10,
    left: 10,
    background: '#3b82f6',
    color: '#fff',
    borderRadius: 6,
    padding: '4px 8px',
    fontWeight: 500,
    fontSize: 11,
  },
  shopProductBody: {
    padding: '14px 16px 16px',
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
    flex: 1,
    background: '#fff',
    position: 'relative',
    zIndex: 1,
  },
  shopProductInfo: { display: 'flex', flexDirection: 'column', gap: 7, minHeight: 144 },
  shopProductName: {
    color: '#0f172a',
    fontSize: 15,
    lineHeight: 1.3,
    fontWeight: 500,
    minHeight: 42,
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
  },
  shopProductShortDescription: {
    margin: 0,
    color: '#475569',
    fontSize: 13,
    lineHeight: 1.45,
    fontWeight: 500,
    minHeight: 0,
    whiteSpace: 'pre-line',
    overflowWrap: 'anywhere',
  },
  shopProductCategory: { color: '#475569', fontSize: 13, fontWeight: 500, minHeight: 18 },
  shopProductFeatures: {
    color: '#0f172a',
    fontSize: 12,
    lineHeight: 1.45,
    minHeight: 52,
    display: '-webkit-box',
    WebkitLineClamp: 3,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
  },
  shopProductBottom: { marginTop: 'auto', display: 'grid', gap: 9 },
  stockLine: { display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 8, color: '#64748b', fontSize: 12, fontWeight: 500, minHeight: 18, marginTop: 'auto' },
  inStockText: { color: '#64748b' },
  outOfStockText: { color: '#ef4444' },
  shopProductPriceStack: { display: 'grid', gap: 3, minWidth: 0 },
  shopProductOriginalRow: { display: 'flex', alignItems: 'center', gap: 6, minHeight: 16 },
  shopProductOriginalPrice: { color: '#94a3b8', fontSize: 12, fontWeight: 500, textDecoration: 'line-through' },
  shopProductDiscountBadge: {
    color: '#ef4444',
    background: '#fff1f2',
    borderRadius: 4,
    padding: '1px 5px',
    fontSize: 10,
    fontWeight: 700,
    lineHeight: 1.4,
    whiteSpace: 'nowrap',
  },
  shopProductPrice: { color: '#0f172a', fontSize: 18, fontWeight: 500 },
  commissionLine: { color: '#64748b', fontSize: 12, fontWeight: 500 },
  shopProductActions: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, alignItems: 'center' },
  inlineQty: {
    height: 32,
    width: 82,
    display: 'grid',
    gridTemplateColumns: '24px 1fr 24px',
    border: '1px solid #cbd5e1',
    borderRadius: 6,
    overflow: 'hidden',
    alignItems: 'center',
    textAlign: 'center',
    background: '#fff',
  },
  detailMiniButton: {
    border: '1px solid #e5e7eb',
    borderRadius: 12,
    background: '#fff',
    color: '#334155',
    fontWeight: 500,
    gridColumn: '1 / -1',
    minHeight: 42,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: '0 8px 20px rgba(15, 23, 42, .03)',
  },
  compareLine: { display: 'none' },
  cartSidebar: { display: 'none' },
  cartSidebarPanel: {
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: 20,
    padding: 18,
    boxShadow: '0 18px 48px rgba(15, 23, 42, .07)',
  },
  cartSidebarList: { display: 'grid', gap: 12, marginTop: 14 },
  cartSidebarItem: {
    display: 'grid',
    gridTemplateColumns: '50px minmax(0, 1fr) 28px',
    gap: 10,
    alignItems: 'start',
    borderBottom: '1px solid #eef2f7',
    paddingBottom: 12,
  },
  cartSidebarImage: {
    width: 50,
    height: 50,
    borderRadius: 8,
    background: '#f1f5f9',
    display: 'grid',
    placeItems: 'center',
    color: '#16a34a',
    overflow: 'hidden',
  },
  cartSidebarImageImg: { width: '100%', height: '100%', objectFit: 'cover' },
  cartSidebarInfo: {
    minWidth: 0,
    display: 'grid',
    gap: 3,
    color: '#334155',
    fontSize: 12.5,
    lineHeight: 1.35,
  },
  cartSidebarQty: {
    height: 30,
    display: 'grid',
    gridTemplateColumns: '26px 1fr 26px',
    border: '1px solid #e5e7eb',
    borderRadius: 0,
    alignItems: 'center',
    textAlign: 'center',
    background: '#fff',
    gridColumn: '2 / 3',
    width: 82,
  },
  cartSidebarSummary: { display: 'grid', gap: 12, marginTop: 18, color: '#64748b', fontWeight: 500 },
  voucherRow: { display: 'grid', gridTemplateColumns: '1fr 86px', gap: 8, alignItems: 'center' },
  voucherButton: {
    height: 42,
    borderRadius: 12,
    background: '#f1f5f9',
    color: '#64748b',
    fontWeight: 500,
  },
  cartSidebarTotal: {
    borderTop: '1px solid #e5e7eb',
    paddingTop: 14,
    display: 'flex',
    justifyContent: 'space-between',
    color: '#0f172a',
    fontSize: 18,
  },
  continueShoppingButton: {
    border: 0,
    background: 'transparent',
    color: '#475569',
    fontWeight: 500,
    padding: 12,
    borderRadius: 12,
  },
  trustPanel: {
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: 14,
    padding: 18,
    display: 'grid',
    gap: 16,
  },
  trustItem: { display: 'flex', gap: 12, alignItems: 'start', color: '#334155' },
  portalLayout: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1fr) 260px',
    gap: 18,
    alignItems: 'start',
  },
  portalMain: { display: 'grid', gap: 16, minWidth: 0 },
  portalRight: { display: 'grid', gap: 16, position: 'sticky', top: 108 },
  toolsKpiCard: {
    minHeight: 116,
    border: '1px solid #eef2f7',
    borderRadius: 12,
    background: '#fff',
    padding: '14px 16px',
    display: 'grid',
    alignContent: 'start',
    gap: 9,
    boxShadow: '0 8px 22px rgba(15, 23, 42, .04)',
  },
  toolsKpiHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    color: '#475569',
    fontWeight: 700,
    fontSize: 13,
  },
  toolsKpiValue: {
    color: '#0f172a',
    fontWeight: 750,
    fontSize: 20,
    lineHeight: 1.3,
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
  },
  toolsKpiMeta: {
    display: 'flex',
    alignItems: 'center',
    color: '#16a34a',
    fontSize: 12,
    fontWeight: 500,
    marginTop: 2,
  },
  networkPage: { width: '100%', minWidth: 0 },
  networkHero: {
    minHeight: 152,
    borderRadius: 16,
    padding: 28,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 24,
    color: '#fff',
    background: 'radial-gradient(circle at 85% 50%, rgba(255,255,255,.18) 0, rgba(255,255,255,0) 28%), linear-gradient(135deg, #04713e 0%, #10b981 100%)',
    overflow: 'hidden',
  },
  networkEyebrow: { display: 'block', marginBottom: 8, fontSize: 12, fontWeight: 500, letterSpacing: '1.8px', opacity: .82 },
  networkHeroActions: { display: 'flex', gap: 12, flexWrap: 'wrap', justifyContent: 'flex-end', flexShrink: 0 },
  networkHeroButton: { height: 44, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 9, padding: '0 18px', border: '1px solid rgba(255,255,255,.55)', borderRadius: 11, background: '#fff', color: '#04713e', fontWeight: 500, boxShadow: '0 10px 24px rgba(3, 75, 45, .16)' },
  networkTextValue: { fontSize: 14, lineHeight: 1.35, color: '#64748b' },
  networkKpiGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
    gap: 14,
  },
  networkKpiCard: { borderTop: '3px solid #22c55e' },
  panelDescription: { margin: '6px 0 0', color: '#64748b', fontSize: 12, fontWeight: 500, lineHeight: 1.5 },
  networkDataNotice: { minHeight: 90, marginTop: 18, padding: 16, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12, borderRadius: 12, background: '#f8fafc', color: '#64748b', fontSize: 12, fontWeight: 500, lineHeight: 1.55, textAlign: 'center' },
  networkRevenueEmpty: { minHeight: 180, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 8, color: '#64748b', textAlign: 'center' },
  networkInsightGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
    gap: 16,
  },
  networkInsightCard: { minHeight: 320, display: 'flex', flexDirection: 'column' },
  journeyList: { display: 'grid', gap: 10, marginTop: 20 },
  journeyItem: { minHeight: 66, width: '100%', display: 'grid', gridTemplateColumns: '34px minmax(0, 1fr) auto', alignItems: 'center', gap: 12, padding: '10px 12px', border: '1px solid #e5e7eb', borderRadius: 11, background: '#f8fafc', color: '#334155' },
  journeyIndex: { width: 30, height: 30, display: 'grid', placeItems: 'center', borderRadius: 9, background: '#dcfce7', color: '#15803d', fontWeight: 500 },
  funnelChart: { display: 'grid', justifyItems: 'center', gap: 2, marginTop: 18 },
  linkStats: { display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 10, marginTop: 20 },
  linkStatItem: { minHeight: 72, display: 'flex', flexDirection: 'column', justifyContent: 'space-between', gap: 8, padding: 12, border: '1px solid #e5e7eb', borderRadius: 11, background: '#f8fafc', color: '#64748b', fontSize: 12 },
  miniLineChart: { display: 'flex', alignItems: 'end', gap: 12, height: 110, marginTop: 18 },
  networkFilterPanel: { display: 'grid', gridTemplateColumns: 'repeat(12, minmax(0, 1fr))', gap: 12, alignItems: 'end', padding: 18, border: '1px solid #e5e7eb', borderRadius: 14, background: '#fff', boxShadow: '0 12px 34px rgba(15, 23, 42, .04)' },
  networkFilterField: { gridColumn: 'span 2', display: 'grid', gap: 7, color: '#475569', fontSize: 12, fontWeight: 500, minWidth: 0 },
  networkSearchField: { gridColumn: 'span 4' },
  networkSearchControl: { position: 'relative', display: 'flex', alignItems: 'center', color: '#94a3b8' },
  networkResetButton: { gridColumn: 'span 2', height: 44, border: '1px solid #bbf7d0', borderRadius: 10, background: '#f0fdf4', color: '#15803d', fontSize: 13, fontWeight: 500 },
  networkFilterResult: { gridColumn: 'span 8', alignSelf: 'center', color: '#64748b', fontSize: 12, fontWeight: 500 },
  networkTable: { marginTop: 14, border: '1px solid #e5e7eb', borderRadius: 12, overflow: 'hidden' },
  networkTableRow: {
    display: 'grid',
    gridTemplateColumns: '1.15fr 1.25fr 1.2fr .85fr .55fr .9fr .45fr',
    gap: 10,
    alignItems: 'center',
    padding: '12px 14px',
    borderBottom: '1px solid #eef2f7',
    color: '#334155',
    fontWeight: 500,
    fontSize: 13,
  },
  networkSectionHeader: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16 },
  networkCountBadge: { display: 'grid', placeItems: 'center', minWidth: 34, height: 28, padding: '0 10px', borderRadius: 999, background: '#dcfce7', color: '#15803d', fontWeight: 500 },
  networkEmptyState: { minHeight: 112, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 14, color: '#64748b', textAlign: 'left' },
  rowActionDots: { textAlign: 'center', color: '#94a3b8', fontWeight: 500 },
  rowActionDotsButton: {
    width: 36,
    height: 28,
    border: '1px solid #e2e8f0',
    borderRadius: 6,
    background: '#fff',
    color: '#94a3b8',
    cursor: 'pointer',
    fontWeight: 700,
    lineHeight: 1,
  },
  rowActionDotsButtonDisabled: {
    cursor: 'not-allowed',
    opacity: 0.45,
  },
  memberTreeOverlay: {
    position: 'fixed',
    inset: 0,
    zIndex: 9999,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 24,
    background: 'rgba(15, 23, 42, 0.58)',
    backdropFilter: 'blur(2px)',
  },
  memberTreeModal: {
    width: 'min(1040px, calc(100vw - 48px))',
    maxHeight: 'min(760px, calc(100vh - 48px))',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    borderRadius: 14,
    background: '#fff',
    boxShadow: '0 28px 80px rgba(15, 23, 42, 0.28)',
  },
  memberTreeHeader: {
    padding: '18px 22px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 16,
    borderBottom: '1px solid #e5e7eb',
    background: 'linear-gradient(135deg, #f8fafc 0%, #eefbf3 100%)',
  },
  memberTreeHeaderMain: { display: 'flex', alignItems: 'center', gap: 14, minWidth: 0 },
  memberTreeHeaderIcon: {
    width: 42,
    height: 42,
    flexShrink: 0,
    display: 'grid',
    placeItems: 'center',
    borderRadius: 10,
    background: '#dcfce7',
    color: '#16a34a',
    fontSize: 18,
  },
  memberTreeTitle: { margin: 0, fontSize: 20, color: '#0f172a', fontWeight: 800, lineHeight: 1.25 },
  memberTreeSubtitle: { marginTop: 6, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', color: '#64748b', fontSize: 12, fontWeight: 600 },
  memberTreeCloseButton: {
    width: 36,
    height: 36,
    border: '1px solid #e2e8f0',
    borderRadius: 8,
    background: '#fff',
    color: '#64748b',
    cursor: 'pointer',
    fontSize: 22,
    lineHeight: 1,
  },
  memberTreeBody: { padding: 20, overflowY: 'auto', flex: 1, background: '#f8fafc' },
  memberTreeLoading: { minHeight: 280, display: 'grid', placeItems: 'center', gap: 10, color: '#64748b', fontWeight: 600 },
  memberTreeStats: { display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 12, marginBottom: 16 },
  memberTreeStatCard: {
    padding: '14px 16px',
    border: '1px solid #e5e7eb',
    borderRadius: 10,
    background: '#fff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
  },
  memberTreeCanvas: { border: '1px solid #e5e7eb', borderRadius: 12, background: '#fff', overflow: 'hidden' },
  memberTreeRootRow: { padding: 16, borderBottom: '1px solid #eef2f7', background: '#ffffff' },
  memberTreeRootCard: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    padding: 14,
    borderRadius: 10,
    border: '1px solid #bbf7d0',
    background: '#f0fdf4',
    color: '#0f172a',
  },
  memberTreeAvatar: { width: 42, height: 42, display: 'grid', placeItems: 'center', borderRadius: '50%', background: '#16a34a', color: '#fff', fontWeight: 800 },
  memberTreeAvatarSmall: { width: 30, height: 30, display: 'grid', placeItems: 'center', borderRadius: '50%', background: '#e2e8f0', color: '#475569', fontWeight: 800, fontSize: 12 },
  memberTreeList: { padding: 16, display: 'flex', flexDirection: 'column', gap: 10, overflowX: 'auto' },
  memberTreeNode: {
    minWidth: 560,
    position: 'relative',
    padding: '12px 14px',
    border: '1px solid #e2e8f0',
    borderRadius: 10,
    background: '#fff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 14,
  },
  memberTreeConnector: { position: 'absolute', left: -20, top: '50%', width: 18, height: 1, background: '#cbd5e1' },
  memberTreeNodeMain: { display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 },
  memberTreeLevelBadge: { padding: '4px 8px', borderRadius: 999, color: '#fff', fontSize: 11, fontWeight: 800, flexShrink: 0 },
  memberTreeNodeInfo: { display: 'flex', flexDirection: 'column', gap: 3, minWidth: 0 },
  memberTreeNodeCount: { flexShrink: 0, padding: '6px 10px', borderRadius: 999, background: '#f1f5f9', color: '#475569', fontSize: 12, fontWeight: 700 },
  memberTreeEmpty: { minHeight: 170, display: 'grid', placeItems: 'center', gap: 7, color: '#64748b', textAlign: 'center' },
  memberTreeError: { minHeight: 180, display: 'grid', placeItems: 'center', color: '#ef4444', fontWeight: 700 },
  tipItem: { display: 'flex', gap: 12, alignItems: 'center', padding: '12px 0', borderBottom: '1px solid #eef2f7', color: '#334155', fontWeight: 500 },
  tipItemButton: { width: '100%', display: 'flex', gap: 12, alignItems: 'center', padding: '12px 0', border: 0, borderBottom: '1px solid #eef2f7', background: 'transparent', color: '#334155', fontWeight: 500, textAlign: 'left' },
  walletMain: { display: 'grid', gap: 16, minWidth: 0, gridColumn: '1 / -1' },
  sectionHeading: { maxWidth: 760, display: 'grid', gap: 7, marginBottom: 2 },
  sectionEyebrow: { color: '#16a34a', fontSize: 12, fontWeight: 500, letterSpacing: '2px' },
  walletTopGrid: { display: 'grid', gridTemplateColumns: 'minmax(0, 1.15fr) minmax(320px, .85fr)', gap: 16 },
  walletSummaryGrid: { gap: 14, marginTop: 18 },
  walletSummaryCard: { position: 'relative', height: '100%', minHeight: 148, border: '1px solid #eef2f7', borderRadius: 14, background: 'linear-gradient(180deg, #ffffff, #f8fafc)', padding: 18, display: 'grid', gridTemplateRows: 'auto 1fr auto auto', gap: 9 },
  walletSummaryIcon: { width: 38, height: 38, display: 'grid', placeItems: 'center', borderRadius: 11, marginBottom: 4, fontSize: 15 },
  incomeTrendPanel: { overflow: 'hidden', background: 'linear-gradient(180deg, #ffffff 0%, #fbfefd 100%)' },
  incomeTrendHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 14, flexWrap: 'wrap' },
  incomeTrendStats: { display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 10, marginTop: 16 },
  incomeTrendStat: {
    minWidth: 0,
    border: '1px solid #eef2f7',
    borderRadius: 13,
    background: '#fff',
    padding: '12px 14px',
    display: 'grid',
    gap: 6,
    boxShadow: '0 10px 24px rgba(15, 23, 42, .035)',
  },
  incomeTrendStatLabel: { display: 'inline-flex', alignItems: 'center', gap: 7, color: '#64748b', fontSize: 12, fontWeight: 700 },
  incomeTrendStatValue: { minWidth: 0, color: '#0f172a', fontSize: 16, fontWeight: 800, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  incomeTrendChart: {
    position: 'relative',
    minHeight: 170,
    marginTop: 16,
    padding: '18px 10px 4px',
    borderRadius: 14,
    background: 'linear-gradient(180deg, rgba(248,250,252,.95), rgba(255,255,255,.98))',
    border: '1px solid #eef2f7',
  },
  incomeTrendGrid: {
    position: 'absolute',
    inset: '18px 12px 34px',
    display: 'grid',
    gridTemplateRows: 'repeat(3, 1fr)',
    pointerEvents: 'none',
  },
  incomeTrendGridLine: { borderTop: '1px dashed #e2e8f0' },
  incomeTrendBars: { position: 'relative', minHeight: 148, display: 'grid', gridTemplateColumns: 'repeat(6, minmax(34px, 1fr))', gap: 10, alignItems: 'end', zIndex: 1 },
  incomeTrendBarItem: { minWidth: 0, display: 'grid', justifyItems: 'center', gap: 8 },
  incomeTrendBarWrap: { height: 126, width: '100%', display: 'flex', alignItems: 'end', justifyContent: 'center', gap: 5 },
  incomeTrendRevenueBar: { width: 18, minHeight: 0, borderRadius: '999px 999px 6px 6px', background: 'linear-gradient(180deg, #34d399 0%, #16a34a 100%)', boxShadow: '0 10px 18px rgba(22, 163, 74, .18)' },
  incomeTrendCommissionBar: { width: 8, minHeight: 0, borderRadius: 999, background: 'linear-gradient(180deg, #93c5fd 0%, #3b82f6 100%)', boxShadow: '0 10px 18px rgba(59, 130, 246, .16)' },
  incomeTrendMonth: { color: '#94a3b8', fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap' },
  walletTrend: { height: 180, display: 'flex', alignItems: 'end', gap: 28, paddingTop: 20 },
  walletEmptyChart: { flex: 1, minHeight: 190, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 7, borderRadius: 12, background: '#f8fafc', color: '#64748b', textAlign: 'center', fontSize: 12 },
  walletContentGrid: { display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 320px', gap: 16, alignItems: 'start' },
  walletAside: { display: 'grid', gap: 16, position: 'sticky', top: 108 },
  walletTabs: { display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 10, marginBottom: 18 },
  walletTabButton: { minHeight: 68, display: 'flex', alignItems: 'center', gap: 11, padding: '10px 12px', border: '1px solid transparent', borderRadius: 12, background: '#fff', color: '#475569', fontSize: 13, fontWeight: 500, textAlign: 'left', outline: 'none' },
  walletTabButtonActive: { boxShadow: '0 8px 22px rgba(15, 23, 42, .06)' },
  walletTabIcon: { width: 38, height: 38, flex: '0 0 38px', display: 'grid', placeItems: 'center', borderRadius: 11, fontSize: 14 },
  walletFilterPanel: { display: 'grid', gridTemplateColumns: '170px 170px minmax(240px, 1fr) 130px 150px', gap: 10, alignItems: 'end', padding: 14, marginBottom: 18, border: '1px solid #e5e7eb', borderRadius: 12, background: '#f8fafc' },
  walletFilterField: { minWidth: 0, display: 'grid', gap: 6, color: '#64748b', fontSize: 11, fontWeight: 500 },
  walletSearchField: { minWidth: 220 },
  walletSearchControl: { position: 'relative', display: 'flex', alignItems: 'center' },
  walletResetButton: { height: 42, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 7, border: '1px solid #e2e8f0', borderRadius: 10, background: '#fff', color: '#475569', fontWeight: 500 },
  walletTable: { border: '1px solid #e5e7eb', borderRadius: 12, overflow: 'hidden', marginTop: 14 },
  walletTableRow: {
    display: 'grid',
    gridTemplateColumns: '1.2fr 1fr 1.25fr 1fr 1fr .8fr',
    gap: 12,
    alignItems: 'center',
    padding: '13px 16px',
    borderBottom: '1px solid #eef2f7',
    color: '#334155',
    fontWeight: 500,
    fontSize: 13,
  },
  walletTableEmpty: { minHeight: 150, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 7, color: '#64748b', textAlign: 'center', fontSize: 12 },
  walletEmptyIcon: { width: 42, height: 42, display: 'grid', placeItems: 'center', marginBottom: 4, borderRadius: 12, background: '#f0fdf4', color: '#16a34a', fontSize: 16 },
  walletTypeBadge: { width: 'fit-content', display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 9px', borderRadius: 999, background: '#dcfce7', color: '#15803d', fontSize: 11, fontWeight: 500 },
  walletTypePassive: { background: '#ede9fe', color: '#7c3aed' },
  walletTypeWithdrawal: { background: '#dbeafe', color: '#2563eb' },
  walletTypeOrder: { background: '#fef3c7', color: '#b45309' },
  withdrawHeader: { display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16, color: '#64748b' },
  withdrawHeaderIcon: { width: 42, height: 42, flex: '0 0 42px', display: 'grid', placeItems: 'center', borderRadius: 12, background: '#dcfce7', color: '#15803d' },
  walletRules: {
    background: '#f0fdf4',
    color: '#15803d',
    borderRadius: 12,
    padding: '12px 14px',
    display: 'grid',
    gap: 8,
    fontWeight: 500,
    fontSize: 12,
    lineHeight: 1.4,
  },
  withdrawItem: { display: 'grid', gap: 3, padding: '12px 0', borderBottom: '1px solid #eef2f7', color: '#334155' },
  compactEmptyState: { marginTop: 14, padding: '18px 12px', borderRadius: 10, background: '#f8fafc', color: '#64748b', textAlign: 'center', fontSize: 12, fontWeight: 500 },
  policyQuickGrid: { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginTop: 18 },
  policyQuickItem: { minHeight: 112, display: 'grid', gridTemplateColumns: '42px 1fr', alignItems: 'center', columnGap: 12, rowGap: 5, padding: 16, border: '1px solid #e5e7eb', borderRadius: 12, color: '#334155' },
  policyQuickIcon: { gridRow: '1 / span 2', width: 42, height: 42, display: 'grid', placeItems: 'center', borderRadius: 11, background: '#f0fdf4', color: '#16a34a' },
  toolsContentGrid: { display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 280px', gap: 16, alignItems: 'start' },
  toolsDocumentGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 320px))', gap: 16, marginTop: 18, alignItems: 'start' },
  toolDocCard: { border: '1px solid #e5e7eb', borderRadius: 14, overflow: 'hidden', background: '#fff', maxWidth: 320, boxShadow: '0 14px 30px rgba(15, 23, 42, .05)' },
  toolDocPreview: { aspectRatio: '16 / 10', minHeight: 160, background: 'linear-gradient(135deg, #ecfdf5, #d1fae5)', color: '#16a34a', display: 'grid', placeItems: 'center', position: 'relative', textAlign: 'center', fontWeight: 500, overflow: 'hidden' },
  toolDocPreviewImage: { width: '100%', height: '100%', objectFit: 'contain', display: 'block', padding: 10 },
  toolDocInfo: { minHeight: 118, padding: 14, display: 'grid', alignContent: 'start', gap: 7, color: '#475569', fontWeight: 500 },
  toolDocActions: { padding: '0 14px 14px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 },
  largeEmptyState: { minHeight: 220, marginTop: 18, border: '1px dashed #cbd5e1', borderRadius: 14, background: '#f8fafc', color: '#64748b', display: 'grid', placeItems: 'center', alignContent: 'center', gap: 10, fontSize: 14, fontWeight: 850 },
  policyTable: { marginTop: 16, border: '1px solid #e5e7eb', borderRadius: 12, overflow: 'hidden' },
  policyTableRow: { display: 'grid', gridTemplateColumns: '1.3fr 1.1fr 1.1fr 1.4fr 28px', gap: 12, alignItems: 'center', padding: '14px 16px', borderBottom: '1px solid #eef2f7', color: '#334155', fontWeight: 500 },
  popularDoc: { display: 'grid', gridTemplateColumns: '28px 1fr 34px', alignItems: 'center', gap: 10, padding: '12px 0', borderBottom: '1px solid #eef2f7' },
  commerceHead: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'end',
    gap: 24,
    background: '#fff',
    borderRadius: 24,
    padding: 32,
    boxShadow: '0 20px 50px rgba(15, 23, 42, 0.05)',
  },
  productCatalogPanel: {
    background: '#fff',
    borderRadius: 24,
    padding: 28,
    boxShadow: '0 20px 50px rgba(15, 23, 42, 0.05)',
    display: 'grid',
    gap: 18,
  },
  productFilterTop: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 18,
  },
  productSearchBox: {
    minWidth: 320,
    height: 50,
    border: '1px solid #e5e7eb',
    borderRadius: 16,
    background: '#f8fafc',
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    padding: '0 16px',
    color: '#94a3b8',
    fontWeight: 500,
  },
  productSearchInput: {
    flex: 1,
    border: 0,
    outline: 'none',
    background: 'transparent',
    fontWeight: 500,
    color: '#111827',
  },
  productCategoryList: {
    display: 'flex',
    gap: 10,
    flexWrap: 'wrap',
  },
  productCategoryChip: {
    border: '1px solid #e5e7eb',
    borderRadius: 999,
    background: '#f8fafc',
    color: '#64748b',
    padding: '11px 16px',
    fontWeight: 500,
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
  },
  productCategoryChipActive: {
    background: '#22c55e',
    borderColor: '#22c55e',
    color: '#fff',
  },
  productGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
    gap: 18,
  },
  productCard: {
    background: '#fff',
    borderRadius: 20,
    overflow: 'hidden',
    boxShadow: '0 20px 50px rgba(15, 23, 42, 0.06)',
    border: '1px solid #eef2f0',
    cursor: 'pointer',
  },
  productVisual: {
    height: 190,
    background: 'linear-gradient(145deg, #edf8ef 0%, #f8fafc 60%, #d9f7df 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
  },
  productMark: {
    position: 'absolute',
    top: 24,
    left: 24,
    color: '#22c55e',
    fontSize: 34,
    fontWeight: 500,
    opacity: 0.9,
  },
  productImage: {
    width: '100%',
    height: '100%',
    objectFit: 'contain',
    padding: 24,
  },
  productFallback: {
    width: '100%',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
  },
  productFallbackHidden: {
    width: '100%',
    height: '100%',
    display: 'none',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
  },
  chargerShape: {
    width: 72,
    height: 132,
    borderRadius: 18,
    background: '#ffffff',
    border: '4px solid #22c55e',
    boxShadow: '0 20px 36px rgba(34, 197, 94, 0.18)',
    display: 'grid',
    alignContent: 'center',
    justifyItems: 'center',
    gap: 10,
  },
  productBody: { padding: 22 },
  productTopline: { display: 'flex', justifyContent: 'space-between', gap: 10, alignItems: 'center' },
  productCode: { color: '#a1a1aa', fontWeight: 500, letterSpacing: 2, fontSize: 12 },
  commissionBadge: {
    background: '#22c55e',
    color: '#fff',
    borderRadius: 12,
    padding: '6px 10px',
    fontWeight: 500,
    fontSize: 12,
    whiteSpace: 'nowrap',
  },
  productName: { fontSize: 20, lineHeight: 1.35, margin: '16px 0 10px', fontWeight: 500, minHeight: 54 },
  productMeta: { color: '#9ca3af', fontWeight: 500, minHeight: 22 },
  originalPrice: {
    color: '#ef4444',
    fontSize: 18,
    fontWeight: 500,
    marginTop: 16,
    textDecoration: 'line-through',
  },
  productPrice: { color: '#16a34a', fontSize: 28, fontWeight: 500, marginTop: 20 },
  productButton: {
    width: '100%',
    marginTop: 18,
    border: 0,
    borderRadius: 16,
    padding: 14,
    background: '#16a34a',
    color: '#fff',
    fontWeight: 500,
  },
  secondaryButton: {
    width: '100%',
    marginTop: 18,
    border: '1px solid #bbf7d0',
    borderRadius: 16,
    padding: 14,
    background: '#f0fdf4',
    color: '#15803d',
    fontWeight: 500,
  },
  productActions: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginTop: 18 },
  addCartButton: {
    border: '1px solid #22c55e',
    borderRadius: 14,
    minHeight: 42,
    padding: '0 14px',
    background: 'linear-gradient(180deg, #f0fdf4 0%, #ecfdf5 100%)',
    color: '#15803d',
    fontWeight: 500,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    boxShadow: '0 10px 22px rgba(34, 197, 94, .08)',
  },
  buyNowButton: {
    border: 0,
    borderRadius: 14,
    minHeight: 42,
    padding: '0 14px',
    background: 'linear-gradient(180deg, #22c55e 0%, #16a34a 100%)',
    color: '#fff',
    fontWeight: 500,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    boxShadow: '0 10px 22px rgba(34, 197, 94, .18)',
  },
  specPanel: {
    background: '#fff',
    borderRadius: 24,
    padding: 24,
    boxShadow: '0 20px 50px rgba(15, 23, 42, 0.05)',
  },
  specPanelHead: { display: 'flex', justifyContent: 'space-between', gap: 20, marginBottom: 18 },
  specSub: { color: '#6b7280', fontSize: 16, margin: '10px 0 0' },
  specTableWrap: {
    maxHeight: 620,
    overflow: 'auto',
    border: '1px solid #111827',
    borderRadius: 12,
  },
  specTable: {
    width: '100%',
    minWidth: 1320,
    borderCollapse: 'collapse',
    tableLayout: 'fixed',
    fontSize: 15,
  },
  specThFirst: {
    position: 'sticky',
    top: 0,
    left: 0,
    zIndex: 3,
    width: 230,
    background: '#2f771f',
    color: '#fff',
    border: '1px solid #111827',
    padding: '18px 12px',
    textTransform: 'uppercase',
    fontSize: 18,
  },
  specTh: {
    position: 'sticky',
    top: 0,
    zIndex: 2,
    width: 270,
    background: '#2f771f',
    color: '#fff',
    border: '1px solid #111827',
    padding: '18px 12px',
    fontSize: 18,
  },
  specLabel: {
    position: 'sticky',
    left: 0,
    zIndex: 1,
    background: '#f8fafc',
    border: '1px solid #111827',
    padding: '14px 12px',
    fontWeight: 500,
    verticalAlign: 'middle',
    width: 230,
  },
  specTd: {
    border: '1px solid #111827',
    padding: '12px 14px',
    verticalAlign: 'top',
    lineHeight: 1.55,
    background: '#fff',
    whiteSpace: 'normal',
  },
  specList: { margin: 0, paddingLeft: 18 },
  ordersPanel: {
    background: '#fff',
    borderRadius: 24,
    padding: 28,
    boxShadow: '0 20px 50px rgba(15, 23, 42, 0.05)',
  },
  ordersHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 24,
    marginBottom: 22,
  },
  orderEmpty: {
    border: '1px dashed #d1d5db',
    borderRadius: 16,
    padding: 28,
    color: '#6b7280',
    fontWeight: 500,
    textAlign: 'center',
  },
  orderTable: {
    border: '1px solid #e5e7eb',
    borderRadius: 16,
    overflow: 'hidden',
  },
  orderRow: {
    display: 'grid',
    gridTemplateColumns: '150px 1.5fr 1fr 150px 150px',
    gap: 16,
    alignItems: 'center',
    padding: '16px 18px',
    borderBottom: '1px solid #e5e7eb',
    color: '#374151',
    fontWeight: 500,
  },
  orderRowHead: {
    background: '#f8fafc',
    color: '#111827',
    fontWeight: 500,
  },
  passiveRow: {
    display: 'grid',
    gridTemplateColumns: '1.5fr 1fr 1fr 1fr',
    gap: 16,
    alignItems: 'center',
    padding: '16px 18px',
    borderBottom: '1px solid #e5e7eb',
    color: '#374151',
    fontWeight: 500,
  },
  orderCode: { color: '#16a34a', fontWeight: 500 },
  statusBadge: {
    width: 'fit-content',
    background: '#f1f5f9',
    color: '#475569',
    borderRadius: 999,
    padding: '4px 10px',
    fontWeight: 500,
    fontSize: 12,
  },
  networkGrid: { display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 22 },
  networkItem: {
    display: 'grid',
    gap: 8,
    border: '1px solid #e5e7eb',
    borderRadius: 16,
    padding: 16,
    marginTop: 12,
    color: '#6b7280',
    fontWeight: 500,
  },
  walletMiniStats: {
    display: 'grid',
    gap: 10,
    color: '#16a34a',
    fontWeight: 500,
    background: '#f0fdf4',
    borderRadius: 18,
    padding: 18,
  },
  orderFormCompact: { display: 'grid', gap: 12, marginTop: 18 },
  smallTitle: { margin: '24px 0 8px', fontSize: 18, fontWeight: 500 },
  segment: {
    display: 'flex',
    gap: 8,
    padding: 6,
    borderRadius: 18,
    background: '#f1f5f9',
  },
  segmentButton: {
    border: 0,
    borderRadius: 14,
    padding: '12px 16px',
    background: 'transparent',
    color: '#6b7280',
    fontWeight: 500,
  },
  segmentButtonActive: {
    background: '#16a34a',
    color: '#fff',
    boxShadow: '0 10px 24px rgba(22, 163, 74, 0.22)',
  },
  toolsGrid: {
    display: 'grid',
    gridTemplateColumns: '1.35fr 0.75fr',
    gap: 22,
  },
  assetGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    gap: 16,
  },
  assetCard: {
    border: '1px solid #e5e7eb',
    borderRadius: 18,
    overflow: 'hidden',
    background: '#fff',
  },
  assetImageBox: {
    height: 180,
    background: 'linear-gradient(145deg, #edf8ef 0%, #f8fafc 62%, #d9f7df 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  assetImage: { width: '100%', height: '100%', objectFit: 'contain', padding: 18 },
  assetPlaceholder: { color: '#22c55e', fontSize: 38, fontWeight: 500 },
  assetInfo: {
    display: 'grid',
    gap: 8,
    padding: 16,
    color: '#6b7280',
    fontWeight: 500,
  },
  assetActions: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 10,
    padding: '0 16px 16px',
  },
  policyList: { display: 'grid', gap: 12, marginTop: 18 },
  policyItem: {
    display: 'grid',
    gap: 8,
    border: '1px solid #e5e7eb',
    borderRadius: 16,
    padding: 16,
    color: '#6b7280',
    fontWeight: 500,
  },
  accountGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 20,
    alignItems: 'start',
  },
  accountPanel: {
    padding: 24,
    border: '1px solid #eef2f7',
    alignSelf: 'start',
  },
  accountStatusBox: {
    minWidth: 260,
    display: 'grid',
    gap: 10,
    background: '#f0fdf4',
    color: '#16a34a',
    borderRadius: 18,
    padding: 18,
    fontWeight: 500,
  },
  formGridTwo: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '16px 18px',
    marginTop: 16,
  },
  formField: {
    display: 'grid',
    gap: 8,
  },
  successBadge: {
    width: 'fit-content',
    height: 'fit-content',
    background: '#dcfce7',
    color: '#16a34a',
    borderRadius: 999,
    padding: '8px 12px',
    fontWeight: 500,
  },
  warningBadge: {
    width: 'fit-content',
    height: 'fit-content',
    background: '#fef3c7',
    color: '#b45309',
    borderRadius: 999,
    padding: '8px 12px',
    fontWeight: 500,
  },
  uploadButton: {
    border: 0,
    borderRadius: 12,
    padding: '12px 24px',
    background: '#16a34a',
    color: '#fff',
    fontWeight: 500,
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    display: 'inline-block',
    textAlign: 'center',
  },
  documentList: {
    display: 'grid',
    gap: 12,
    marginTop: 18,
  },
  documentItem: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 16,
    border: '1px solid #e5e7eb',
    borderRadius: 16,
    padding: 14,
  },
  documentLink: {
    border: 0,
    background: 'transparent',
    color: '#16a34a',
    fontWeight: 500,
    cursor: 'pointer',
  },
  dangerTextButton: {
    border: 0,
    background: 'transparent',
    color: '#ef4444',
    fontWeight: 500,
    cursor: 'pointer',
  },
  emptyModule: { background: '#fff', borderRadius: 24, padding: 42, minHeight: 420 },
  moduleTitle: { fontSize: 38, fontWeight: 700, marginTop: 18 },
  modalBackdrop: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(15, 23, 42, 0.55)',
    zIndex: 3000,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 24,
  },
  productModal: {
    width: '100%',
    maxWidth: 1120,
    maxHeight: '90vh',
    overflow: 'auto',
    background: '#fff',
    borderRadius: 24,
    boxShadow: '0 30px 80px rgba(15, 23, 42, 0.28)',
  },
  orderModal: {
    width: '100%',
    maxWidth: 680,
    maxHeight: '90vh',
    overflow: 'auto',
    background: '#fff',
    borderRadius: 24,
    boxShadow: '0 30px 80px rgba(15, 23, 42, 0.28)',
  },
  insightModal: {
    width: 'min(860px, 96vw)',
    maxHeight: '88vh',
    overflow: 'hidden',
    background: '#fff',
    borderRadius: 18,
    boxShadow: '0 30px 80px rgba(15, 23, 42, 0.28)',
    display: 'flex',
    flexDirection: 'column',
  },
  marketingDocModal: {
    width: 'min(980px, calc(100vw - 32px))',
    maxHeight: '88vh',
    overflow: 'hidden',
    background: '#fff',
    borderRadius: 24,
    boxShadow: '0 32px 90px rgba(15, 23, 42, 0.30)',
    display: 'flex',
    flexDirection: 'column',
  },
  referralQRModal: {
    width: 'min(440px, calc(100vw - 32px))',
    background: '#fff',
    borderRadius: 24,
    boxShadow: '0 32px 90px rgba(15, 23, 42, 0.30)',
    padding: 28,
    position: 'relative',
    display: 'grid',
    gap: 18,
  },
  referralQRCloseButton: {
    position: 'absolute',
    top: 16,
    right: 16,
    width: 38,
    height: 38,
    borderRadius: '50%',
    background: '#f1f5f9',
    color: '#64748b',
    fontSize: 24,
    lineHeight: 1,
    display: 'grid',
    placeItems: 'center',
  },
  referralQRHeader: {
    display: 'grid',
    gap: 8,
    paddingRight: 40,
  },
  referralQRBadge: {
    width: 'fit-content',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    padding: '7px 12px',
    borderRadius: 999,
    background: '#dcfce7',
    color: '#15803d',
    fontSize: 12,
    fontWeight: 900,
  },
  referralQRTitle: {
    margin: 0,
    color: '#0f172a',
    fontSize: 25,
    lineHeight: 1.2,
    fontWeight: 900,
  },
  referralQRDesc: {
    margin: 0,
    color: '#64748b',
    fontSize: 14,
    lineHeight: 1.55,
    fontWeight: 600,
  },
  referralQRBox: {
    justifySelf: 'center',
    background: '#fff',
    padding: 18,
    borderRadius: 20,
    border: '1px solid #d7f3df',
    boxShadow: '0 18px 50px rgba(22, 163, 74, .12)',
  },
  referralQRInfo: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
    border: '1px solid #bbf7d0',
    background: '#f0fdf4',
    borderRadius: 16,
    padding: '12px 14px',
    color: '#047857',
    fontSize: 13,
    fontWeight: 700,
  },
  referralQRActions: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    gap: 12,
  },
  marketingDocModalHeader: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 20,
    padding: '28px 30px 22px',
    background: 'linear-gradient(135deg, #ffffff 0%, #f8fffb 58%, #ecfdf5 100%)',
    borderBottom: '1px solid #e7f5ed',
  },
  marketingDocHeading: {
    minWidth: 0,
    display: 'grid',
    gap: 6,
  },
  marketingDocTypeBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    width: 'fit-content',
    padding: '6px 12px',
    borderRadius: 999,
    background: '#dcfce7',
    color: '#15803d',
    fontSize: 12,
    fontWeight: 900,
    letterSpacing: '.01em',
  },
  marketingDocModalBody: {
    padding: 28,
    overflowY: 'auto',
    display: 'grid',
    gridTemplateColumns: '260px minmax(0, 1fr)',
    gap: 24,
    background: '#fff',
  },
  marketingDocPreviewPanel: {
    display: 'grid',
    alignContent: 'start',
    gap: 14,
  },
  marketingDocPreview: {
    width: '100%',
    aspectRatio: '4 / 3',
    borderRadius: 18,
    overflow: 'hidden',
    border: '1px solid #d7f3df',
    background: 'linear-gradient(145deg, #ecfdf5 0%, #ffffff 58%, #dcfce7 100%)',
    boxShadow: '0 18px 48px rgba(22, 163, 74, .12)',
  },
  marketingDocPreviewImage: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    display: 'block',
  },
  marketingDocPreviewIcon: {
    height: '100%',
    display: 'grid',
    placeItems: 'center',
    alignContent: 'center',
    gap: 10,
    color: '#16a34a',
    fontWeight: 900,
    fontSize: 22,
  },
  marketingDocOpenButton: {
    height: 44,
    border: '1px solid #bbf7d0',
    borderRadius: 14,
    background: '#f0fdf4',
    color: '#15803d',
    fontWeight: 800,
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 9,
  },
  marketingDocDetailPanel: {
    minWidth: 0,
    display: 'grid',
    alignContent: 'start',
    gap: 14,
  },
  marketingDocMetaGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 12,
  },
  marketingDocMetaItem: {
    minHeight: 58,
    border: '1px solid #edf2f7',
    borderRadius: 14,
    background: '#fbfdff',
    padding: '10px 14px',
    display: 'grid',
    alignContent: 'center',
    gap: 4,
    color: '#64748b',
    fontSize: 12,
    fontWeight: 800,
  },
  marketingDocSection: {
    border: '1px solid #e8eef5',
    borderRadius: 16,
    padding: '16px 18px',
    display: 'grid',
    gap: 10,
    background: '#fff',
    color: '#475569',
    fontSize: 14,
    lineHeight: 1.7,
    fontWeight: 600,
  },
  marketingDocList: {
    margin: 0,
    paddingLeft: 18,
    color: '#475569',
    lineHeight: 1.7,
  },
  marketingDocActionRow: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: 12,
    padding: '18px 28px 26px',
    flexWrap: 'wrap',
    borderTop: '1px solid #edf2f7',
    background: '#fbfdff',
  },
  marketingDocPrimaryButton: {
    minWidth: 160,
    height: 48,
    border: 0,
    borderRadius: 14,
    background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
    color: '#fff',
    fontWeight: 900,
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 10,
    boxShadow: '0 14px 28px rgba(22, 163, 74, .22)',
  },
  marketingDocSecondaryButton: {
    minWidth: 170,
    height: 48,
    border: '1px solid #dbe5ef',
    borderRadius: 14,
    background: '#fff',
    color: '#475569',
    fontWeight: 800,
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 10,
  },
  insightModalHeader: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 20,
    padding: '22px 24px',
    borderBottom: '1px solid #e5e7eb',
  },
  insightModalTitle: { margin: 0, fontSize: 22, fontWeight: 900, color: '#0f172a' },
  insightModalDescription: { margin: '6px 0 0', fontSize: 14, lineHeight: 1.5, color: '#64748b', fontWeight: 600 },
  insightCloseButton: {
    width: 34,
    height: 34,
    border: 0,
    borderRadius: 17,
    background: '#f1f5f9',
    color: '#64748b',
    fontSize: 22,
    lineHeight: 1,
    cursor: 'pointer',
    flexShrink: 0,
  },
  walletNoticeModal: {
    position: 'relative',
    width: 'min(460px, calc(100vw - 32px))',
    background: '#fff',
    borderRadius: 22,
    padding: '34px 30px 28px',
    boxShadow: '0 32px 80px rgba(15, 23, 42, 0.28)',
    textAlign: 'center',
    border: '1px solid rgba(226, 232, 240, 0.9)',
  },
  walletNoticeIcon: {
    width: 58,
    height: 58,
    margin: '0 auto 16px',
    borderRadius: 18,
    display: 'grid',
    placeItems: 'center',
    background: '#fff7ed',
    color: '#f97316',
    fontSize: 24,
  },
  walletNoticeCloseButton: {
    position: 'absolute',
    top: 16,
    right: 16,
  },
  walletNoticeIconSuccess: {
    background: '#dcfce7',
    color: '#16a34a',
  },
  walletNoticeTitle: {
    margin: 0,
    color: '#0f172a',
    fontSize: 22,
    lineHeight: 1.3,
    fontWeight: 900,
  },
  walletNoticeMessage: {
    margin: '10px 0 24px',
    color: '#64748b',
    fontSize: 15,
    lineHeight: 1.6,
    fontWeight: 600,
  },
  walletNoticeButton: {
    width: '100%',
    height: 46,
    border: 0,
    borderRadius: 14,
    background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
    color: '#fff',
    fontWeight: 900,
    cursor: 'pointer',
    boxShadow: '0 14px 30px rgba(22, 163, 74, .24)',
  },
  insightSummaryBand: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 16,
    margin: '18px 24px 0',
    padding: '16px 18px',
    borderRadius: 12,
    background: '#ecfdf5',
    color: '#047857',
    fontWeight: 700,
  },
  insightTable: { marginTop: 16, padding: '0 24px 24px', overflowY: 'auto' },
  insightTableHead: {
    display: 'grid',
    gridTemplateColumns: 'minmax(220px, 1.5fr) 140px 130px 120px',
    gap: 12,
    padding: '12px 14px',
    borderRadius: 10,
    background: '#f8fafc',
    color: '#64748b',
    fontSize: 12,
    fontWeight: 900,
  },
  insightTableRow: {
    display: 'grid',
    gridTemplateColumns: 'minmax(220px, 1.5fr) 140px 130px 120px',
    gap: 12,
    alignItems: 'center',
    padding: '14px',
    borderBottom: '1px solid #eef2f7',
    color: '#334155',
    fontSize: 13,
    fontWeight: 700,
  },
  insightEmptyState: {
    minHeight: 180,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 10,
    color: '#94a3b8',
    fontWeight: 700,
  },
  cartModal: {
    width: '100%',
    maxWidth: 980,
    maxHeight: '92vh',
    overflow: 'hidden',
    background: '#fff',
    borderRadius: 24,
    boxShadow: '0 30px 80px rgba(15, 23, 42, 0.28)',
    display: 'flex',
    flexDirection: 'column',
  },
  cartContent: { padding: 24, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 14, flex: 1, minHeight: 0 },
  cartEmpty: {
    minHeight: 300,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 14,
    color: '#64748b',
  },
  cartTableHeader: {
    display: 'flex',
    alignItems: 'center',
    background: '#f8fafc',
    padding: '12px 16px',
    borderRadius: 8,
    marginBottom: 10,
    border: '1px solid #f1f5f9',
  },
  shopGroupHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '16px 16px 8px',
    borderBottom: '1px solid #f1f5f9',
    marginTop: 10,
  },
  yêuThichBadge: {
    background: '#16a34a',
    color: '#fff',
    fontSize: 11,
    fontWeight: 500,
    padding: '2px 6px',
    borderRadius: 3,
    textTransform: 'uppercase',
  },
  shopGroupName: {
    fontSize: 14,
    color: '#1e293b',
    fontWeight: 500,
  },
  cartItem: {
    display: 'flex',
    flexDirection: 'column',
    padding: '16px',
    borderBottom: '1px solid #f1f5f9',
    background: '#fff',
    transition: 'all 0.2s ease',
  },
  cartItemImageWrap: {
    width: 64,
    height: 64,
    borderRadius: 8,
    overflow: 'hidden',
    background: '#f8fafc',
    flexShrink: 0,
    border: '1px solid #f1f5f9',
  },
  cartItemImage: { width: '100%', height: '100%', objectFit: 'contain', padding: 4 },
  cartItemFallback: {
    width: '100%',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#16a34a',
    fontWeight: 500,
  },
  cartItemInfo: { display: 'flex', flexDirection: 'column', gap: 4, marginLeft: 12 },
  cartItemName: { fontSize: 14, fontWeight: 500, color: '#1e293b' },
  cartItemPrice: { fontSize: 14, color: '#475569' },
  quantityControl: {
    display: 'flex',
    alignItems: 'center',
    border: '1px solid #cbd5e1',
    borderRadius: 0,
    overflow: 'hidden',
    background: '#fff',
    height: 30,
  },
  quantityButton: { 
    border: 0, 
    background: '#f8fafc', 
    fontSize: 16, 
    color: '#475569', 
    width: 28, 
    height: '100%', 
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 0,
  },
  quantityInput: { 
    width: 50, 
    border: 0, 
    textAlign: 'center', 
    outline: 'none', 
    background: 'transparent', 
    fontSize: 14, 
    fontWeight: 500,
    color: '#1e293b',
    height: '100%',
    borderRadius: 0,
  },
  cartLineTotal: { 
    color: '#ef4444', 
    fontSize: 14, 
    fontWeight: 500, 
  },
  removeCartButton: {
    border: 0,
    background: 'transparent',
    color: '#ef4444',
    fontSize: 14,
    cursor: 'pointer',
    fontWeight: 500,
  },
  cartFooter: {
    borderTop: '1px solid #f1f5f9',
    padding: '16px 24px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    background: '#fff',
  },
  cartTotalLabel: { color: '#475569', fontSize: 14, fontWeight: 500 },
  cartTotalValue: { color: '#ef4444', fontSize: 24, fontWeight: 700 },
  checkoutButton: {
    border: 0,
    borderRadius: 8,
    background: '#16a34a',
    color: '#fff',
    height: 44,
    padding: '0 32px',
    fontWeight: 500,
    fontSize: 15,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    cursor: 'pointer',
    transition: 'all 0.15s ease',
  },
  modalHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: 20,
    padding: '28px 32px',
    borderBottom: '1px solid #e5e7eb',
  },
  modalTitle: { margin: '10px 0 0', fontSize: 32, lineHeight: 1.2, fontWeight: 500 },
  closeButton: {
    border: 0,
    background: '#f3f4f6',
    color: '#111827',
    width: 42,
    height: 42,
    borderRadius: 21,
    fontSize: 28,
    lineHeight: '40px',
    cursor: 'pointer',
  },
  modalContent: { display: 'grid', gridTemplateColumns: '380px 1fr', gap: 28, padding: 32 },
  productGallery: { minWidth: 0, display: 'grid', gap: 12, alignContent: 'start' },
  modalVisual: {
    minHeight: 300,
    borderRadius: 20,
    background: 'linear-gradient(145deg, #edf8ef 0%, #f8fafc 60%, #d9f7df 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
  },
  modalImage: { width: '100%', height: 320, objectFit: 'contain', padding: 24, animation: 'evgProductImageFade .45s ease' },
  galleryArrow: { position: 'absolute', top: '50%', transform: 'translateY(-50%)', width: 38, height: 38, display: 'grid', placeItems: 'center', borderRadius: 999, background: 'rgba(255,255,255,.92)', color: '#15803d', boxShadow: '0 8px 20px rgba(15,23,42,.14)', zIndex: 2 },
  galleryThumbnails: { display: 'grid', gridTemplateColumns: 'repeat(5, minmax(0, 1fr))', gap: 8 },
  galleryThumbnail: { height: 64, padding: 3, border: '2px solid transparent', borderRadius: 10, background: '#f8fafc', overflow: 'hidden', outline: 'none', cursor: 'pointer' },
  galleryThumbnailImage: { width: '100%', height: '100%', display: 'block', objectFit: 'contain', borderRadius: 6 },
  imageCountBadge: { position: 'absolute', left: 12, bottom: 12, zIndex: 2, display: 'inline-flex', alignItems: 'center', gap: 5, padding: '6px 9px', borderRadius: 999, background: 'rgba(15,23,42,.76)', color: '#fff', fontSize: 11, fontWeight: 500, backdropFilter: 'blur(5px)' },
  galleryThumbnailActive: { borderColor: '#22c55e', background: '#f0fdf4' },
  modalProductMark: {
    position: 'absolute',
    top: 24,
    left: 24,
    color: '#22c55e',
    fontSize: 44,
    fontWeight: 500,
  },
  modalSummary: { display: 'flex', flexDirection: 'column', justifyContent: 'center' },
  modalOriginalPrice: {
    color: '#ef4444',
    fontSize: 22,
    fontWeight: 500,
    marginTop: 14,
    textDecoration: 'line-through',
  },
  modalPrice: { color: '#16a34a', fontSize: 40, fontWeight: 500, marginTop: 16 },
  modalCommission: {
    width: 'fit-content',
    marginTop: 14,
    background: '#dcfce7',
    color: '#16a34a',
    borderRadius: 14,
    padding: '8px 12px',
    fontWeight: 500,
  },
  modalDesc: { color: '#6b7280', fontSize: 16, lineHeight: 1.7, margin: '18px 0' },
  detailTable: { padding: '0 32px 32px' },
  detailRow: {
    display: 'grid',
    gridTemplateColumns: '260px 1fr',
    border: '1px solid #e5e7eb',
    borderBottom: 0,
  },
  detailLabel: { background: '#f8fafc', padding: 14, fontWeight: 500, color: '#111827' },
  detailValue: { padding: 14, color: '#374151', lineHeight: 1.55 },
  detailList: { margin: 0, paddingLeft: 20 },
  orderForm: {
    padding: 32,
    display: 'grid',
    gap: 10,
  },
  checkoutSummary: {
    display: 'grid',
    gap: 8,
    background: '#f8fafc',
    border: '1px solid #e5e7eb',
    borderRadius: 14,
    padding: 14,
    marginBottom: 6,
  },
  checkoutSummaryTitle: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 16,
    paddingBottom: 10,
    marginBottom: 2,
    borderBottom: '1px solid #e2e8f0',
    color: '#0f172a',
    fontWeight: 500,
  },
  checkoutSummaryRow: { display: 'flex', justifyContent: 'space-between', gap: 16, color: '#475569' },
  checkoutSectionTitle: {
    marginTop: 8,
    color: '#0f172a',
    fontSize: 16,
    fontWeight: 500,
  },
  checkoutFieldGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    gap: 12,
  },
  checkoutField: {
    display: 'grid',
    gap: 7,
    marginTop: 4,
    color: '#334155',
    fontSize: 13,
    fontWeight: 500,
  },
  formLabel: {
    color: '#374151',
    fontWeight: 500,
    marginTop: 8,
  },
  requiredMark: {
    color: '#ef4444',
    marginLeft: 4,
    fontWeight: 800,
  },
  formErrorText: {
    color: '#ef4444',
    fontSize: 12,
    fontWeight: 700,
    lineHeight: 1.4,
  },
  formInput: {
    width: '100%',
    border: '1px solid #d1d5db',
    borderRadius: 14,
    padding: '14px 16px',
    fontSize: 15,
    outline: 'none',
  },
  formInputError: {
    borderColor: '#ef4444',
    boxShadow: '0 0 0 3px rgba(239, 68, 68, 0.10)',
  },
  paymentMethodBlock: {
    display: 'grid',
    gap: 12,
    marginTop: 10,
    padding: 14,
    border: '1px solid #dcfce7',
    borderRadius: 18,
    background: 'linear-gradient(180deg, #f8fff9 0%, #ffffff 100%)',
  },
  paymentMethodHeader: {
    display: 'flex',
    alignItems: 'flex-end',
    justifyContent: 'space-between',
    gap: 12,
    color: '#0f172a',
    fontWeight: 500,
  },
  paymentMethodGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
    gap: 10,
  },
  paymentMethodCard: {
    position: 'relative',
    display: 'grid',
    gridTemplateColumns: '42px 1fr',
    gap: 12,
    alignItems: 'flex-start',
    minHeight: 108,
    padding: 14,
    border: '1px solid #e5e7eb',
    borderRadius: 16,
    background: '#fff',
    textAlign: 'left',
    color: '#334155',
    cursor: 'pointer',
    boxShadow: '0 8px 20px rgba(15, 23, 42, .04)',
  },
  paymentMethodCardActive: {
    border: '1px solid #22c55e',
    background: '#f0fdf4',
    boxShadow: '0 12px 28px rgba(34, 197, 94, .14)',
  },
  paymentMethodCardDisabled: {
    opacity: .58,
    cursor: 'not-allowed',
    background: '#f8fafc',
  },
  paymentMethodIcon: {
    width: 42,
    height: 42,
    borderRadius: 14,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: '#dcfce7',
    color: '#16a34a',
    fontSize: 16,
  },
  paymentMethodLogo: {
    maxWidth: 30,
    maxHeight: 30,
    objectFit: 'contain',
    display: 'block',
  },
  paymentMethodContent: {
    display: 'grid',
    gap: 4,
    lineHeight: 1.35,
  },
  paymentMethodCheck: {
    position: 'absolute',
    top: 12,
    right: 12,
    color: '#16a34a',
  },
  paymentQRModal: {
    width: 'min(680px, calc(100vw - 36px))',
    background: '#fff',
    borderRadius: 28,
    overflow: 'hidden',
    boxShadow: '0 30px 90px rgba(15, 23, 42, .24)',
  },
  paymentQRContent: {
    display: 'grid',
    gridTemplateColumns: '260px 1fr',
    gap: 22,
    padding: '8px 28px 0',
    alignItems: 'center',
  },
  paymentQRBox: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 18,
    borderRadius: 24,
    background: '#f8fafc',
    border: '1px solid #e2e8f0',
  },
  paymentInfoList: {
    display: 'grid',
    gap: 10,
  },
  paymentInfoRow: {
    display: 'grid',
    gridTemplateColumns: '110px 1fr',
    gap: 12,
    padding: '12px 0',
    borderBottom: '1px solid #eef2f7',
    color: '#64748b',
  },
  paymentQRNote: {
    marginTop: 8,
    padding: 14,
    borderRadius: 16,
    background: '#ecfdf5',
    color: '#15803d',
    fontWeight: 500,
    lineHeight: 1.5,
  },
  paymentQRActions: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 12,
    padding: '0 28px 28px',
  },
  orderTotal: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    background: '#f0fdf4',
    color: '#14532d',
    borderRadius: 16,
    padding: 18,
    marginTop: 10,
    fontSize: 18,
  },
  shopeeStats: { display: 'flex', alignItems: 'center', gap: 12, margin: '8px 0 16px', fontSize: 14 },
  shopeeRating: { color: '#f59e0b', fontWeight: 500 },
  shopeeDivider: { color: '#e2e8f0' },
  shopeeStatItem: { color: '#64748b' },
  shopeePriceBlock: {
    background: '#f8fafc',
    borderRadius: 12,
    padding: '16px 20px',
    display: 'flex',
    alignItems: 'baseline',
    gap: 12,
    margin: '16px 0',
  },
  shopeeOriginalPrice: { color: '#94a3b8', textDecoration: 'line-through', fontSize: 16 },
  shopeeCurrentPrice: { color: '#ef4444', fontSize: 32, fontWeight: 500 },
  shopeeDiscountBadge: { background: '#fef2f2', color: '#ef4444', fontSize: 11, fontWeight: 500, padding: '2px 6px', borderRadius: 4, textTransform: 'uppercase' },
  shopeeCommissionLine: { display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16, fontSize: 14 },
  shopeeLabel: { color: '#64748b', flexShrink: 0, fontWeight: 500 },
  shopeeCommissionValue: { background: '#f0fdf4', color: '#16a34a', padding: '4px 10px', borderRadius: 8, fontWeight: 500 },
  shopeeSpecs: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    columnGap: 24,
    rowGap: 12,
    borderTop: '1px solid #f1f5f9',
    borderBottom: '1px solid #f1f5f9',
    padding: '16px 0',
    margin: '16px 0'
  },
  shopeeSpecItem: { display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'flex-start', fontSize: 14 },
  shopeeValue: { color: '#1e293b', fontWeight: 500, whiteSpace: 'pre-line', wordBreak: 'break-word' },
  shopeeDesc: { color: '#475569', fontSize: 14, lineHeight: 1.6, marginTop: 6, wordBreak: 'break-word' },
  shopeeQtyLine: { display: 'flex', alignItems: 'center', gap: 12, margin: '20px 0' },
  shopeeQtyControl: { display: 'flex', alignItems: 'center', border: '1px solid #cbd5e1', borderRadius: 8, overflow: 'hidden', height: 34 },
  shopeeQtyButton: { border: 0, background: '#f1f5f9', width: 32, height: '100%', fontSize: 18, color: '#475569', cursor: 'pointer' },
  shopeeQtyVal: { width: 44, textAlign: 'center', fontWeight: 500, fontSize: 14, color: '#1e293b' },
  shopeeStockText: { color: '#64748b', fontSize: 14 },
  shopeeActions: { display: 'flex', gap: 16, marginTop: 24 },
  shopeeAddCartBtn: {
    border: '1px solid #16a34a',
    borderRadius: 12,
    background: '#f0fdf4',
    color: '#16a34a',
    padding: '0 24px',
    height: 48,
    fontWeight: 500,
    fontSize: 15,
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    cursor: 'pointer',
    transition: 'all 0.15s ease',
  },
  shopeeBuyNowBtn: {
    border: 0,
    borderRadius: 12,
    background: '#16a34a',
    color: '#fff',
    padding: '0 32px',
    height: 48,
    fontWeight: 500,
    fontSize: 15,
    cursor: 'pointer',
    transition: 'all 0.15s ease',
  },
  cartHoverPopup: {},
  cartHoverTitle: {
    color: '#94a3b8',
    fontSize: 13,
    fontWeight: 500,
    marginBottom: 12,
    textAlign: 'left',
  },
  cartHoverList: {
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
    maxHeight: 280,
    overflowY: 'auto',
    paddingRight: 10,
    paddingLeft: 2,
  },
  cartHoverItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: '4px 0',
  },
  cartHoverImage: {
    width: 40,
    height: 40,
    borderRadius: 6,
    objectFit: 'contain',
    background: '#f8fafc',
    border: '1px solid #f1f5f9',
    flexShrink: 0,
  },
  cartHoverItemInfo: {
    flex: 1,
    textAlign: 'left',
  },
  cartHoverItemName: {
    fontSize: 13,
    fontWeight: 500,
    color: '#334155',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    maxWidth: 180,
  },
  cartHoverItemPrice: {
    fontSize: 13,
    fontWeight: 500,
    color: '#ef4444',
  },
  cartHoverFooter: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: 14,
    paddingTop: 10,
    borderTop: '1px solid #f1f5f9',
  },
  cartHoverViewBtn: {
    border: 0,
    borderRadius: 8,
    background: '#16a34a',
    color: '#fff',
    padding: '8px 16px',
    fontSize: 13,
    fontWeight: 500,
    cursor: 'pointer',
    transition: 'all 0.15s ease',
  },
};

export default CustomerHomeScreen;
